import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
// Geolocation will use native browser API instead of Capacitor plugin
import CookieConsent from "./components/CookieConsent";

// OneSignal initialization
const initOneSignal = () => {
  if (Capacitor.isNativePlatform() && window.plugins && window.plugins.OneSignal) {
    window.plugins.OneSignal.initialize("8492eadc-8af6-4903-bec7-3b0da2162f43");
    window.plugins.OneSignal.Notifications.requestPermission(true);
    console.log("OneSignal initialized successfully");
  }
};

// Initialize OneSignal when app loads
if (typeof window !== 'undefined') {
  document.addEventListener('deviceready', initOneSignal, false);
  // Also try after a delay for Capacitor
  setTimeout(() => {
    initOneSignal();
  }, 2000);
}
import { 
  Search, 
  MapPin, 
  Clock, 
  Phone, 
  Star, 
  Filter,
  User,
  LogIn,
  LogOut,
  UserPlus,
  Heart,
  Navigation,
  Utensils,
  Coffee,
  Plus,
  Share2,
  Facebook,
  Instagram,
  MessageCircle,
  Globe,
  Mail,
  Copy,
  Map,
  Upload,
  X,
  MessageSquare,
  Music2,
  Camera,
  BarChart3,
  Users,
  CheckCircle,
  AlertCircle,
  Eye,
  Ticket,
  Tag,
  ChevronLeft,
  ChevronRight,
  Newspaper,
  Trash2,
  Edit,
  RefreshCw,
  Trophy,
  Award,
  TrendingUp,
  Lock,
  Car,
  ShoppingBag,
  UtensilsCrossed,
  CalendarCheck,
  WifiOff,
  Link,
  Bell,
  Send,
  Download,
  ThumbsUp,
  List,
  Bookmark,
  BookmarkCheck,
  Check
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ===================================================================
// 🔄 Auto Cache Clear - حل مشكلة الشاشة البيضاء بعد التحديث
// ===================================================================
const APP_VERSION = '2.0.1'; // قم بتحديث هذا الرقم مع كل تحديث جديد

(function autoCacheClear() {
  try {
    const savedVersion = localStorage.getItem('app_version');
    
    // إذا كان الإصدار مختلف أو غير موجود
    if (savedVersion !== APP_VERSION) {
      console.log('🔄 تم اكتشاف تحديث جديد - جاري مسح الكاش...');
      
      // مسح الكاش القديم
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            caches.delete(name);
            console.log('✅ تم مسح الكاش:', name);
          });
        });
      }
      
      // حفظ الإصدار الجديد
      localStorage.setItem('app_version', APP_VERSION);
      
      // إذا كان هناك إصدار قديم محفوظ (ليس أول مرة)
      if (savedVersion) {
        console.log('🔄 جاري إعادة تحميل الصفحة...');
        // إعادة تحميل الصفحة لتطبيق التحديثات
        setTimeout(() => {
          window.location.reload(true);
        }, 500);
      }
    }
  } catch (e) {
    console.log('⚠️ خطأ في مسح الكاش:', e);
  }
})();

// Safe localStorage wrapper that checks for consent
const safeLocalStorage = {
  getItem: (key) => {
    try {
      const consent = localStorage.getItem('cookieConsent');
      // Allow reading consent-related keys always
      if (key === 'cookieConsent' || key === 'cookieConsentDate' || consent === 'accepted') {
        return localStorage.getItem(key);
      }
      return null;
    } catch (e) {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      const consent = localStorage.getItem('cookieConsent');
      if (key === 'cookieConsent' || key === 'cookieConsentDate' || consent === 'accepted') {
        localStorage.setItem(key, value);
      }
    } catch (e) { /* Storage access denied */ }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) { /* Storage access denied */ }
  },
  clear: () => {
    try {
      localStorage.clear();
    } catch (e) { /* Storage access denied */ }
  }
};

// Configure axios defaults for better error handling
axios.defaults.timeout = 15000; // 15 seconds default timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add axios interceptor for better error handling
axios.interceptors.response.use(
  response => response,
  error => {
    // Handle network errors silently for non-critical requests
    if (error.config && error.config.silent) {
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

// Offline Alert Component - شريط إشعار بعدم الاتصال بالإنترنت
const OfflineAlert = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBanner, setShowBanner] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setShowBanner(false);
      // إظهار رسالة استعادة الاتصال
      toast.success('تم استعادة الاتصال بالإنترنت ✓', {
        duration: 3000,
      });
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOffline || !showBanner) return null;

  // شريط إشعار صغير في الأعلى بدلاً من حجب الشاشة كاملة
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 shadow-lg"
      dir="rtl"
    >
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        <div className="flex items-center gap-2">
          <WifiOff className="h-4 w-4" />
          <span className="text-sm font-medium">
            أنت غير متصل بالإنترنت - بعض الخدمات قد لا تعمل
          </span>
        </div>
        <button
          onClick={() => setShowBanner(false)}
          className="p-1 hover:bg-white/20 rounded-full transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

// Auth Context with Persistent Authentication
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    const checkAuthState = async () => {
      const savedToken = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      const hasSeenApp = localStorage.getItem('hasSeenApp');
      
      if (savedToken && savedUser) {
        try {
          // تعيين الـ token والمستخدم فوراً بدون انتظار التحقق
          const parsedUser = JSON.parse(savedUser);
          setToken(savedToken);
          setUser(parsedUser);
          setIsFirstTime(false);
          
          // التحقق من الـ token في الخلفية (بدون حذفه إذا فشل)
          try {
            const response = await axios.get(`${API}/profile`, {
              headers: { Authorization: `Bearer ${savedToken}` },
              timeout: 10000 // 10 ثواني
            });
            
            // تحديث بيانات المستخدم إذا نجح التحقق
            if (response.data) {
              const updatedUser = { ...parsedUser, ...response.data };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
            fetchFavorites(savedToken);
          } catch (verifyError) {
            // إذا كان الخطأ 401 (token منتهي) فقط نحذف الـ token
            if (verifyError.response && verifyError.response.status === 401) {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setToken(null);
              setUser(null);
              setIsFirstTime(!hasSeenApp);
            } else {
              // خطأ شبكة - نبقي المستخدم مسجل الدخول
              fetchFavorites(savedToken);
            }
          }
        } catch (parseError) {
          // خطأ في قراءة البيانات المحفوظة
          console.error('Error parsing saved user:', parseError);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsFirstTime(!hasSeenApp);
        }
      } else {
        setIsFirstTime(!hasSeenApp);
      }
      
      setLoading(false);
    };

    checkAuthState();
  }, []);

  const fetchFavorites = async (authToken = token) => {
    if (!authToken) return;
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setFavorites(response.data.map(r => r.id));
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  const login = (token, userData) => {
    setToken(token);
    setUser(userData);
    setIsFirstTime(false);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('hasSeenApp', 'true');
    fetchFavorites(token);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setFavorites([]);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Keep hasSeenApp to avoid showing splash again
  };

  const markAsSeenApp = () => {
    setIsFirstTime(false);
    localStorage.setItem('hasSeenApp', 'true');
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      logout, 
      loading, 
      isFirstTime, 
      markAsSeenApp,
      updateUser,
      isAuthenticated: !!user && !!token,
      favorites, 
      setFavorites, 
      fetchFavorites 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Google Maps Location Picker Component
const LocationPicker = ({ onLocationSelect, initialLocation }) => {
  const [showMap, setShowMap] = useState(false);
  const [location, setLocation] = useState(initialLocation || { lat: 52.5200, lng: 13.4050 }); // Berlin default

  useEffect(() => {
    if (showMap && window.google) {
      initMap();
    }
  }, [showMap]);

  const initMap = () => {
    try {
      const map = new window.google.maps.Map(document.getElementById('map'), {
        center: location,
        zoom: 13
      });

      const marker = new window.google.maps.Marker({
        position: location,
        map: map,
        draggable: true
      });

    marker.addListener('dragend', function() {
      const newLocation = {
        lat: marker.getPosition().lat(),
        lng: marker.getPosition().lng()
      };
      setLocation(newLocation);
      
      // Get address from coordinates
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: newLocation }, (results, status) => {
        if (status === 'OK' && results[0]) {
          onLocationSelect(newLocation, results[0].formatted_address);
        }
      });
    });

    // Add search box
    const searchBox = new window.google.maps.places.SearchBox(
      document.getElementById('location-search')
    );

    searchBox.addListener('places_changed', () => {
      const places = searchBox.getPlaces();
      if (places.length === 0) return;

      const place = places[0];
      const newLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };

      map.setCenter(newLocation);
      marker.setPosition(newLocation);
      setLocation(newLocation);
      onLocationSelect(newLocation, place.formatted_address);
    });
    } catch (error) {
      console.error('Google Maps initialization error:', error);
      toast.error('خطأ في تهيئة الخريطة - يرجى المحاولة لاحقاً');
    }
  };

  const loadGoogleMaps = () => {
    if (!window.google) {
      const script = document.createElement('script');
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'demo-key';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=ar`;
      script.async = true;
      script.defer = true;
      script.onload = () => setShowMap(true);
      script.onerror = () => {
        console.error('Google Maps API failed to load');
        toast.error('خطأ في تحميل خرائط جوجل - يرجى المحاولة لاحقاً');
      };
      document.head.appendChild(script);
    } else {
      setShowMap(true);
    }
  };

  return (
    <div className="space-y-4">
      <Button 
        type="button" 
        onClick={loadGoogleMaps}
        variant="outline"
        className="w-full"
      >
        <MapPin className="h-4 w-4 mr-2" />
        حدد الموقع على الخريطة
      </Button>

      {showMap && (
        <div className="border rounded-lg p-4">
          <Input
            id="location-search"
            placeholder="ابحث عن موقع المطعم..."
            className="mb-4"
          />
          <div id="map" className="h-64 rounded-lg bg-gray-100 flex items-center justify-center">
            <p className="text-gray-500 text-center">
              جاري تحميل الخريطة...
              <br />
              <span className="text-xs">في حالة عدم ظهور الخريطة، يرجى إدخال الإحداثيات يدوياً</span>
            </p>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            اسحب المؤشر لتحديد الموقع الدقيق للمطعم
          </p>
        </div>
      )}
      
      <div className="text-xs text-gray-500 p-2 bg-gray-50 rounded">
        <strong>ملاحظة:</strong> يمكنك إضافة المطعم حتى بدون تحديد الموقع على الخريطة. 
        سيتم استخدام العنوان المكتوب للعثور على المطعم.
      </div>
    </div>
  );
};

// New Splash Screen Component
const NewSplashScreen = ({ onFinish, hasCache = false }) => {
  useEffect(() => {
    // مدة عرض السبلاش ثابتة 2.5 ثانية
    const duration = 2500;
    const timer = setTimeout(() => {
      onFinish();
    }, duration);
    
    return () => clearTimeout(timer);
  }, [onFinish]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex items-center justify-center z-50 overflow-hidden">
      {/* خلفية متحركة */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-20 h-20 bg-amber-400 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-32 h-32 bg-orange-400 rounded-full animate-pulse delay-300"></div>
        <div className="absolute top-1/3 right-10 w-16 h-16 bg-amber-300 rounded-full animate-pulse delay-500"></div>
      </div>
      
      <div className="text-center relative z-10" style={{ direction: 'rtl' }}>
        {/* أيقونة الطعام المتحركة */}
        <div className="mb-8 relative">
          <div className="relative inline-block">
            <Utensils className="h-28 w-28 text-amber-600 mx-auto animate-bounce" 
              style={{ animationDuration: '1.5s' }} />
            {/* تأثير توهج */}
            <div className="absolute inset-0 h-28 w-28 bg-amber-400 rounded-full opacity-20 animate-ping mx-auto"></div>
          </div>
        </div>
        
        {/* العنوان الرئيسي */}
        <h1 className="text-5xl font-bold text-amber-900 mb-3 animate-fade-in"
          style={{ 
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            animation: 'fadeInUp 0.8s ease-out'
          }}>
          دليل المطاعم
        </h1>
        
        {/* الشعار */}
        <p className="text-2xl text-amber-700 mb-8 font-medium"
          style={{ animation: 'fadeInUp 0.8s ease-out 0.2s both' }}>
          طعم الوطن بعيداً عن الوطن
        </p>
        
        {/* فنجان القهوة المتحرك - بدون الخط */}
        <div className="flex items-center justify-center space-x-4 space-x-reverse text-amber-600">
          <Coffee className="h-8 w-8 animate-spin" style={{ animationDuration: '3s' }} />
          <span className="text-xl font-medium animate-pulse">جاري التحميل...</span>
        </div>
        
        {/* نقاط التحميل */}
        <div className="flex justify-center mt-6 space-x-2 space-x-reverse">
          <div className="w-3 h-3 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
          <div className="w-3 h-3 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-3 h-3 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
        </div>
      </div>
      
      {/* CSS للأنيميشن */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// Phone Authentication Component
const PhoneAuth = ({ onAuthenticated }) => {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState(1); // 1: phone input, 2: code verification, 3: user details (if new)
  const [loading, setLoading] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    city: '',
    country: 'ألمانيا'
  });
  const { login } = useAuth();

  const sendVerificationCode = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error('يرجى إدخال رقم الهاتف');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/phone-login`, { phone: phone.trim() });
      setVerificationData(response.data);
      setUserExists(response.data.user_exists);
      setStep(2);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إرسال رمز التحقق');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error('يرجى إدخال رمز التحقق');
      return;
    }

    setLoading(true);
    try {
      let payload = {
        phone: phone.trim(),
        verification_code: code.trim()
      };

      // If new user, add required details
      if (!userExists) {
        if (!newUserForm.name.trim() || !newUserForm.city.trim()) {
          toast.error('يرجى ملء جميع الحقول المطلوبة');
          setLoading(false);
          return;
        }
        payload = { ...payload, ...newUserForm };
      }

      const response = await axios.post(`${API}/phone-verify`, payload);
      
      if (response.data.token && response.data.user) {
        login(response.data.token, response.data.user);
        onAuthenticated();
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في التحقق من الرمز');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md" style={{ direction: 'rtl' }}>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-gray-900">مرحباً بك</CardTitle>
          <CardDescription>
            {step === 1 && 'سجل دخولك برقم الهاتف'}
            {step === 2 && 'أدخل رمز التحقق المرسل إلى هاتفك'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 && (
            <form onSubmit={sendVerificationCode} className="space-y-4">
              <div>
                <Label htmlFor="phone">رقم الهاتف</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+49xxxxxxxxx"
                  className="text-center"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  أدخل رقم الهاتف مع رمز الدولة (مثال: +49)
                </p>
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyCode} className="space-y-4">
              <div>
                <Label htmlFor="code">رمز التحقق</Label>
                <Input
                  id="code"
                  type="text"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="text-center text-2xl tracking-widest"
                  required
                />
              </div>

              {verificationData?.verification_code && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 text-center">
                    رمز التحقق (للتجربة): <strong className="text-lg">{verificationData.verification_code}</strong>
                  </p>
                </div>
              )}

              {!userExists && (
                <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900">إكمال إنشاء الحساب</h4>
                  <div>
                    <Label htmlFor="name">الاسم *</Label>
                    <Input
                      id="name"
                      value={newUserForm.name}
                      onChange={(e) => setNewUserForm({...newUserForm, name: e.target.value})}
                      placeholder="أدخل اسمك الكامل"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">المدينة *</Label>
                    <Input
                      id="city"
                      value={newUserForm.city}
                      onChange={(e) => setNewUserForm({...newUserForm, city: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">البلد</Label>
                    <Select value={newUserForm.country} onValueChange={(value) => setNewUserForm({...newUserForm, country: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ألمانيا">ألمانيا</SelectItem>
                        <SelectItem value="النمسا">النمسا</SelectItem>
                        <SelectItem value="سويسرا">سويسرا</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'جاري التحقق...' : 'تأكيد'}
              </Button>
              
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full" 
                onClick={() => setStep(1)}
              >
                العودة لتغيير رقم الهاتف
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Email Authentication Component
const EmailAuth = ({ onAuthenticated }) => {
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [demoCode, setDemoCode] = useState('');
  const [registrationForm, setRegistrationForm] = useState({
    name: '',
    city: '',
    country: 'ألمانيا',
    email: '',
    password: ''
  });
  const [customCity, setCustomCity] = useState('');
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [customCountry, setCustomCountry] = useState('');
  const [showCustomCountry, setShowCustomCountry] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();

  const handleCountryChange = (value) => {
    setRegistrationForm({ ...registrationForm, country: value, city: '' });
    setShowCustomCountry(value === 'أخرى');
    setShowCustomCity(false);
    setCustomCity('');
  };

  const handleCityChange = (value) => {
    if (value === 'أخرى') {
      setShowCustomCity(true);
      setRegistrationForm({ ...registrationForm, city: '' });
    } else {
      setShowCustomCity(false);
      setRegistrationForm({ ...registrationForm, city: value });
      setCustomCity('');
    }
  };

  const availableCities = CITIES_BY_COUNTRY[registrationForm.country] || [];

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      toast.error('يرجى ملء جميع الحقول');
      return;
    }

    setLoading(true);
    
    // محاولة تسجيل الدخول مع إعادة المحاولة
    const attemptLogin = async (retries = 3) => {
      try {
        const response = await axios.post(`${API}/login`, { 
          email: email.trim(), 
          password 
        }, {
          timeout: 15000 // 15 ثانية
        });
        
        if (response.data.token && response.data.user) {
          login(response.data.token, response.data.user);
          onAuthenticated();
          toast.success('تم تسجيل الدخول بنجاح');
          return true;
        } else {
          throw new Error('Invalid response');
        }
      } catch (error) {
        // إذا كان خطأ شبكة وليس خطأ من السيرفر
        const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.message.includes('Network');
        
        if (isNetworkError && retries > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // انتظر ثانية
          return attemptLogin(retries - 1);
        }
        
        // خطأ من السيرفر (401, 400, etc)
        if (error.response?.status === 401 || error.response?.status === 400) {
          toast.error('البريد الإلكتروني أو كلمة المرور غير صحيحة');
        } else if (isNetworkError) {
          toast.error('خطأ في الاتصال. يرجى التحقق من الإنترنت والمحاولة مرة أخرى');
        } else {
          toast.error(error.response?.data?.detail || 'خطأ في تسجيل الدخول');
        }
        return false;
      }
    };
    
    await attemptLogin();
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const { name, city, country, email, password } = registrationForm;
    
    if (!name.trim() || !city.trim() || !email.trim() || !password.trim()) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/register`, registrationForm);
      // Check if demo code was returned (fallback mode)
      if (response.data.verification_code) {
        setDemoCode(response.data.verification_code);
      }
      setShowVerification(true);
      toast.success('تم إرسال رمز التأكيد إلى بريدك الإلكتروني');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في التسجيل');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      toast.error('يرجى إدخال رمز التحقق');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/verify`, { 
        email: registrationForm.email, 
        verification_code: verificationCode 
      });
      login(response.data.token, response.data.user);
      onAuthenticated();
      toast.success('تم تأكيد الحساب بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في التأكيد');
    } finally {
      setLoading(false);
    }
  };

  if (showVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4">
        <Card className="w-full max-w-md" style={{ direction: 'rtl' }}>
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
            <CardTitle>تأكيد الحساب</CardTitle>
            <CardDescription>
              تم إرسال رمز التأكيد المكون من 5 خانات إلى: {registrationForm.email}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerification} className="space-y-4">
              <div>
                <Label htmlFor="verification_code">رمز التأكيد</Label>
                <Input
                  id="verification_code"
                  type="text"
                  maxLength={5}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="12345"
                  className="text-center text-2xl tracking-widest"
                  required
                />
              </div>

              {demoCode && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 text-center">
                    رمز التأكيد (للتجربة): <strong className="text-lg">{demoCode}</strong>
                  </p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'جاري التأكيد...' : 'تأكيد الحساب'}
              </Button>
              
              <Button 
                type="button" 
                variant="ghost" 
                className="w-full" 
                onClick={() => setShowVerification(false)}
              >
                العودة للتسجيل
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md" style={{ direction: 'rtl' }}>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-gray-900">
            {mode === 'login' ? 'تسجيل الدخول' : 'إنشاء حساب جديد'}
          </CardTitle>
          <CardDescription>
            {mode === 'login' ? 'ادخل بريدك الإلكتروني وكلمة المرور' : 'املأ البيانات لإنشاء حساب جديد'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="email">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              <div>
                <Label htmlFor="password">كلمة المرور</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
              </Button>
              
              {/* Forgot Password Link */}
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-red-600 hover:text-red-700 text-sm hover:underline"
                >
                  نسيت كلمة المرور؟
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="name">الاسم *</Label>
                <Input
                  id="name"
                  value={registrationForm.name}
                  onChange={(e) => setRegistrationForm({...registrationForm, name: e.target.value})}
                  placeholder="أدخل اسمك الكامل"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="country">البلد</Label>
                  {!showCustomCountry ? (
                    <Select value={registrationForm.country} onValueChange={handleCountryChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="custom_country"
                      value={customCountry}
                      onChange={(e) => {
                        setCustomCountry(e.target.value);
                        setRegistrationForm({...registrationForm, country: e.target.value});
                      }}
                      placeholder="اكتب اسم البلد"
                      required
                    />
                  )}
                </div>
                <div>
                  <Label htmlFor="city">المدينة</Label>
                  {availableCities.length > 0 && !showCustomCity ? (
                    <>
                      <Select value={registrationForm.city} onValueChange={handleCityChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر المدينة" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCities.map((city) => (
                            <SelectItem key={city} value={city}>
                              {city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 باللغة الإنجليزية
                      </p>
                    </>
                  ) : (
                    <>
                      <Input
                        id="city"
                        value={showCustomCity ? customCity : registrationForm.city}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (showCustomCity) {
                            setCustomCity(value);
                          }
                          setRegistrationForm({...registrationForm, city: value});
                        }}
                        placeholder="Berlin, Munich..."
                        dir="ltr"
                        required
                      />
                      <p className="text-xs text-orange-600 mt-1 font-medium">
                        ⚠️ اكتب باللغة الإنجليزية
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="register_email">البريد الإلكتروني</Label>
                <Input
                  id="register_email"
                  type="email"
                  value={registrationForm.email}
                  onChange={(e) => setRegistrationForm({...registrationForm, email: e.target.value})}
                  required
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              <div>
                <Label htmlFor="register_password">كلمة المرور</Label>
                <Input
                  id="register_password"
                  type="password"
                  value={registrationForm.password}
                  onChange={(e) => setRegistrationForm({...registrationForm, password: e.target.value})}
                  required
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب'}
              </Button>
            </form>
          )}

          <div className="text-center space-y-2">
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            >
              {mode === 'login' ? 'ليس لديك حساب؟ أنشئ حساباً جديداً' : 'لديك حساب؟ سجل الدخول'}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordFlow 
          onClose={() => setShowForgotPassword(false)}
          onBackToLogin={() => setShowForgotPassword(false)}
        />
      )}
    </div>
  );
};

// Authentication Gate Component - Mandatory Authentication
const AuthenticationGate = ({ onAuthenticated }) => {
  return (
    <div className="min-h-screen">
      <EmailAuth 
        onAuthenticated={onAuthenticated}
      />
    </div>
  );
};

// Profile Edit Component
const ProfileEdit = () => {
  const { user, updateUser, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState({
    name: user?.name || '',
    city: user?.city || '',
    country: user?.country || 'ألمانيا',
    phone: user?.phone || '',
    email: user?.email || ''
  });
  const [customCity, setCustomCity] = useState('');
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [customCountry, setCustomCountry] = useState('');
  const [showCustomCountry, setShowCustomCountry] = useState(false);
  
  // My ratings state
  const [myRatings, setMyRatings] = useState([]);
  const [loadingRatings, setLoadingRatings] = useState(false);

  const handleCountryChange = (value) => {
    setProfile({ ...profile, country: value, city: '' });
    setShowCustomCountry(value === 'أخرى');
    setShowCustomCity(false);
    setCustomCity('');
  };

  const handleCityChange = (value) => {
    if (value === 'أخرى') {
      setShowCustomCity(true);
      setProfile({ ...profile, city: '' });
    } else {
      setShowCustomCity(false);
      setProfile({ ...profile, city: value });
      setCustomCity('');
    }
  };

  const availableCities = CITIES_BY_COUNTRY[profile.country] || [];

  // Fetch user's ratings when tab changes
  useEffect(() => {
    if (activeTab === 'ratings') {
      fetchMyRatings();
    }
  }, [activeTab]);

  const fetchMyRatings = async () => {
    setLoadingRatings(true);
    try {
      const response = await axios.get(`${API}/my-ratings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRatings(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل التقييمات');
    } finally {
      setLoadingRatings(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.put(`${API}/profile`, profile, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      updateUser(response.data.user);
      toast.success('تم تحديث البيانات بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تحديث البيانات');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" style={{ direction: 'rtl' }}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            حسابي
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Tabs */}
          <div className="mb-6 border-b overflow-x-auto">
            <div className="flex gap-2 sm:gap-4 min-w-max">
              <button
                className={`pb-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('profile')}
              >
                <User className="h-3 w-3 sm:h-4 sm:w-4 inline-block ml-1 sm:ml-2" />
                <span className="hidden sm:inline">البيانات الشخصية</span>
                <span className="inline sm:hidden">البيانات</span>
              </button>
              <button
                className={`pb-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'ratings'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('ratings')}
              >
                <Star className="h-3 w-3 sm:h-4 sm:w-4 inline-block ml-1 sm:ml-2" />
                تقييماتي
              </button>
              <button
                className={`pb-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'achievements'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('achievements')}
              >
                <Trophy className="h-3 w-3 sm:h-4 sm:w-4 inline-block ml-1 sm:ml-2" />
                الإنجازات
              </button>
              <button
                className={`pb-2 px-1.5 sm:px-2 text-xs sm:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                  activeTab === 'password'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('password')}
              >
                <Lock className="h-3 w-3 sm:h-4 sm:w-4 inline-block ml-1 sm:ml-2" />
                <span className="hidden sm:inline">تغيير كلمة المرور</span>
                <span className="inline sm:hidden">كلمة المرور</span>
              </button>
            </div>
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">الاسم</Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile({...profile, name: e.target.value})}
                required
                placeholder="أدخل اسمك الكامل"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="country">البلد</Label>
                {!showCustomCountry ? (
                  <Select value={profile.country} onValueChange={handleCountryChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="custom_country"
                    value={customCountry}
                    onChange={(e) => {
                      setCustomCountry(e.target.value);
                      setProfile({...profile, country: e.target.value});
                    }}
                    placeholder="اكتب اسم البلد"
                    required
                  />
                )}
              </div>

              <div>
                <Label htmlFor="city">المدينة</Label>
                {availableCities.length > 0 && !showCustomCity ? (
                  <>
                    <Select value={profile.city} onValueChange={handleCityChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="اختر المدينة" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCities.map((city) => (
                          <SelectItem key={city} value={city}>
                            {city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      💡 باللغة الإنجليزية
                    </p>
                  </>
                ) : (
                  <>
                    <Input
                      id="city"
                      value={showCustomCity ? customCity : profile.city}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (showCustomCity) {
                          setCustomCity(value);
                        }
                        setProfile({...profile, city: value});
                      }}
                      placeholder="Berlin, Munich..."
                      dir="ltr"
                      required
                    />
                    <p className="text-xs text-orange-600 mt-1 font-medium">
                      ⚠️ اكتب باللغة الإنجليزية
                    </p>
                  </>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="phone">رقم الهاتف</Label>
              <Input
                id="phone"
                value={profile.phone}
                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">لا يمكن تغيير رقم الهاتف</p>
            </div>

            {user?.email && (
              <div>
                <Label htmlFor="email">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({...profile, email: e.target.value})}
                  disabled
                  className="bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">لا يمكن تغيير البريد الإلكتروني</p>
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
            </Button>
          </form>
          )}

          {/* Ratings Tab */}
          {activeTab === 'ratings' && (
            <div>
              {loadingRatings ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">جاري التحميل...</p>
                </div>
              ) : myRatings.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">لم تقم بتقييم أي مطعم بعد</p>
                  <p className="text-sm text-gray-500">قم بزيارة المطاعم وإضافة تقييماتك</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 mb-4">
                    عدد التقييمات: <span className="font-bold">{myRatings.length}</span>
                  </p>

                  {myRatings.map((rating) => (
                    <div key={rating.id} className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-bold text-lg text-orange-700 mb-1">
                            {rating.restaurant_name || 'مطعم'}
                          </h3>
                          <p className="text-xs text-gray-600">
                            {new Date(rating.created_at).toLocaleDateString('ar-SA', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 bg-white px-3 py-1 rounded-full">
                          <Star className="h-4 w-4 text-amber-500 fill-current" />
                          <span className="font-bold">
                            {((rating.service_rating + rating.price_rating + rating.taste_rating + (rating.cleanliness_rating || 0)) / 4).toFixed(1)}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                        <div className="bg-white rounded p-2 text-center">
                          <p className="text-xs text-gray-600 mb-1">الخدمة</p>
                          <div className="flex items-center justify-center gap-1">
                            <Star className="h-3 w-3 text-amber-500 fill-current" />
                            <span className="font-bold">{rating.service_rating}</span>
                          </div>
                        </div>

                        <div className="bg-white rounded p-2 text-center">
                          <p className="text-xs text-gray-600 mb-1">السعر</p>
                          <div className="flex items-center justify-center gap-1">
                            <Star className="h-3 w-3 text-amber-500 fill-current" />
                            <span className="font-bold">{rating.price_rating}</span>
                          </div>
                        </div>

                        <div className="bg-white rounded p-2 text-center">
                          <p className="text-xs text-gray-600 mb-1">الطعم</p>
                          <div className="flex items-center justify-center gap-1">
                            <Star className="h-3 w-3 text-amber-500 fill-current" />
                            <span className="font-bold">{rating.taste_rating}</span>
                          </div>
                        </div>

                        <div className="bg-white rounded p-2 text-center">
                          <p className="text-xs text-gray-600 mb-1">النظافة</p>
                          <div className="flex items-center justify-center gap-1">
                            <Star className="h-3 w-3 text-amber-500 fill-current" />
                            <span className="font-bold">{rating.cleanliness_rating || 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      {rating.comment && (
                        <div className="bg-white rounded p-3 border-r-4 border-orange-500">
                          <p className="text-xs text-gray-600 mb-1 font-medium">تعليقك:</p>
                          <p className="text-sm text-gray-700">{rating.comment}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <div className="space-y-6">
              {/* Points and Level Section */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-3 rounded-full">
                      <Trophy className="h-8 w-8" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{user?.level || 'مبتدئ'}</h3>
                      <p className="text-sm text-white/80">مستواك الحالي</p>
                    </div>
                  </div>
                  <div className="text-left">
                    <div className="text-3xl font-bold">{user?.points || 0}</div>
                    <p className="text-sm text-white/80">نقطة</p>
                  </div>
                </div>
                
                {/* Progress to next level */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>التقدم إلى المستوى التالي</span>
                    <span>
                      {user?.level === 'مبتدئ' && '50 نقطة'}
                      {user?.level === 'نشط' && '150 نقطة'}
                      {user?.level === 'محترف' && '300 نقطة'}
                      {user?.level === 'خبير' && '500 نقطة'}
                      {user?.level === 'أسطورة' && 'أقصى مستوى'}
                    </span>
                  </div>
                  <div className="w-full bg-white/30 rounded-full h-2">
                    <div 
                      className="bg-white h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          user?.level === 'مبتدئ' ? Math.min((user?.points || 0) / 50 * 100, 100) :
                          user?.level === 'نشط' ? Math.min(((user?.points || 0) - 50) / 100 * 100, 100) :
                          user?.level === 'محترف' ? Math.min(((user?.points || 0) - 150) / 150 * 100, 100) :
                          user?.level === 'خبير' ? Math.min(((user?.points || 0) - 300) / 200 * 100, 100) :
                          100
                        }%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* How to earn points */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start gap-3">
                  <TrendingUp className="h-5 w-5 text-blue-600 mt-1" />
                  <div>
                    <h4 className="font-bold text-blue-900 mb-2">كيف تكسب النقاط؟</h4>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li className="flex items-center gap-2">
                        <span className="bg-blue-200 rounded-full px-2 py-0.5 font-bold">+10</span>
                        <span>كتابة تقييم جديد</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="bg-blue-200 rounded-full px-2 py-0.5 font-bold">+5</span>
                        <span>إضافة تعليق على التقييم</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="bg-blue-200 rounded-full px-2 py-0.5 font-bold">+5</span>
                        <span>كتابة تعليق مفصل (أكثر من 50 حرف)</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Badges Section */}
              <div>
                <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Award className="h-5 w-5 text-orange-600" />
                  أوسمتك ({user?.badges?.length || 0})
                </h4>
                
                {(!user?.badges || user.badges.length === 0) ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Award className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600">لم تحصل على أي وسام بعد</p>
                    <p className="text-sm text-gray-500 mt-2">قم بكتابة المزيد من التقييمات لكسب الأوسمة</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {user.badges.map((badge, index) => (
                      <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-4 border-2 border-amber-200 text-center">
                        <Award className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                        <p className="font-bold text-amber-900">{badge}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Available Badges to unlock */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <h5 className="font-bold text-sm text-gray-700 mb-3">الأوسمة المتاحة:</h5>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center justify-between">
                      <span>🌟 مقيّم مبتدئ</span>
                      <span className="text-xs text-gray-500">(تقييم واحد)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>⭐ مقيّم نشط</span>
                      <span className="text-xs text-gray-500">(5 تقييمات)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>🏆 خبير التقييمات</span>
                      <span className="text-xs text-gray-500">(10 تقييمات)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>👑 أسطورة التقييمات</span>
                      <span className="text-xs text-gray-500">(20 تقييم)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Password Tab - Inline ChangePassword */}
          {activeTab === 'password' && (
            <ChangePasswordInline />
          )}

        </CardContent>
      </Card>
    </div>
  );
};

// Inline Change Password Component
const ChangePasswordInline = () => {
  const [loading, setLoading] = useState(false);
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('كلمة المرور الجديدة وتأكيدها غير متطابقين');
      return;
    }

    if (passwords.new_password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setLoading(true);
    
    try {
      await axios.put(`${API}/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('تم تغيير كلمة المرور بنجاح');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
      <div>
        <Label htmlFor="current_password">كلمة المرور الحالية</Label>
        <Input
          id="current_password"
          type="password"
          value={passwords.current_password}
          onChange={(e) => setPasswords({...passwords, current_password: e.target.value})}
          required
          dir="ltr"
          style={{ textAlign: 'left' }}
        />
      </div>

      <div>
        <Label htmlFor="new_password">كلمة المرور الجديدة</Label>
        <Input
          id="new_password"
          type="password"
          value={passwords.new_password}
          onChange={(e) => setPasswords({...passwords, new_password: e.target.value})}
          required
          minLength={6}
          dir="ltr"
          style={{ textAlign: 'left' }}
        />
      </div>

      <div>
        <Label htmlFor="confirm_password">تأكيد كلمة المرور الجديدة</Label>
        <Input
          id="confirm_password"
          type="password"
          value={passwords.confirm_password}
          onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})}
          required
          minLength={6}
          dir="ltr"
          style={{ textAlign: 'left' }}
        />
      </div>

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
      </Button>
    </form>
  );
};

// Change Password Component
const ChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('كلمة المرور الجديدة وتأكيدها غير متطابقين');
      return;
    }

    if (passwords.new_password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setLoading(true);
    
    try {
      await axios.put(`${API}/change-password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('تم تغيير كلمة المرور بنجاح');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6" style={{ direction: 'rtl' }}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            تغيير كلمة المرور
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="current_password">كلمة المرور الحالية</Label>
              <Input
                id="current_password"
                type="password"
                value={passwords.current_password}
                onChange={(e) => setPasswords({...passwords, current_password: e.target.value})}
                required
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
            </div>

            <div>
              <Label htmlFor="new_password">كلمة المرور الجديدة</Label>
              <Input
                id="new_password"
                type="password"
                value={passwords.new_password}
                onChange={(e) => setPasswords({...passwords, new_password: e.target.value})}
                required
                minLength={6}
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
            </div>

            <div>
              <Label htmlFor="confirm_password">تأكيد كلمة المرور الجديدة</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwords.confirm_password}
                onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})}
                required
                minLength={6}
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'جاري التغيير...' : 'تغيير كلمة المرور'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Contact Us Component
const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const whatsappNumber = '+4915756349382';
  const whatsappMessage = 'مرحبا، أريد التواصل معكم بخصوص تطبيق دليل المطاعم';

  const handleWhatsApp = () => {
    const url = `https://wa.me/${whatsappNumber.replace('+', '')}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, contact);
      toast.success('تم إرسال رسالتك بنجاح. سنتواصل معك قريباً');
      setContact({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إرسال الرسالة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" style={{ direction: 'rtl' }}>
      {/* قسم الواتساب */}
      <Card className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="bg-green-500 p-4 rounded-full">
                <MessageCircle className="h-8 w-8 text-white" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-green-800">تواصل معنا عبر الواتساب</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              نحن هنا للمساعدة! إن كان لديك مشكلة أو اقتراح أو تعديل بيانات مطعم من معلومات وصور، أرسل لنا رسالتك عبر الواتساب أو عبر نموذج الاتصال وسنتواصل معك في أقرب وقت ممكن.
            </p>
            <Button 
              onClick={handleWhatsApp}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
            >
              <MessageCircle className="h-5 w-5 ml-2" />
              راسلنا على الواتساب
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* خط فاصل */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-px bg-gray-300"></div>
        <span className="text-gray-500 text-sm">أو</span>
        <div className="flex-1 h-px bg-gray-300"></div>
      </div>

      {/* نموذج الاتصال */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            نموذج الاتصال
          </CardTitle>
          <CardDescription>
            يمكنك أيضاً إرسال رسالتك عبر النموذج التالي
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">الاسم الكامل</Label>
                <Input
                  id="name"
                  value={contact.name}
                  onChange={(e) => setContact({...contact, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({...contact, email: e.target.value})}
                  required
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">رقم الهاتف</Label>
              <Input
                id="phone"
                value={contact.phone}
                onChange={(e) => setContact({...contact, phone: e.target.value})}
              />
            </div>

            <div>
              <Label htmlFor="subject">عنوان الرسالة</Label>
              <Input
                id="subject"
                value={contact.subject}
                onChange={(e) => setContact({...contact, subject: e.target.value})}
                placeholder="مثال: استفسار عن مطعم، مشكلة تقنية، اقتراح..."
                required
              />
            </div>

            <div>
              <Label htmlFor="message">الرسالة</Label>
              <textarea
                id="message"
                value={contact.message}
                onChange={(e) => setContact({...contact, message: e.target.value})}
                required
                rows={5}
                className="w-full p-2 border border-gray-300 rounded-md resize-none"
                placeholder="اكتب رسالتك هنا..."
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'جاري الإرسال...' : 'إرسال الرسالة'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Initial Location Permission Component
const InitialLocationPermission = () => {
  const [showDialog, setShowDialog] = useState(false);
  
  useEffect(() => {
    const permission = localStorage.getItem('locationPermission');
    const hasAsked = localStorage.getItem('locationAsked');
    
    // If first time using app and no permission set
    if (!hasAsked && !permission) {
      setShowDialog(true);
    }
  }, []);

  const handlePermissionChoice = (choice) => {
    localStorage.setItem('locationPermission', choice);
    localStorage.setItem('locationAsked', 'true');
    setShowDialog(false);

    if (choice === 'always') {
      if (navigator.geolocation) {
        const options = {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 0
        };
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            localStorage.setItem('userLocation', JSON.stringify(location));
            toast.success('تم حفظ موقعك لإظهار المطاعم القريبة');
          },
          (error) => {
            console.error('خطأ في تحديد الموقع:', error);
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            let msg = 'لا يمكن تحديد موقعك الحالي';
            if (error.code === 1) {
              msg = isMobile
                ? '📱 تم رفض إذن الموقع. افتح إعدادات التطبيق وفعّل "الموقع"'
                : '💻 تم رفض إذن الموقع. اضغط على أيقونة القفل 🔒 بجانب العنوان واختر "السماح"';
            } else if (error.code === 2) {
              msg = isMobile
                ? '📱 الموقع غير متاح. تأكد من تفعيل GPS من إعدادات الجهاز'
                : '💻 الموقع غير متاح. تأكد من تفعيل GPS واتصالك بالإنترنت';
            } else if (error.code === 3) {
              msg = 'انتهت مهلة تحديد الموقع. حاول مرة أخرى';
            }
            toast.error(msg, { duration: 6000 });
          },
          options
        );
      } else {
        toast.error('المتصفح لا يدعم تحديد الموقع');
      }
    }
  };

  if (!showDialog) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]" style={{ direction: 'rtl' }}>
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <Navigation className="w-8 h-8 text-blue-600" />
          </div>
          <CardTitle className="text-xl">تحديد الموقع</CardTitle>
          <CardDescription>
            هل تريد السماح للتطبيق بتحديد موقعك لإظهار المطاعم القريبة؟
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Button
              onClick={() => handlePermissionChoice('always')}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              <Navigation className="w-4 h-4 ml-2" />
              السماح دائماً (موصى به)
            </Button>
            
            <Button
              variant="outline"
              onClick={() => handlePermissionChoice('ask')}
              className="w-full"
            >
              السؤال في كل مرة
            </Button>
            
            <Button
              variant="ghost"
              onClick={() => handlePermissionChoice('never')}
              className="w-full text-gray-600"
            >
              عدم السماح
            </Button>
          </div>
          
          <div className="text-xs text-gray-500 text-center">
            يمكنك تغيير هذا الإعداد لاحقاً من قائمة الحساب الشخصي
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Location Settings Component
const LocationSettings = () => {
  const [locationPermission, setLocationPermission] = useState(
    localStorage.getItem('locationPermission') || 'ask'
  );

  const handleLocationPermissionChange = (permission) => {
    setLocationPermission(permission);
    localStorage.setItem('locationPermission', permission);
    
    if (permission === 'always') {
      // Request location immediately
      if (navigator.geolocation) {
        const options = {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 0
        };
        
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            localStorage.setItem('userLocation', JSON.stringify(location));
            toast.success('تم حفظ موقعك بنجاح');
          },
          (error) => {
            console.error('خطأ في تحديد الموقع:', error);
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            let msg = 'لا يمكن تحديد موقعك الحالي';
            if (error.code === 1) {
              msg = isMobile
                ? '📱 تم رفض إذن الموقع. افتح إعدادات التطبيق وفعّل "الموقع"'
                : '💻 تم رفض إذن الموقع. اضغط على أيقونة القفل 🔒 بجانب العنوان واختر "السماح"';
            } else if (error.code === 2) {
              msg = isMobile
                ? '📱 الموقع غير متاح. تأكد من تفعيل GPS من إعدادات الجهاز'
                : '💻 الموقع غير متاح. تأكد من تفعيل GPS واتصالك بالإنترنت';
            } else if (error.code === 3) {
              msg = 'انتهت مهلة تحديد الموقع. حاول مرة أخرى';
            }
            toast.error(msg, { duration: 6000 });
          },
          options
        );
      } else {
        toast.error('المتصفح لا يدعم تحديد الموقع');
      }
    } else if (permission === 'never') {
      localStorage.removeItem('userLocation');
      toast.success('تم إيقاف تحديد الموقع');
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6" style={{ direction: 'rtl' }}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Navigation className="h-5 w-5" />
            إعدادات تحديد الموقع
          </CardTitle>
          <CardDescription>
            اختر كيف تريد التعامل مع تحديد موقعك في التطبيق
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              locationPermission === 'always' ? 'border-green-500 bg-green-50' : 'border-gray-200'
            }`}
            onClick={() => handleLocationPermissionChange('always')}>
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  locationPermission === 'always' ? 'border-green-500 bg-green-500' : 'border-gray-300'
                }`}></div>
                <div>
                  <h3 className="font-medium">السماح دائماً</h3>
                  <p className="text-sm text-gray-600">سيتم تحديد موقعك تلقائياً لإظهار المطاعم القريبة</p>
                </div>
              </div>
            </div>

            <div className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              locationPermission === 'ask' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
            }`}
            onClick={() => handleLocationPermissionChange('ask')}>
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  locationPermission === 'ask' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`}></div>
                <div>
                  <h3 className="font-medium">السؤال في كل مرة</h3>
                  <p className="text-sm text-gray-600">سيتم السؤال عن الإذن في كل مرة</p>
                </div>
              </div>
            </div>

            <div className={`p-4 border rounded-lg cursor-pointer transition-colors ${
              locationPermission === 'never' ? 'border-red-500 bg-red-50' : 'border-gray-200'
            }`}
            onClick={() => handleLocationPermissionChange('never')}>
              <div className="flex items-center gap-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  locationPermission === 'never' ? 'border-red-500 bg-red-500' : 'border-gray-300'
                }`}></div>
                <div>
                  <h3 className="font-medium">عدم السماح أبداً</h3>
                  <p className="text-sm text-gray-600">لن يتم طلب تحديد الموقع نهائياً</p>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-sm text-gray-600">
              يمكنك تغيير هذا الإعداد في أي وقت من قائمة الحساب الشخصي
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// About App Component
const AboutApp = () => {
  return (
    <div className="max-w-4xl mx-auto p-6" style={{ direction: 'rtl' }}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            معلومات عن التطبيق
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">دليل المطاعم</h3>
            <p className="text-gray-700">
              تطبيق شامل للعثور على أفضل المطاعم. نهدف إلى ربط المجتمع بأطعمة الوطن الأصيلة - طعم الوطن بعيداً عن الوطن.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">المميزات:</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>البحث عن المطاعم حسب المدينة ونوع المطبخ</li>
              <li>تقييمات المستخدمين للخدمة والأسعار والطعم</li>
              <li>نظام المفضلة وحفظ المطاعم</li>
              <li>خرائط Google للتنقل</li>
              <li>كوبونات الخصم</li>
              <li>مشاركة المطاعم عبر وسائل التواصل</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">الإصدار:</h4>
            <p className="text-gray-700">1.0.0</p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">تواصل معنا:</h4>
            <p className="text-gray-700">
              لأي استفسارات أو اقتراحات، يمكنكم التواصل معنا عبر صفحة "الاتصال بنا"
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Privacy Policy Component
const PrivacyPolicy = () => {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl" style={{ direction: 'rtl' }}>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-center">سياسة الخصوصية</CardTitle>
          <CardDescription className="text-center">آخر تحديث: فبراير 2026</CardDescription>
        </CardHeader>
        <CardContent className="prose prose-lg max-w-none space-y-6 text-gray-700">
          
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. مقدمة</h2>
            <p>
              مرحباً بك في تطبيق "دليل المطاعم". نحن نحترم خصوصيتك ونلتزم بحماية بياناتك الشخصية. 
              توضح سياسة الخصوصية هذه كيفية جمع واستخدام وحماية معلوماتك عند استخدام تطبيقنا.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. البيانات التي نجمعها</h2>
            <p>نقوم بجمع الأنواع التالية من البيانات:</p>
            <ul className="list-disc list-inside space-y-2 mr-4">
              <li><strong>معلومات الحساب:</strong> الاسم، البريد الإلكتروني، وكلمة المرور عند إنشاء حساب</li>
              <li><strong>بيانات الموقع:</strong> موقعك الجغرافي (بإذنك) لعرض المطاعم القريبة منك</li>
              <li><strong>بيانات الاستخدام:</strong> المطاعم المفضلة، التقييمات، وسجل البحث</li>
              <li><strong>معلومات الجهاز:</strong> نوع الجهاز، نظام التشغيل، ومعرّف الجهاز للإشعارات</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. كيف نستخدم بياناتك</h2>
            <p>نستخدم بياناتك للأغراض التالية:</p>
            <ul className="list-disc list-inside space-y-2 mr-4">
              <li>عرض المطاعم القريبة من موقعك</li>
              <li>حفظ مطاعمك المفضلة وتقييماتك</li>
              <li>إرسال إشعارات بالعروض والمطاعم الجديدة (بإذنك)</li>
              <li>تحسين خدماتنا وتجربة المستخدم</li>
              <li>التواصل معك بخصوص حسابك</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. مشاركة البيانات</h2>
            <p>
              نحن لا نبيع أو نؤجر بياناتك الشخصية لأطراف ثالثة. قد نشارك بياناتك فقط في الحالات التالية:
            </p>
            <ul className="list-disc list-inside space-y-2 mr-4">
              <li>مع مزودي الخدمات الذين يساعدوننا في تشغيل التطبيق (مثل خدمات الاستضافة)</li>
              <li>عند الضرورة للامتثال للقوانين أو الإجراءات القانونية</li>
              <li>لحماية حقوقنا أو سلامة المستخدمين</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. أمان البيانات</h2>
            <p>
              نتخذ إجراءات أمنية مناسبة لحماية بياناتك من الوصول غير المصرح به أو التغيير أو الكشف أو الإتلاف.
              تشمل هذه الإجراءات التشفير والخوادم الآمنة وضوابط الوصول المحدودة.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. حقوقك</h2>
            <p>لديك الحقوق التالية بخصوص بياناتك:</p>
            <ul className="list-disc list-inside space-y-2 mr-4">
              <li><strong>الوصول:</strong> يمكنك طلب نسخة من بياناتك الشخصية</li>
              <li><strong>التصحيح:</strong> يمكنك تحديث أو تصحيح بياناتك في أي وقت</li>
              <li><strong>الحذف:</strong> يمكنك طلب حذف حسابك وبياناتك من خلال إعدادات التطبيق</li>
              <li><strong>الإلغاء:</strong> يمكنك إلغاء الاشتراك في الإشعارات في أي وقت</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. ملفات تعريف الارتباط (Cookies)</h2>
            <p>
              نستخدم ملفات تعريف الارتباط لتحسين تجربتك في التطبيق. يمكنك التحكم في إعدادات ملفات تعريف الارتباط 
              من خلال إعدادات جهازك أو المتصفح.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. التغييرات على سياسة الخصوصية</h2>
            <p>
              قد نقوم بتحديث سياسة الخصوصية من وقت لآخر. سنقوم بإخطارك بأي تغييرات جوهرية 
              عبر التطبيق أو البريد الإلكتروني.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. الاتصال بنا</h2>
            <p>
              إذا كانت لديك أي أسئلة حول سياسة الخصوصية هذه، يرجى التواصل معنا عبر:
            </p>
            <ul className="list-disc list-inside space-y-2 mr-4">
              <li>البريد الإلكتروني: privacy@dalilfood.de</li>
              <li>الموقع الإلكتروني: dalilfood.de</li>
            </ul>
          </section>

          <div className="mt-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800 text-center">
              باستخدامك لتطبيق "دليل المطاعم"، فإنك توافق على سياسة الخصوصية هذه.
            </p>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

// Delete Account Component
const DeleteAccount = () => {
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const { logout } = useAuth();

  const handleDelete = async () => {
    if (confirmText !== 'احذف حسابي') {
      toast.error('يرجى كتابة "احذف حسابي" للتأكيد');
      return;
    }

    setLoading(true);
    
    try {
      await axios.delete(`${API}/account`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('تم حذف حسابك بنجاح');
      logout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في حذف الحساب');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6" style={{ direction: 'rtl' }}>
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <X className="h-5 w-5" />
            حذف الحساب
          </CardTitle>
          <CardDescription className="text-red-500">
            تحذير: هذا الإجراء لا يمكن التراجع عنه
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 mb-2">
              <strong>سيتم حذف جميع بياناتك نهائياً:</strong>
            </p>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              <li>معلومات الحساب الشخصية</li>
              <li>قائمة المطاعم المفضلة</li>
              <li>التقييمات والتعليقات</li>
              <li>سجل النشاط</li>
            </ul>
          </div>

          <div>
            <Label htmlFor="confirm">اكتب "احذف حسابي" للتأكيد:</Label>
            <Input
              id="confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="احذف حسابي"
            />
          </div>

          <Button 
            onClick={handleDelete}
            disabled={loading || confirmText !== 'احذف حسابي'}
            variant="destructive"
            className="w-full"
          >
            {loading ? 'جاري الحذف...' : 'حذف الحساب نهائياً'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Top Rated Restaurants Component
const TopRatedRestaurants = ({ restaurants, onRestaurantClick }) => {
  const topRated = restaurants
    .filter(r => r.average_rating && r.average_rating >= 4.0)
    .sort((a, b) => b.average_rating - a.average_rating)
    .slice(0, 20);

  return (
    <div className="space-y-6" style={{ direction: 'rtl' }}>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">المطاعم الأكثر تقييماً</h2>
        <p className="text-gray-600">أفضل المطاعم العربية بتقييم 4 نجوم وأكثر</p>
      </div>

      {topRated.length === 0 ? (
        <div className="text-center py-12">
          <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">لا توجد مطاعم بتقييم عالي حتى الآن</h3>
          <p className="text-gray-500">كن أول من يقيم المطاعم لمساعدة الآخرين</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {topRated.map((restaurant, index) => (
            <RestaurantCard 
              key={restaurant.id} 
              restaurant={restaurant}
              rank={index + 1}
              showRank={true}
              onClick={() => onRestaurantClick && onRestaurantClick(restaurant)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Verification Component
const VerificationDialog = ({ email, onVerified, demoCode }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/verify`, { email, verification_code: code });
      onVerified(response.data.token, response.data.user);
      toast.success('تم تأكيد الحساب بنجاح');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في التأكيد');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" style={{ direction: 'rtl' }}>
      <div className="text-center mb-4">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
        <h3 className="text-lg font-semibold">تأكيد الحساب</h3>
        <p className="text-sm text-gray-600">
          تم إرسال رمز التأكيد المكون من 5 خانات إلى: {email}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="code">رمز التأكيد</Label>
        <Input
          id="code"
          type="text"
          maxLength={5}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="12345"
          className="text-center text-2xl tracking-widest"
          required
        />
      </div>

      <Button type="submit" className="w-full" disabled={loading || code.length !== 5}>
        {loading ? 'جاري التأكيد...' : 'تأكيد الحساب'}
      </Button>

      {demoCode && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800 text-center">
            رمز التأكيد (للتجربة): <strong className="text-lg">{demoCode}</strong>
          </p>
          <p className="text-xs text-amber-600 text-center mt-1">
            انسخ الرمز أعلاه واستخدمه للتأكيد
          </p>
        </div>
      )}
    </form>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [deleteType, setDeleteType] = useState('soft');
  const [deleting, setDeleting] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  
  // User management states
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [savingUser, setSavingUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    city: '',
    country: '',
    role: 'user',
    is_admin: false,
    is_verified: true
  });
  
  // Rating management states
  const [showEditRatingDialog, setShowEditRatingDialog] = useState(false);
  const [showDeleteRatingDialog, setShowDeleteRatingDialog] = useState(false);
  const [selectedRating, setSelectedRating] = useState(null);
  const [editingRating, setEditingRating] = useState(null);
  const [savingRating, setSavingRating] = useState(false);
  
  // User ratings view state
  const [showUserRatingsDialog, setShowUserRatingsDialog] = useState(false);
  const [userRatings, setUserRatings] = useState([]);
  const [loadingUserRatings, setLoadingUserRatings] = useState(false);

  // Push Notifications states
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Backup states
  const [exportingBackup, setExportingBackup] = useState(false);

  // دالة تصدير النسخة الاحتياطية
  const handleExportBackup = async () => {
    setExportingBackup(true);
    try {
      const response = await axios.get(`${API}/admin/backup/restaurants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // إنشاء ملف JSON للتحميل
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        
        // إنشاء رابط تحميل
        const link = document.createElement('a');
        link.href = url;
        const date = new Date().toISOString().split('T')[0];
        link.download = `dalilfood_backup_${date}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`تم تصدير ${response.data.count} مطعم بنجاح`);
      }
    } catch (error) {
      toast.error('خطأ في تصدير البيانات');
      console.error('Export error:', error);
    } finally {
      setExportingBackup(false);
    }
  };

  // دالة تصدير كل البيانات
  const handleExportAllData = async () => {
    setExportingBackup(true);
    try {
      const response = await axios.get(`${API}/admin/backup/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        
        const link = document.createElement('a');
        link.href = url;
        const date = new Date().toISOString().split('T')[0];
        link.download = `dalilfood_full_backup_${date}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success('تم تصدير جميع البيانات بنجاح');
      }
    } catch (error) {
      toast.error('خطأ في تصدير البيانات');
      console.error('Export error:', error);
    } finally {
      setExportingBackup(false);
    }
  };

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, usersRes, ratingsRes, restaurantsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/ratings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/restaurants`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data);
      setRatings(ratingsRes.data);
      setRestaurants(restaurantsRes.data);
    } catch (error) {
      toast.error('خطأ في تحميل بيانات المدير');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRestaurant = async () => {
    if (!selectedRestaurant) return;
    
    setDeleting(true);
    
    try {
      const isPermanent = deleteType === 'permanent';
      await axios.delete(`${API}/restaurants/${selectedRestaurant.id}?permanent=${isPermanent}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const message = isPermanent ? 'تم حذف المطعم نهائياً' : 'تم إخفاء المطعم مؤقتاً';
      toast.success(message);
      
      // Remove from list
      setRestaurants(restaurants.filter(r => r.id !== selectedRestaurant.id));
      setShowDeleteDialog(false);
      setSelectedRestaurant(null);
      setDeleteType('soft');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في حذف المطعم');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user, password: '' }); // Don't set password
    setShowEditUserDialog(true);
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    
    setSavingUser(true);
    
    try {
      const response = await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم إنشاء المستخدم بنجاح');
      
      // Add to users list
      setUsers([response.data.user, ...users]);
      setShowAddUserDialog(false);
      setNewUser({
        name: '',
        email: '',
        password: '',
        city: '',
        country: '',
        role: 'user',
        is_admin: false,
        is_verified: true
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إنشاء المستخدم');
    } finally {
      setSavingUser(false);
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    setSavingUser(true);
    
    try {
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        city: editingUser.city,
        country: editingUser.country,
        phone: editingUser.phone || '',
        role: editingUser.role,
        is_admin: editingUser.is_admin,
        is_verified: editingUser.is_verified
      };
      
      // Only include password if it was changed
      if (editingUser.password && editingUser.password.trim() !== '') {
        updateData.password = editingUser.password;
      }
      
      const response = await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم تحديث بيانات المستخدم بنجاح');
      
      // Update users list
      setUsers(users.map(u => u.id === editingUser.id ? response.data.user : u));
      setShowEditUserDialog(false);
      setEditingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تحديث المستخدم');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setSavingUser(true);
    
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم حذف المستخدم بنجاح');
      
      // Remove from list
      setUsers(users.filter(u => u.id !== selectedUser.id));
      setShowDeleteUserDialog(false);
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في حذف المستخدم');
    } finally {
      setSavingUser(false);
    }
  };

  const handleEditRating = (rating) => {
    setEditingRating({ ...rating });
    setShowEditRatingDialog(true);
  };

  const handleSaveRating = async () => {
    if (!editingRating) return;
    
    setSavingRating(true);
    
    try {
      const updateData = {
        service_rating: editingRating.service_rating,
        price_rating: editingRating.price_rating,
        taste_rating: editingRating.taste_rating,
        cleanliness_rating: editingRating.cleanliness_rating,
        comment: editingRating.comment || ''
      };
      
      const response = await axios.put(`${API}/admin/ratings/${editingRating.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم تحديث التقييم بنجاح');
      
      // Update ratings list
      setRatings(ratings.map(r => r.id === editingRating.id ? { ...r, ...updateData } : r));
      setShowEditRatingDialog(false);
      setEditingRating(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تحديث التقييم');
    } finally {
      setSavingRating(false);
    }
  };

  const handleDeleteRating = async () => {
    if (!selectedRating) return;
    
    setSavingRating(true);
    
    try {
      await axios.delete(`${API}/admin/ratings/${selectedRating.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم حذف التقييم بنجاح');
      
      // Remove from list
      setRatings(ratings.filter(r => r.id !== selectedRating.id));
      setShowDeleteRatingDialog(false);
      setSelectedRating(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في حذف التقييم');
    } finally {
      setSavingRating(false);
    }
  };

  // Fetch user ratings when dialog opens
  useEffect(() => {
    if (showUserRatingsDialog && selectedUser) {
      fetchUserRatings(selectedUser.id);
    }
  }, [showUserRatingsDialog, selectedUser]);

  const fetchUserRatings = async (userId) => {
    setLoadingUserRatings(true);
    try {
      const response = await axios.get(`${API}/admin/ratings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter ratings for this user
      const userSpecificRatings = response.data.filter(r => r.user_id === userId);
      setUserRatings(userSpecificRatings);
    } catch (error) {
      toast.error('خطأ في تحميل تقييمات المستخدم');
    } finally {
      setLoadingUserRatings(false);
    }
  };

  // Push Notification functions
  const handleSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      toast.error('يرجى إدخال عنوان ونص الإشعار');
      return;
    }

    setSendingNotification(true);
    try {
      const response = await axios.post(
        `${API}/admin/notifications/send`,
        {
          title: notificationTitle,
          message: notificationMessage,
          segment: "All"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`تم إرسال الإشعار بنجاح! وصل إلى ${response.data.recipients || 0} مستخدم`);
        setNotificationTitle('');
        setNotificationMessage('');
        // Refresh history
        fetchNotificationHistory();
      } else {
        toast.error(response.data.error || 'فشل في إرسال الإشعار');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إرسال الإشعار');
    } finally {
      setSendingNotification(false);
    }
  };

  const fetchNotificationHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${API}/admin/notifications/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationHistory(response.data);
    } catch (error) {
      console.error('Error fetching notification history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Coffee className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
        <p className="text-lg text-gray-600">جاري تحميل لوحة التحكم...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ direction: 'rtl' }}>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">لوحة تحكم المدير</h2>
        <p className="text-gray-600">إدارة المستخدمين والتقييمات</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="stats">الإحصائيات</TabsTrigger>
          <TabsTrigger value="users">المستخدمين</TabsTrigger>
          <TabsTrigger value="ratings">التقييمات</TabsTrigger>
          <TabsTrigger value="restaurants">المطاعم</TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-1">
            <Bell className="h-4 w-4" />
            الإشعارات
          </TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="space-y-4">
          {stats && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">إجمالي المستخدمين</p>
                        <p className="text-3xl font-bold text-blue-600">{stats.total_users}</p>
                      </div>
                      <Users className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">إجمالي المطاعم</p>
                        <p className="text-3xl font-bold text-green-600">{stats.total_restaurants}</p>
                      </div>
                      <Utensils className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">إجمالي التقييمات</p>
                        <p className="text-3xl font-bold text-amber-600">{stats.total_ratings}</p>
                      </div>
                      <Star className="h-8 w-8 text-amber-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">المستخدمين المؤكدين</p>
                        <p className="text-2xl font-bold text-green-600">{stats.verified_users}</p>
                      </div>
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">المستخدمين غير المؤكدين</p>
                        <p className="text-2xl font-bold text-red-600">{stats.unverified_users}</p>
                      </div>
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>التسجيلات الأخيرة</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.recent_registrations.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                          <p className="text-xs text-gray-400">{user.city}, {user.country}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {user.role === 'admin' && (
                            <Badge className="bg-orange-100 text-orange-800">مدير</Badge>
                          )}
                          {user.is_verified ? (
                            <Badge className="bg-green-100 text-green-800">مؤكد</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">غير مؤكد</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>جميع المستخدمين</CardTitle>
              <Button
                onClick={() => setShowAddUserDialog(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <UserPlus className="h-4 w-4 ml-2" />
                إضافة مستخدم
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-right p-2">الاسم</th>
                      <th className="text-right p-2">البريد الإلكتروني</th>
                      <th className="text-right p-2">الموقع</th>
                      <th className="text-right p-2">النوع</th>
                      <th className="text-right p-2">الحالة</th>
                      <th className="text-right p-2">التسجيل</th>
                      <th className="text-center p-2">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b">
                        <td className="p-2">{user.name}</td>
                        <td className="p-2">{user.email}</td>
                        <td className="p-2 text-sm">
                          <div>{user.city}</div>
                          <div className="text-gray-500">{user.country || 'غير محدد'}</div>
                        </td>
                        <td className="p-2">
                          {user.role === 'admin' ? (
                            <Badge className="bg-orange-100 text-orange-800">مدير</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800">مستخدم</Badge>
                          )}
                        </td>
                        <td className="p-2">
                          {user.is_verified ? (
                            <Badge className="bg-green-100 text-green-800">مؤكد</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">غير مؤكد</Badge>
                          )}
                        </td>
                        <td className="p-2 text-sm">
                          {new Date(user.created_at).toLocaleDateString('ar')}
                        </td>
                        <td className="p-2">
                          <div className="flex justify-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleEditUser(user)}
                              title="تعديل المستخدم"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowUserRatingsDialog(true);
                              }}
                              title="عرض تقييمات المستخدم"
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowDeleteUserDialog(true);
                              }}
                              title="حذف المستخدم"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ratings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>جميع التقييمات</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <div key={rating.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium">{rating.user_name || 'مستخدم'}</p>
                        <p className="text-sm text-gray-500">{rating.user_city}</p>
                        <p className="text-sm font-medium text-amber-600">{rating.restaurant_name}</p>
                      </div>
                      <div className="text-right flex-1">
                        <div className="flex space-x-4 text-sm">
                          <span>خدمة: {rating.service_rating}/5</span>
                          <span>سعر: {rating.price_rating}/5</span>
                          <span>طعم: {rating.taste_rating}/5</span>
                          <span>نظافة: {rating.cleanliness_rating || 'N/A'}/5</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(rating.created_at).toLocaleDateString('ar')}
                        </p>
                      </div>
                      <div className="flex gap-2 mr-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleEditRating(rating)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedRating(rating);
                            setShowDeleteRatingDialog(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {rating.comment && (
                      <p className="text-sm text-gray-600 mt-2 p-2 bg-white rounded">
                        {rating.comment}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="restaurants" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Utensils className="h-5 w-5" />
                    جميع المطاعم ({restaurants.length})
                  </CardTitle>
                  <CardDescription>
                    قائمة بجميع المطاعم المسجلة في التطبيق
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleExportBackup}
                    disabled={exportingBackup}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {exportingBackup ? 'جاري التصدير...' : 'تصدير المطاعم'}
                  </Button>
                  <Button
                    onClick={handleExportAllData}
                    disabled={exportingBackup}
                    variant="default"
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <Download className="h-4 w-4" />
                    {exportingBackup ? 'جاري التصدير...' : 'نسخة احتياطية كاملة'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-right p-3 font-semibold">اسم المطعم</th>
                      <th className="text-right p-3 font-semibold">المدينة</th>
                      <th className="text-right p-3 font-semibold">رقم الهاتف</th>
                      <th className="text-right p-3 font-semibold">نوع المطبخ</th>
                      <th className="text-right p-3 font-semibold">التقييم</th>
                      <th className="text-right p-3 font-semibold">عدد المشاهدات</th>
                      <th className="text-right p-3 font-semibold">كوبون</th>
                      <th className="text-right p-3 font-semibold">تاريخ الإضافة</th>
                      <th className="text-center p-3 font-semibold">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...restaurants].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((restaurant) => (
                      <tr key={restaurant.id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="p-3">
                          <div>
                            <p className="font-medium text-gray-900">{restaurant.name_ar}</p>
                            {restaurant.name && (
                              <p className="text-sm text-gray-500">{restaurant.name}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="text-gray-700">{restaurant.city || 'غير محدد'}</span>
                        </td>
                        <td className="p-3">
                          {restaurant.phone ? (
                            <a 
                              href={`tel:${restaurant.phone}`}
                              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                              title="اضغط للاتصال"
                            >
                              <Phone className="h-4 w-4" />
                              <span dir="ltr">{restaurant.phone}</span>
                            </a>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-400" />
                              <span className="text-gray-400">غير محدد</span>
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-gray-700">{restaurant.cuisine_type_ar || restaurant.cuisine_type || 'غير محدد'}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-amber-500 fill-current" />
                            <span className="text-gray-700">
                              {isRestaurantNew(restaurant) ? 'جديد' : (restaurant.average_rating > 0 ? restaurant.average_rating.toFixed(1) : 'لا يوجد تقييمات')}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({restaurant.total_ratings || 0})
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Eye className="h-4 w-4 text-blue-500" />
                            <span className="font-medium text-gray-900">{restaurant.view_count || 0}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          {restaurant.has_coupon ? (
                            <Badge className="bg-orange-100 text-orange-800">
                              <Ticket className="h-3 w-3 mr-1" />
                              متوفر
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                              غير متوفر
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <span className="text-sm text-gray-500">
                            {new Date(restaurant.created_at).toLocaleDateString('en-GB')}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => setEditingRestaurant(restaurant)}
                              title="تعديل المطعم"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => {
                                setSelectedRestaurant(restaurant);
                                setShowDeleteDialog(true);
                              }}
                              title="حذف المطعم"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {restaurants.length === 0 && (
                  <div className="text-center py-8">
                    <Utensils className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">لا توجد مطاعم مسجلة حتى الآن</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Send Notification Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-orange-600" />
                  إرسال إشعار جديد
                </CardTitle>
                <CardDescription>
                  أرسل إشعارات فورية لجميع مستخدمي التطبيق
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="notificationTitle">عنوان الإشعار *</Label>
                  <Input
                    id="notificationTitle"
                    placeholder="مثال: عرض خاص اليوم!"
                    value={notificationTitle}
                    onChange={(e) => setNotificationTitle(e.target.value)}
                    className="mt-1"
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">{notificationTitle.length}/50 حرف</p>
                </div>

                <div>
                  <Label htmlFor="notificationMessage">نص الإشعار *</Label>
                  <Textarea
                    id="notificationMessage"
                    placeholder="مثال: اكتشف أفضل المطاعم العربية في منطقتك..."
                    value={notificationMessage}
                    onChange={(e) => setNotificationMessage(e.target.value)}
                    className="mt-1"
                    rows={4}
                    maxLength={200}
                  />
                  <p className="text-xs text-gray-500 mt-1">{notificationMessage.length}/200 حرف</p>
                </div>

                {/* Quick Templates */}
                <div>
                  <Label className="text-sm text-gray-600">قوالب سريعة:</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNotificationTitle('🆕 مطعم جديد!');
                        setNotificationMessage('تم إضافة مطعم جديد في منطقتك. اكتشفه الآن!');
                      }}
                    >
                      مطعم جديد
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNotificationTitle('🔥 عرض خاص!');
                        setNotificationMessage('لا تفوت العروض الحصرية من مطاعمنا المميزة!');
                      }}
                    >
                      عرض خاص
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNotificationTitle('🍽️ وقت الغداء!');
                        setNotificationMessage('هل جوعان؟ اكتشف أقرب المطاعم إليك الآن!');
                      }}
                    >
                      وقت الغداء
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setNotificationTitle('⭐ قيّم تجربتك');
                        setNotificationMessage('ساعد الآخرين باختيار أفضل المطاعم. شاركنا رأيك!');
                      }}
                    >
                      طلب تقييم
                    </Button>
                  </div>
                </div>

                <Button
                  onClick={handleSendNotification}
                  disabled={sendingNotification || !notificationTitle.trim() || !notificationMessage.trim()}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  {sendingNotification ? (
                    <>
                      <Coffee className="h-4 w-4 animate-spin ml-2" />
                      جاري الإرسال...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 ml-2" />
                      إرسال الإشعار لجميع المستخدمين
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Notification History Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Bell className="h-5 w-5 text-blue-600" />
                    سجل الإشعارات
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchNotificationHistory}
                    disabled={loadingHistory}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                  </Button>
                </CardTitle>
                <CardDescription>
                  آخر الإشعارات المرسلة
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <Coffee className="h-8 w-8 animate-spin text-amber-600 mx-auto mb-2" />
                    <p className="text-gray-500">جاري التحميل...</p>
                  </div>
                ) : notificationHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">لم يتم إرسال أي إشعارات بعد</p>
                    <Button
                      variant="link"
                      onClick={fetchNotificationHistory}
                      className="text-orange-600"
                    >
                      تحديث السجل
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {notificationHistory.map((notification) => (
                      <div
                        key={notification.id}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{notification.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                          </div>
                          <Badge variant="secondary" className="bg-green-100 text-green-800 mr-2">
                            {notification.recipients || 0} مستلم
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {notification.sent_by_name || 'مدير'}
                          </span>
                          <span>
                            {new Date(notification.created_at).toLocaleDateString('ar-SA', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Tips Card */}
          <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="p-4">
              <h3 className="font-semibold text-orange-800 mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                نصائح لإشعارات فعالة
              </h3>
              <ul className="text-sm text-orange-700 space-y-1 list-disc list-inside">
                <li>استخدم عناوين قصيرة وجذابة</li>
                <li>أضف إيموجي لجذب الانتباه 🎯</li>
                <li>أرسل في أوقات مناسبة (وقت الغداء أو العشاء)</li>
                <li>لا تكثر من الإشعارات - واحد أو اثنين في الأسبوع كافي</li>
                <li>قدم قيمة حقيقية (عروض، مطاعم جديدة، معلومات مفيدة)</li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Edit User Modal */}
      {showEditUserDialog && editingUser && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowEditUserDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4 text-gray-900">تعديل بيانات المستخدم</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الكامل</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-lg"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  placeholder="أدخل الاسم الكامل"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني</label>
                <input
                  type="email"
                  className="w-full p-2 border rounded-lg"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدينة</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-lg"
                    value={editingUser.city}
                    onChange={(e) => setEditingUser({...editingUser, city: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الدولة</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-lg"
                    value={editingUser.country || ''}
                    onChange={(e) => setEditingUser({...editingUser, country: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-lg"
                  value={editingUser.phone || ''}
                  onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور الجديدة (اتركها فارغة إذا لم ترد التغيير)</label>
                <input
                  type="password"
                  className="w-full p-2 border rounded-lg"
                  value={editingUser.password || ''}
                  onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                  placeholder="اترك فارغاً للإبقاء على كلمة المرور الحالية"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_admin"
                    checked={editingUser.is_admin || editingUser.role === 'admin'}
                    onChange={(e) => {
                      setEditingUser({
                        ...editingUser, 
                        is_admin: e.target.checked,
                        role: e.target.checked ? 'admin' : 'user'
                      });
                    }}
                  />
                  <label htmlFor="is_admin" className="text-sm font-medium">مدير (Admin)</label>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_verified"
                    checked={editingUser.is_verified}
                    onChange={(e) => setEditingUser({...editingUser, is_verified: e.target.checked})}
                  />
                  <label htmlFor="is_verified" className="text-sm font-medium">حساب مؤكد</label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditUserDialog(false);
                  setEditingUser(null);
                }}
                disabled={savingUser}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleSaveUser}
                disabled={savingUser}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {savingUser ? 'جاري الحفظ...' : 'حفظ التعديلات'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserDialog && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowAddUserDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4 text-gray-900">إضافة مستخدم جديد</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الاسم الكامل *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-lg"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="أدخل الاسم الكامل"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني *</label>
                <input
                  type="email"
                  className="w-full p-2 border rounded-lg"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="example@email.com"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور *</label>
                <input
                  type="password"
                  className="w-full p-2 border rounded-lg"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="أدخل كلمة المرور"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدينة</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-lg"
                    value={newUser.city}
                    onChange={(e) => setNewUser({...newUser, city: e.target.value})}
                    placeholder="المدينة"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الدولة</label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-lg"
                    value={newUser.country}
                    onChange={(e) => setNewUser({...newUser, country: e.target.value})}
                    placeholder="الدولة"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="new_is_admin"
                    checked={newUser.is_admin}
                    onChange={(e) => {
                      setNewUser({
                        ...newUser, 
                        is_admin: e.target.checked,
                        role: e.target.checked ? 'admin' : 'user'
                      });
                    }}
                  />
                  <label htmlFor="new_is_admin" className="text-sm font-medium">مدير (Admin)</label>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="new_is_verified"
                    checked={newUser.is_verified}
                    onChange={(e) => setNewUser({...newUser, is_verified: e.target.checked})}
                  />
                  <label htmlFor="new_is_verified" className="text-sm font-medium">حساب مؤكد</label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddUserDialog(false);
                  setNewUser({
                    name: '',
                    email: '',
                    password: '',
                    city: '',
                    country: '',
                    role: 'user',
                    is_admin: false,
                    is_verified: true
                  });
                }}
                disabled={savingUser}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleAddUser}
                disabled={savingUser}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {savingUser ? 'جاري الإنشاء...' : 'إنشاء المستخدم'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Rating Modal */}
      {showEditRatingDialog && editingRating && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowEditRatingDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4 text-gray-900">تعديل التقييم</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  تقييم الخدمة ({editingRating.service_rating}/5)
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  className="w-full"
                  value={editingRating.service_rating}
                  onChange={(e) => setEditingRating({...editingRating, service_rating: parseInt(e.target.value)})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  تقييم السعر ({editingRating.price_rating}/5)
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  className="w-full"
                  value={editingRating.price_rating}
                  onChange={(e) => setEditingRating({...editingRating, price_rating: parseInt(e.target.value)})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  تقييم الطعم ({editingRating.taste_rating}/5)
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  className="w-full"
                  value={editingRating.taste_rating}
                  onChange={(e) => setEditingRating({...editingRating, taste_rating: parseInt(e.target.value)})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  تقييم النظافة ({editingRating.cleanliness_rating}/5)
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  className="w-full"
                  value={editingRating.cleanliness_rating || 3}
                  onChange={(e) => setEditingRating({...editingRating, cleanliness_rating: parseInt(e.target.value)})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">التعليق</label>
                <textarea
                  className="w-full p-2 border rounded-lg"
                  rows="3"
                  value={editingRating.comment || ''}
                  onChange={(e) => setEditingRating({...editingRating, comment: e.target.value})}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditRatingDialog(false);
                  setEditingRating(null);
                }}
                disabled={savingRating}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleSaveRating}
                disabled={savingRating}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {savingRating ? 'جاري الحفظ...' : 'حفظ التعديلات'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Rating Modal */}
      {showDeleteRatingDialog && selectedRating && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowDeleteRatingDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <AlertCircle className="h-5 w-5" />
                <h2 className="text-lg font-semibold">تأكيد حذف التقييم</h2>
              </div>
              <p className="text-gray-600 text-sm">
                هل أنت متأكد من حذف تقييم المستخدم "{selectedRating.user_name}" للمطعم "{selectedRating.restaurant_name}"؟
              </p>
              <p className="text-red-600 text-sm mt-2">
                ⚠️ سيتم إعادة حساب متوسط تقييم المطعم بعد الحذف.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteRatingDialog(false);
                  setSelectedRating(null);
                }}
                disabled={savingRating}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleDeleteRating}
                disabled={savingRating}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {savingRating ? 'جاري الحذف...' : 'حذف التقييم'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* User Ratings Modal */}
      {showUserRatingsDialog && selectedUser && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowUserRatingsDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  تقييمات المستخدم: {selectedUser.name}
                </h2>
                <p className="text-sm text-gray-600">
                  {selectedUser.email} - {selectedUser.city}
                </p>
              </div>
              <button 
                onClick={() => setShowUserRatingsDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {loadingUserRatings ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
                <p className="text-gray-600 mt-4">جاري التحميل...</p>
              </div>
            ) : userRatings.length === 0 ? (
              <div className="text-center py-12">
                <Star className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">لا توجد تقييمات لهذا المستخدم</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 mb-4">
                  عدد التقييمات: <span className="font-bold">{userRatings.length}</span>
                </p>

                {userRatings.map((rating) => (
                  <div key={rating.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-orange-600 mb-1">
                          {rating.restaurant_name || 'مطعم غير معروف'}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {new Date(rating.created_at).toLocaleDateString('ar-SA', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => {
                            setEditingRating({ ...rating });
                            setShowUserRatingsDialog(false);
                            setShowEditRatingDialog(true);
                          }}
                          title="تعديل التقييم"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedRating(rating);
                            setShowUserRatingsDialog(false);
                            setShowDeleteRatingDialog(true);
                          }}
                          title="حذف التقييم"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-xs text-gray-600 mb-1">الخدمة</p>
                        <div className="flex items-center justify-center gap-1">
                          <Star className="h-4 w-4 text-amber-500 fill-current" />
                          <span className="font-bold text-lg">{rating.service_rating}</span>
                          <span className="text-gray-500 text-sm">/5</span>
                        </div>
                      </div>

                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-xs text-gray-600 mb-1">السعر</p>
                        <div className="flex items-center justify-center gap-1">
                          <Star className="h-4 w-4 text-amber-500 fill-current" />
                          <span className="font-bold text-lg">{rating.price_rating}</span>
                          <span className="text-gray-500 text-sm">/5</span>
                        </div>
                      </div>

                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-xs text-gray-600 mb-1">الطعم</p>
                        <div className="flex items-center justify-center gap-1">
                          <Star className="h-4 w-4 text-amber-500 fill-current" />
                          <span className="font-bold text-lg">{rating.taste_rating}</span>
                          <span className="text-gray-500 text-sm">/5</span>
                        </div>
                      </div>

                      <div className="bg-white rounded p-2 text-center">
                        <p className="text-xs text-gray-600 mb-1">النظافة</p>
                        <div className="flex items-center justify-center gap-1">
                          <Star className="h-4 w-4 text-amber-500 fill-current" />
                          <span className="font-bold text-lg">{rating.cleanliness_rating || 'N/A'}</span>
                          <span className="text-gray-500 text-sm">/5</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded p-3">
                      <p className="text-xs text-gray-600 mb-1 font-medium">المتوسط الإجمالي:</p>
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star}
                              className={`h-5 w-5 ${
                                star <= Math.round((rating.service_rating + rating.price_rating + rating.taste_rating + (rating.cleanliness_rating || 0)) / 4)
                                  ? 'text-amber-500 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="font-bold text-lg">
                          {((rating.service_rating + rating.price_rating + rating.taste_rating + (rating.cleanliness_rating || 0)) / 4).toFixed(1)}
                        </span>
                      </div>
                    </div>

                    {rating.comment && (
                      <div className="mt-3 bg-white rounded p-3 border-r-4 border-orange-500">
                        <p className="text-xs text-gray-600 mb-1 font-medium">التعليق:</p>
                        <p className="text-sm text-gray-700">{rating.comment}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowUserRatingsDialog(false)}
              >
                إغلاق
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {showDeleteUserDialog && selectedUser && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowDeleteUserDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <AlertCircle className="h-5 w-5" />
                <h2 className="text-lg font-semibold">تأكيد حذف المستخدم</h2>
              </div>
              <p className="text-gray-600 text-sm">
                هل أنت متأكد من حذف المستخدم "{selectedUser.name}"؟
              </p>
              <p className="text-red-600 text-sm mt-2">
                ⚠️ سيتم حذف جميع تقييماته ومفضلاته أيضاً. لا يمكن التراجع عن هذا الإجراء.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteUserDialog(false);
                  setSelectedUser(null);
                }}
                disabled={savingUser}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleDeleteUser}
                disabled={savingUser}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {savingUser ? 'جاري الحذف...' : 'حذف المستخدم'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Restaurant Modal - Simple Custom Implementation */}
      {showDeleteDialog && selectedRestaurant && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => {
            setShowDeleteDialog(false);
            setSelectedRestaurant(null);
            setDeleteType('soft');
          }}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="mb-4">
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <AlertCircle className="h-5 w-5" />
                <h2 className="text-lg font-semibold">تأكيد حذف المطعم</h2>
              </div>
              <p className="text-gray-600 text-sm">
                هل أنت متأكد من حذف مطعم "{selectedRestaurant.name_ar}"؟
              </p>
            </div>
            
            {/* Options */}
            <div className="space-y-3 mb-6">
              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  deleteType === 'soft' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'
                }`}
                onClick={() => setDeleteType('soft')}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    deleteType === 'soft' ? 'border-orange-500' : 'border-gray-300'
                  }`}>
                    {deleteType === 'soft' && (
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">إخفاء مؤقت</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      سيتم إخفاء المطعم من القوائم ويمكن استرجاعه لاحقاً
                    </p>
                  </div>
                </div>
              </div>

              <div 
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  deleteType === 'permanent' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-300'
                }`}
                onClick={() => setDeleteType('permanent')}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    deleteType === 'permanent' ? 'border-red-500' : 'border-gray-300'
                  }`}>
                    {deleteType === 'permanent' && (
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">حذف نهائي</h4>
                    <p className="text-sm text-gray-600 mt-1">
                      سيتم حذف المطعم نهائياً مع جميع التقييمات والمفضلة
                    </p>
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      ⚠️ لا يمكن التراجع عن هذا الإجراء
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setSelectedRestaurant(null);
                  setDeleteType('soft');
                }}
                disabled={deleting}
              >
                إلغاء
              </Button>
              <Button
                onClick={handleDeleteRestaurant}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'جاري الحذف...' : (deleteType === 'permanent' ? 'حذف نهائي' : 'إخفاء مؤقت')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Restaurant Modal */}
      {editingRestaurant && (
        <Dialog open={true} onOpenChange={() => setEditingRestaurant(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ direction: 'rtl' }}>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-amber-900">
                تعديل مطعم: {editingRestaurant.name_ar}
              </DialogTitle>
            </DialogHeader>
            <EditRestaurantForm 
              restaurant={editingRestaurant}
              onSave={(updatedRestaurant) => {
                setRestaurants(restaurants.map(r => 
                  r.id === updatedRestaurant.id ? updatedRestaurant : r
                ));
                setEditingRestaurant(null);
                toast.success('تم تحديث المطعم بنجاح');
              }}
              onCancel={() => setEditingRestaurant(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// User Menu Component
const UserMenu = ({ currentView, setCurrentView, onNavigate }) => {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu && !event.target.closest('.user-menu-container')) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // دالة للتنقل مع إغلاق القائمة وتصفير المطعم
  const handleNavigate = (view) => {
    setShowMenu(false);
    if (onNavigate) {
      onNavigate(view);
    } else {
      setCurrentView(view);
    }
  };

  // تحديد لون المستوى
  const getLevelColor = (level) => {
    const levelColors = {
      'مبتدئ': 'bg-gray-500',
      'متوسط': 'bg-blue-500',
      'نشط': 'bg-blue-500',
      'محترف': 'bg-purple-500',
      'خبير': 'bg-amber-500',
      'أسطورة': 'bg-gradient-to-r from-amber-500 to-orange-600'
    };
    return levelColors[level] || 'bg-gray-500';
  };

  // تحديد أيقونة المستوى
  const getLevelIcon = (level) => {
    if (level === 'أسطورة') return '👑';
    if (level === 'خبير') return '⭐';
    if (level === 'محترف') return '💎';
    if (level === 'متوسط' || level === 'نشط') return '🔥';
    return '🌟';
  };

  return (
    <div className="relative user-menu-container">
      <Button
        variant="ghost"
        size="lg"
        onClick={() => setShowMenu(!showMenu)}
        className="rounded-full p-4 bg-orange-100 hover:bg-orange-200 transition-colors border-3 border-orange-300 hover:border-orange-400 shadow-md hover:shadow-lg w-14 h-14 flex items-center justify-center"
      >
        <User className="h-8 w-8 text-orange-700" />
      </Button>

      {showMenu && (
        <div 
          className="fixed top-16 right-2 left-2 sm:absolute sm:left-0 sm:top-full sm:right-auto sm:mt-2 w-auto sm:w-72 bg-white rounded-lg shadow-2xl border-2 border-gray-100 z-[9999] max-h-[calc(100vh-80px)] overflow-y-auto" 
          style={{ direction: 'rtl' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-3 border-b bg-gradient-to-br from-amber-50 to-orange-50 rounded-t-lg">
            <p className="font-medium text-gray-900 text-base">{user.name}</p>
            <p className="text-xs text-gray-500 mt-1 truncate">{user.email || user.phone}</p>
            
            {/* معلومات المستوى والنقاط */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {user?.level && (
                <Badge className={`${getLevelColor(user.level)} text-white text-xs font-bold`}>
                  {getLevelIcon(user.level)} {user.level}
                </Badge>
              )}
              {user?.points !== undefined && (
                <Badge className="bg-amber-100 text-amber-800 text-xs font-medium">
                  <Trophy className="h-3 w-3 inline ml-1" />
                  {user.points} نقطة
                </Badge>
              )}
              {user.role === 'admin' && (
                <Badge className="bg-orange-100 text-orange-800 text-xs">مدير</Badge>
              )}
            </div>
          </div>
          
          <div className="py-2">
            {/* 1. حسابي */}
            <button
              onClick={() => handleNavigate('profile')}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-orange-50 hover:text-orange-700 flex items-center gap-3 transition-colors text-sm"
            >
              <User className="h-4 w-4" />
              <span className="font-medium">حسابي</span>
            </button>
            
            {/* 2. تغيير كلمة المرور */}
            <button
              onClick={() => handleNavigate('change-password')}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-orange-50 hover:text-orange-700 flex items-center gap-3 transition-colors text-sm"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">تغيير كلمة المرور</span>
            </button>

            {/* 3. إعدادات الموقع */}
            <button
              onClick={() => handleNavigate('location-settings')}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-amber-50 hover:text-amber-700 flex items-center gap-3 transition-colors text-sm"
            >
              <Navigation className="h-4 w-4" />
              <span className="font-medium">إعدادات الموقع</span>
            </button>

            {/* 4. تقييماتي */}
            <button
              onClick={() => handleNavigate('my-ratings')}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-3 transition-colors text-sm"
            >
              <Star className="h-4 w-4" />
              <span className="font-medium">تقييماتي</span>
            </button>

            {/* 5. اقتراح مطعم (للجميع) أو إضافة مطعم (للمدراء) */}
            {user.role === 'admin' ? (
              <>
                <button
                  onClick={() => handleNavigate('add-restaurant')}
                  className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-3 transition-colors text-sm"
                >
                  <Plus className="h-4 w-4" />
                  <span className="font-medium">إضافة مطعم</span>
                </button>
                <button
                  onClick={() => handleNavigate('add-post')}
                  className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-orange-50 hover:text-orange-700 flex items-center gap-3 transition-colors text-sm"
                >
                  <span className="h-4 w-4">📰</span>
                  <span className="font-medium">نشر خبر جديد</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => handleNavigate('suggest-restaurant')}
                className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center gap-3 transition-colors text-sm"
              >
                <MessageCircle className="h-4 w-4" />
                <span className="font-medium">اقتراح مطعم</span>
              </button>
            )}

            {/* إدارة (للمدراء فقط) */}
            {user.is_admin && (
              <button
                onClick={() => handleNavigate('admin')}
                className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-3 transition-colors text-sm"
              >
                <BarChart3 className="h-4 w-4" />
                <span className="font-medium">الإدارة</span>
              </button>
            )}

            {/* اتصل بنا */}
            <button
              onClick={() => handleNavigate('contact-us')}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-3 transition-colors text-sm"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="font-medium">اتصل بنا</span>
            </button>

            {/* 5. معلومات عن التطبيق */}
            <button
              onClick={() => handleNavigate('about')}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-gray-50 hover:text-gray-900 flex items-center gap-3 transition-colors text-sm"
            >
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium">معلومات عن التطبيق</span>
            </button>

            <div className="border-t my-2 mx-3"></div>

            {/* 6. تسجيل الخروج */}
            <button
              onClick={() => {
                logout();
                setShowMenu(false);
              }}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center gap-3 transition-colors text-sm"
            >
              <LogOut className="h-4 w-4" />
              <span className="font-medium">تسجيل الخروج</span>
            </button>

            {/* 7. سياسة الخصوصية */}
            <button
              onClick={() => handleNavigate('privacy-policy')}
              className="w-full px-4 py-2.5 text-right text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors text-sm"
            >
              <Lock className="h-4 w-4" />
              <span className="font-medium">سياسة الخصوصية</span>
            </button>

            {/* 8. إلغاء الحساب */}
            <button
              onClick={() => handleNavigate('delete-account')}
              className="w-full px-4 py-2.5 text-right text-red-600 hover:bg-red-50 hover:text-red-700 flex items-center gap-3 transition-colors text-sm"
            >
              <X className="h-4 w-4" />
              <span className="font-medium">إلغاء الحساب</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Header Component
const Header = ({ currentView, setCurrentView, onClearSearch, onNavigate }) => {
  const { user } = useAuth();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between" style={{ direction: 'rtl' }}>
          {/* Logo - على اليمين */}
          <div className="flex flex-col items-center">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => {
                if (onNavigate) onNavigate('home');
                else {
                  setCurrentView('home');
                  if (onClearSearch) onClearSearch();
                }
              }}
            >
              <Utensils className="h-6 w-6 lg:h-8 lg:w-8 text-amber-600" />
              <h1 className="text-lg lg:text-2xl font-bold text-amber-900">دليل المطاعم</h1>
            </div>
            <p className="text-sm text-amber-700 mt-1">طعم الوطن بعيداً عن الوطن</p>
          </div>

          {/* User Menu - على اليسار */}
          {user && (
            <UserMenu currentView={currentView} setCurrentView={setCurrentView} onNavigate={onNavigate} />
          )}
        </div>

        {/* Navigation Tabs */}
        {user && (
          <div className="mt-4 overflow-x-auto">
            <div className="flex gap-2 justify-center" style={{ direction: 'rtl' }}>
              <Button
                variant={currentView === 'home' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  if (onNavigate) onNavigate('home');
                  else {
                    setCurrentView('home');
                    if (onClearSearch) onClearSearch();
                  }
                }}
                className="whitespace-nowrap px-2 py-1 text-xs"
              >
                <Utensils className="h-3 w-3 ml-1" />
                المطاعم
              </Button>
              
              <Button
                variant={currentView === 'news' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  if (onNavigate) onNavigate('news');
                  else {
                    setCurrentView('news');
                    if (onClearSearch) onClearSearch();
                  }
                }}
                className="whitespace-nowrap px-2 py-1 text-xs"
              >
                <Newspaper className="h-3 w-3 ml-1" />
                الأخبار
              </Button>
              
              <Button
                variant={currentView === 'favorites' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  if (onNavigate) onNavigate('favorites');
                  else {
                    setCurrentView('favorites');
                    if (onClearSearch) onClearSearch();
                  }
                }}
                className="whitespace-nowrap px-2 py-1 text-xs"
              >
                <Heart className="h-3 w-3 ml-1" />
                المفضلة
              </Button>

              <Button
                variant={currentView === 'want-to-visit' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => {
                  if (onNavigate) onNavigate('want-to-visit');
                  else {
                    setCurrentView('want-to-visit');
                    if (onClearSearch) onClearSearch();
                  }
                }}
                className="whitespace-nowrap px-2 py-1 text-xs"
              >
                <List className="h-3 w-3 ml-1" />
                أريد زيارتها
              </Button>

            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Share Component - Simple Modal Implementation
const ShareRestaurant = ({ restaurant }) => {
  const [showShareDialog, setShowShareDialog] = useState(false);
  
  // رابط التطبيق - Deep Link مع fallback لمتجر التطبيقات
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.dalilfood.app';
  const appStoreUrl = 'https://apps.apple.com/app/dalilfood'; // غيّر هذا عند نشر iOS
  
  // Deep link للتطبيق
  const appDeepLink = `dalilfood://restaurant/${restaurant.id}`;
  
  // رابط ذكي يفتح التطبيق أو المتجر
  const shareUrl = playStoreUrl; // مؤقتاً نستخدم رابط المتجر
  const shareText = `🍽️ ${restaurant.name_ar} - ${restaurant.city}\n⭐ مطعم عربي مميز\n📲 حمّل تطبيق دليل المطاعم لاكتشاف المزيد:\n`;
  const fullShareText = `${shareText}${shareUrl}`;

  const handleShare = (platform) => {
    let url = '';
    switch (platform) {
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(fullShareText)}`;
        break;
      case 'sms':
        url = `sms:?body=${encodeURIComponent(fullShareText)}`;
        break;
      case 'instagram':
        navigator.clipboard.writeText(`${shareText}${shareUrl}\n#مطاعم #طعام_عربي #dalilfood`);
        toast.success('تم نسخ النص للمشاركة على Instagram');
        return;
      default:
        return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('تم نسخ الرابط');
  };

  return (
    <>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setShowShareDialog(true)}
      >
        <Share2 className="h-4 w-4 mr-2" />
        مشاركة
      </Button>

      {showShareDialog && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
          style={{ zIndex: 9999 }}
          onClick={() => setShowShareDialog(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            style={{ direction: 'rtl' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-900">مشاركة {restaurant.name_ar}</h2>
              <p className="text-sm text-gray-600 mt-1">شارك هذا المطعم مع أصدقائك</p>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg flex flex-col items-center justify-center transition-colors gap-1"
                  title="WhatsApp"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span className="text-xs">واتساب</span>
                </button>
                <button
                  onClick={() => handleShare('instagram')}
                  className="bg-gradient-to-br from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white p-3 rounded-lg flex flex-col items-center justify-center transition-colors gap-1"
                  title="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                  <span className="text-xs">انستغرام</span>
                </button>
                <button
                  onClick={() => handleShare('sms')}
                  className="bg-gray-600 hover:bg-gray-700 text-white p-3 rounded-lg flex flex-col items-center justify-center transition-colors gap-1"
                  title="رسالة نصية"
                >
                  <MessageSquare className="h-5 w-5" />
                  <span className="text-xs">رسالة</span>
                </button>
                <button
                  onClick={copyToClipboard}
                  className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg flex flex-col items-center justify-center transition-colors gap-1"
                  title="نسخ الرابط"
                >
                  <Copy className="h-5 w-5" />
                  <span className="text-xs">نسخ</span>
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 p-2 border rounded-lg bg-gray-50 text-sm text-left"
                  dir="ltr"
                />
                />
                <Button onClick={copyToClipboard} size="sm" className="bg-orange-600 hover:bg-orange-700 text-white">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowShareDialog(false)}
              >
                إغلاق
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Image Upload Component
const ImageUpload = ({ images, setImages }) => {
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newImages = [];

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error(`حجم الصورة ${file.name} كبير جداً (الحد الأقصى 5MB)`);
        continue;
      }

      try {
        const base64 = await convertToBase64(file);
        newImages.push(base64);
      } catch (error) {
        toast.error(`خطأ في رفع ${file.name}`);
      }
    }

    setImages([...images, ...newImages]);
    setUploading(false);
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleAddUrl = () => {
    if (!urlInput.trim()) {
      toast.error('يرجى إدخال رابط الصورة');
      return;
    }
    
    // التحقق من صحة الرابط
    const urlPattern = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;
    const isValidImageUrl = urlPattern.test(urlInput) || 
                           urlInput.includes('unsplash.com') || 
                           urlInput.includes('cloudinary.com') ||
                           urlInput.includes('imgbb.com') ||
                           urlInput.includes('i.ibb.co') ||
                           urlInput.includes('imgur.com');
    
    if (!isValidImageUrl) {
      toast.error('يرجى إدخال رابط صورة صحيح (jpg, png, gif, webp)');
      return;
    }
    
    setImages([...images, urlInput.trim()]);
    setUrlInput('');
    setShowUrlInput(false);
    toast.success('تم إضافة الصورة بنجاح');
  };

  const removeImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
  };

  // Drag and Drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    
    // Remove from old position
    newImages.splice(draggedIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);
    
    setImages(newImages);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Label htmlFor="images" className="cursor-pointer">
          <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-500 transition-colors">
            <Upload className="h-5 w-5 text-gray-500" />
            <span>رفع صورة</span>
          </div>
        </Label>
        <Input
          id="images"
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
        
        <button
          type="button"
          onClick={() => setShowUrlInput(!showUrlInput)}
          className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 transition-colors text-blue-600 hover:text-blue-700"
        >
          <Link className="h-5 w-5" />
          <span>إضافة رابط URL</span>
        </button>
        
        {uploading && <span className="text-sm text-gray-500">جاري الرفع...</span>}
      </div>
      
      {showUrlInput && (
        <div className="flex gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <Input
            type="url"
            placeholder="https://example.com/image.jpg"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            className="flex-1 text-left"
            dir="ltr"
          />
          <Button type="button" onClick={handleAddUrl} className="bg-blue-500 hover:bg-blue-600">
            إضافة
          </Button>
          <Button type="button" variant="ghost" onClick={() => { setShowUrlInput(false); setUrlInput(''); }}>
            إلغاء
          </Button>
        </div>
      )}
      
      <p className="text-xs text-gray-500">
        💡 يُفضل استخدام روابط الصور (URL) بدلاً من رفعها لتحميل أسرع. يمكنك رفع صورك على 
        <a href="https://imgbb.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline mx-1">ImgBB</a>
        أو
        <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline mx-1">Cloudinary</a>
        ثم نسخ الرابط هنا.
      </p>

      {images.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600">💡 اسحب الصورة لتغيير ترتيبها ({images.length} صور)</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((image, index) => (
              <div 
                key={index} 
                className={`relative cursor-move ${draggedIndex === index ? 'opacity-50' : ''}`}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                onDragEnd={handleDragEnd}
              >
                <div className="relative group">
                  <img 
                    src={image} 
                    alt={`صورة ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border border-gray-300 group-hover:border-amber-500 transition-colors"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/300x200?text=صورة'; }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-2xl opacity-0 group-hover:opacity-100 transition-opacity">
                      {index + 1}
                    </span>
                  </div>
                  {/* عرض نوع الصورة */}
                  <span className={`absolute bottom-2 left-2 text-xs px-2 py-1 rounded ${image.startsWith('data:') ? 'bg-orange-500 text-white' : 'bg-green-500 text-white'}`}>
                    {image.startsWith('data:') ? 'ملف' : 'URL'}
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => removeImage(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Add Restaurant Form (Available for all verified users)
const AddRestaurantForm = () => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    street_number: '',
    postal_code: '',
    city: '',
    country: 'Deutschland',
    google_maps_link: '',
    website: '',
    email: '',
    facebook: '',
    instagram: '',
    tiktok: '',
    cuisine_type: '',
    cuisine_type_ar: '',
    phone: '',
    opening_hours: {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: ''
    },
    menu_items: '',
    services_note: '', // ملاحظة إضافية للخدمات
    // Coupon fields
    has_coupon: false,
    coupon_title: '',
    coupon_description: '',
    coupon_discount: '',
    coupon_code: '',
    coupon_expiry: '',
    coupon_terms: ''
  });
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);

  // Restaurant Services/Amenities
  const restaurantServices = [
    { id: 'parking', name: 'مواقف سيارات', icon: '🚗', description: 'يوجد مواقف للسيارات' },
    { id: 'takeaway', name: 'أخذ الطعام', icon: '🥡', description: 'يمكن طلب الطعام للخارج' },
    { id: 'dine_in', name: 'تناول في المكان', icon: '🪑', description: 'يمكن تناول الطعام في المطعم' },
    { id: 'restroom', name: 'دورات مياه', icon: '🚻', description: 'يوجد دورات مياه' },
    { id: 'kids_menu', name: 'وجبات أطفال', icon: '👶', description: 'يوجد وجبات خاصة للأطفال' },
    { id: 'wifi', name: 'واي فاي', icon: '📶', description: 'يوجد انترنت مجاني' },
    { id: 'outdoor_seating', name: 'جلسة خارجية', icon: '🌳', description: 'يوجد جلسات في الهواء الطلق' },
    { id: 'delivery', name: 'توصيل للمنزل', icon: '🛵', description: 'يوجد خدمة توصيل' },
    { id: 'card_payment', name: 'دفع بالبطاقة', icon: '💳', description: 'يقبل الدفع بالبطاقات' },
    { id: 'prayer_room', name: 'مكان للصلاة', icon: '🕌', description: 'يوجد مكان مخصص للصلاة' },
    { id: 'kids_playground', name: 'ألعاب أطفال', icon: '🎠', description: 'يوجد منطقة ألعاب للأطفال' }
  ];

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build full address from components
      const addressParts = [
        formData.street_number,
        formData.postal_code,
        formData.city,
        formData.country
      ].filter(part => part && part.trim());
      
      const fullAddress = addressParts.join(', ');
      
      const restaurantData = {
        ...formData,
        address: fullAddress, // Combined address
        menu_items: formData.menu_items.split(',').map(item => item.trim()),
        images: images,
        // Don't send latitude/longitude - let backend geocode the address
        services: selectedServices,
        service_icons: selectedServices.map(serviceId => {
          const service = restaurantServices.find(s => s.id === serviceId);
          return { id: serviceId, name: service?.name, icon: service?.icon };
        })
      };
      
      // Remove latitude and longitude from data sent to backend
      delete restaurantData.latitude;
      delete restaurantData.longitude;

      await axios.post(`${API}/restaurants`, restaurantData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم إضافة المطعم بنجاح');
      // Reset form
      setFormData({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        street_number: '',
        postal_code: '',
        city: '',
        country: 'Deutschland',
        google_maps_link: '',
        website: '',
        email: '',
        cuisine_type: '',
        cuisine_type_ar: '',
        phone: '',
        opening_hours: {
          monday: '',
          tuesday: '',
          wednesday: '',
          thursday: '',
          friday: '',
          saturday: '',
          sunday: ''
        },
        menu_items: '',
        // Reset coupon fields
        has_coupon: false,
        coupon_title: '',
        coupon_description: '',
        coupon_discount: '',
        coupon_code: '',
        coupon_expiry: '',
        coupon_terms: ''
      });
      setImages([]);
      setSelectedServices([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إضافة المطعم');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" style={{ direction: 'rtl' }}>
      <Card>
        <CardHeader>
          <CardTitle>إضافة مطعم جديد</CardTitle>
          <CardDescription>
            املأ المعلومات التالية لإضافة مطعم جديد للدليل
            <br />
            <span className="text-blue-600 font-medium">📝 جميع الحقول اختيارية عدا اسم المطعم العربي</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم المطعم (عربي) *</Label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>اسم المطعم (إنجليزي)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>الوصف (عربي)</Label>
                <Textarea
                  value={formData.description_ar}
                  onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>الوصف (إنجليزي)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <Label className="text-lg font-semibold">العنوان (Address)</Label>
              <p className="text-sm text-gray-600">يرجى كتابة العنوان بالأحرف اللاتينية (Latin characters)</p>
              
              <div className="space-y-2">
                <Label>اسم الشارع والرقم (Street and Number)</Label>
                <Input
                  value={formData.street_number}
                  onChange={(e) => setFormData({ ...formData, street_number: e.target.value })}
                  placeholder="e.g., Friedrichstraße 123"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>الرمز البريدي (Postal Code)</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="e.g., 10115"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>المدينة (City)</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Berlin"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>البلد (Country)</Label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                >
                  <option value="Deutschland">Deutschland (Germany)</option>
                  <option value="Austria">Austria</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Bulgaria">Bulgaria</option>
                  <option value="Croatia">Croatia</option>
                  <option value="Cyprus">Cyprus</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Denmark">Denmark</option>
                  <option value="Estonia">Estonia</option>
                  <option value="Finland">Finland</option>
                  <option value="France">France</option>
                  <option value="Greece">Greece</option>
                  <option value="Hungary">Hungary</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Italy">Italy</option>
                  <option value="Latvia">Latvia</option>
                  <option value="Lithuania">Lithuania</option>
                  <option value="Luxembourg">Luxembourg</option>
                  <option value="Malta">Malta</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Poland">Poland</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Romania">Romania</option>
                  <option value="Slovakia">Slovakia</option>
                  <option value="Slovenia">Slovenia</option>
                  <option value="Spain">Spain</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Norway">Norway</option>
                  <option value="Other">أخرى (Other)</option>
                </select>
                {formData.country === 'Other' && (
                  <Input
                    value={formData.custom_country || ''}
                    onChange={(e) => setFormData({ ...formData, custom_country: e.target.value, country: e.target.value || 'Other' })}
                    placeholder="اكتب اسم البلد (Enter country name)"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                    className="mt-2"
                  />
                )}
              </div>
            </div>

            {/* Google Maps Link */}
            <div className="space-y-2">
              <Label>رابط المطعم من Google Maps (اختياري)</Label>
              <Input
                value={formData.google_maps_link}
                onChange={(e) => setFormData({ ...formData, google_maps_link: e.target.value })}
                placeholder="https://maps.app.goo.gl/..."
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
              <p className="text-sm text-gray-500">
                سيتم استخدام هذا الرابط عند الضغط على "فتح في خرائط جوجل". إذا تُرك فارغاً، سيتم استخدام العنوان.
              </p>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label>الهاتف (Phone)</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 30 1234567"
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>الموقع الإلكتروني</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>البريد الإلكتروني</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="info@restaurant.com"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
            </div>

            {/* حقول السوشيال ميديا */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">روابط التواصل الاجتماعي (اختياري)</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    فيسبوك
                  </Label>
                  <Input
                    value={formData.facebook}
                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    placeholder="https://facebook.com/..."
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Instagram className="h-4 w-4 text-pink-600" />
                    إنستغرام
                  </Label>
                  <Input
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    placeholder="https://instagram.com/..."
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.04-.1z"/>
                    </svg>
                    تيك توك
                  </Label>
                  <Input
                    value={formData.tiktok}
                    onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
                    placeholder="https://tiktok.com/@..."
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>نوع المطبخ (عربي)</Label>
                <Input
                  value={formData.cuisine_type_ar}
                  onChange={(e) => setFormData({ ...formData, cuisine_type_ar: e.target.value })}
                  placeholder="مثل: شامي، لبناني، عراقي"
                />
              </div>
              <div className="space-y-2">
                <Label>نوع المطبخ (إنجليزي)</Label>
                <Input
                  value={formData.cuisine_type}
                  onChange={(e) => setFormData({ ...formData, cuisine_type: e.target.value })}
                  placeholder="مثل: Syrian, Lebanese, Iraqi"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>قائمة الطعام (مفصولة بفواصل)</Label>
              <Textarea
                value={formData.menu_items}
                onChange={(e) => setFormData({ ...formData, menu_items: e.target.value })}
                placeholder="مثل: حمص، فلافل، كبة، شاورما"
              />
            </div>

            <div className="space-y-2">
              <Label>صور المطعم</Label>
              <ImageUpload images={images} setImages={setImages} />
            </div>

            <div className="space-y-4">
              <Label>ساعات العمل</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.keys(formData.opening_hours).map(day => (
                  <div key={day} className="space-y-2">
                    <Label>{
                      day === 'monday' ? 'الاثنين' :
                      day === 'tuesday' ? 'الثلاثاء' :
                      day === 'wednesday' ? 'الأربعاء' :
                      day === 'thursday' ? 'الخميس' :
                      day === 'friday' ? 'الجمعة' :
                      day === 'saturday' ? 'السبت' : 'الأحد'
                    }</Label>
                    <Input
                      value={formData.opening_hours[day]}
                      onChange={(e) => setFormData({
                        ...formData,
                        opening_hours: {
                          ...formData.opening_hours,
                          [day]: e.target.value
                        }
                      })}
                      placeholder="مثل: 09:00-22:00 أو closed"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Services/Amenities Section */}
            <Card className="border-2 border-dashed border-blue-300 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Star className="h-5 w-5" />
                  الخدمات المتوفرة
                </CardTitle>
                <CardDescription className="text-blue-600">
                  اختر الخدمات والمرافق المتوفرة في المطعم
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {restaurantServices.map((service) => (
                    <div 
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                        selectedServices.includes(service.id)
                          ? 'border-blue-500 bg-blue-100 shadow-md scale-105'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="text-2xl mb-2">{service.icon}</div>
                      <div className="text-sm font-medium text-gray-800">{service.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{service.description}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  تم اختيار {selectedServices.length} من {restaurantServices.length} خدمات
                </div>
                
                {/* حقل نص إضافي للخدمات */}
                <div className="mt-4 space-y-2">
                  <Label htmlFor="services_note" className="flex items-center gap-2">
                    <span>📝</span>
                    <span>ملاحظة إضافية للخدمات (اختياري)</span>
                  </Label>
                  <Textarea
                    id="services_note"
                    value={formData.services_note}
                    onChange={(e) => setFormData({ ...formData, services_note: e.target.value })}
                    placeholder="أضف أي معلومات إضافية عن الخدمات هنا... مثلاً: يوجد واي فاي مجاني، مواقف سيارات واسعة، إلخ"
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500">
                    💡 هذا النص سيظهر أسفل الخدمات في صفحة تفاصيل المطعم
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Coupon Section */}
            <Card className="border-2 border-dashed border-orange-300 bg-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-800">
                  <Ticket className="h-5 w-5" />
                  كوبون الخصم
                </CardTitle>
                <CardDescription className="text-orange-600">
                  إضافة كوبون خصم للمطعم لجذب المزيد من العملاء
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="has_coupon"
                    checked={formData.has_coupon}
                    onChange={(e) => setFormData({ ...formData, has_coupon: e.target.checked })}
                    className="ml-2"
                  />
                  <Label htmlFor="has_coupon" className="text-sm font-medium">
                    يحتوي هذا المطعم على كوبون خصم
                  </Label>
                </div>

                {formData.has_coupon && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>عنوان الكوبون</Label>
                        <Input
                          value={formData.coupon_title}
                          onChange={(e) => setFormData({ ...formData, coupon_title: e.target.value })}
                          placeholder="مثل: خصم خاص للعملاء الجدد"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>قيمة الخصم</Label>
                        <Input
                          value={formData.coupon_discount}
                          onChange={(e) => setFormData({ ...formData, coupon_discount: e.target.value })}
                          placeholder="مثل: 20% أو 10 يورو"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>وصف الكوبون</Label>
                      <Textarea
                        value={formData.coupon_description}
                        onChange={(e) => setFormData({ ...formData, coupon_description: e.target.value })}
                        placeholder="وصف العرض المقدم..."
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>كود الكوبون (اختياري)</Label>
                        <Input
                          value={formData.coupon_code}
                          onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value })}
                          placeholder="مثل: SAVE20"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>تاريخ الانتهاء</Label>
                        <Input
                          type="date"
                          value={formData.coupon_expiry}
                          onChange={(e) => setFormData({ ...formData, coupon_expiry: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>شروط الكوبون</Label>
                      <Textarea
                        value={formData.coupon_terms}
                        onChange={(e) => setFormData({ ...formData, coupon_terms: e.target.value })}
                        placeholder="الشروط والأحكام للكوبون..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'جاري الإضافة...' : 'إضافة المطعم'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Edit Restaurant Form Component  
const EditRestaurantForm = ({ restaurant, onSuccess, onCancel }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    name: restaurant.name || '',
    name_ar: restaurant.name_ar || '',
    description: restaurant.description || '',
    description_ar: restaurant.description_ar || '',
    street_number: restaurant.street_number || '',
    postal_code: restaurant.postal_code || '',
    city: restaurant.city || '',
    country: restaurant.country || 'Deutschland',
    google_maps_link: restaurant.google_maps_link || '',
    website: restaurant.website || '',
    email: restaurant.email || '',
    facebook: restaurant.facebook || '',
    instagram: restaurant.instagram || '',
    tiktok: restaurant.tiktok || '',
    cuisine_type: restaurant.cuisine_type || '',
    cuisine_type_ar: restaurant.cuisine_type_ar || '',
    phone: restaurant.phone || '',
    opening_hours: restaurant.opening_hours || {
      monday: '',
      tuesday: '',
      wednesday: '',
      thursday: '',
      friday: '',
      saturday: '',
      sunday: ''
    },
    menu_items: Array.isArray(restaurant.menu_items) ? restaurant.menu_items.join(', ') : '',
    services_note: restaurant.services_note || '', // ملاحظة إضافية للخدمات
    // Coupon fields
    has_coupon: restaurant.has_coupon || false,
    coupon_title: restaurant.coupon?.title || '',
    coupon_description: restaurant.coupon?.description || '',
    coupon_discount: restaurant.coupon?.discount || '',
    coupon_code: restaurant.coupon?.code || '',
    coupon_expiry: restaurant.coupon?.expiry_date || '',
    coupon_terms: restaurant.coupon?.terms || ''
  });
  const [images, setImages] = useState(restaurant.images || []);
  const [loading, setLoading] = useState(false);
  const [selectedServices, setSelectedServices] = useState(
    restaurant.services || restaurant.service_icons?.map(s => s.id) || []
  );

  // Restaurant Services/Amenities
  const restaurantServices = [
    { id: 'parking', name: 'مواقف سيارات', icon: '🚗', description: 'يوجد مواقف للسيارات' },
    { id: 'takeaway', name: 'أخذ الطعام', icon: '🥡', description: 'يمكن طلب الطعام للخارج' },
    { id: 'dine_in', name: 'تناول في المكان', icon: '🪑', description: 'يمكن تناول الطعام في المطعم' },
    { id: 'restroom', name: 'دورات مياه', icon: '🚻', description: 'يوجد دورات مياه' },
    { id: 'kids_menu', name: 'وجبات أطفال', icon: '👶', description: 'يوجد وجبات خاصة للأطفال' },
    { id: 'wifi', name: 'واي فاي', icon: '📶', description: 'يوجد انترنت مجاني' },
    { id: 'outdoor_seating', name: 'جلسة خارجية', icon: '🌳', description: 'يوجد جلسات في الهواء الطلق' },
    { id: 'delivery', name: 'توصيل للمنزل', icon: '🛵', description: 'يوجد خدمة توصيل' },
    { id: 'card_payment', name: 'دفع بالبطاقة', icon: '💳', description: 'يقبل الدفع بالبطاقات' },
    { id: 'prayer_room', name: 'مكان للصلاة', icon: '🕌', description: 'يوجد مكان مخصص للصلاة' },
    { id: 'kids_playground', name: 'ألعاب أطفال', icon: '🎠', description: 'يوجد منطقة ألعاب للأطفال' }
  ];

  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build full address from components
      const addressParts = [
        formData.street_number,
        formData.postal_code,
        formData.city,
        formData.country
      ].filter(part => part && part.trim());
      
      const fullAddress = addressParts.join(', ');
      
      const restaurantData = {
        ...formData,
        address: fullAddress, // Combined address
        menu_items: formData.menu_items.split(',').map(item => item.trim()).filter(item => item),
        images: images,
        // Don't send latitude/longitude - let backend geocode the address
        services: selectedServices,
        service_icons: selectedServices.map(serviceId => {
          const service = restaurantServices.find(s => s.id === serviceId);
          return { id: serviceId, name: service?.name, icon: service?.icon };
        })
      };
      
      // Remove latitude and longitude from data sent to backend
      delete restaurantData.latitude;
      delete restaurantData.longitude;

      await axios.put(`${API}/restaurants/${restaurant.id}`, restaurantData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم تحديث المطعم بنجاح');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تحديث المطعم');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" style={{ direction: 'rtl' }}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            ✏️ تعديل المطعم: {restaurant.name_ar}
          </CardTitle>
          <CardDescription>
            قم بتعديل معلومات المطعم في الدليل
            <br />
            <span className="text-blue-600 font-medium">📝 جميع الحقول اختيارية عدا اسم المطعم العربي</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>اسم المطعم (عربي) *</Label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>اسم المطعم (إنجليزي)</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>الوصف (عربي)</Label>
                <Textarea
                  value={formData.description_ar}
                  onChange={(e) => setFormData({ ...formData, description_ar: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>الوصف (إنجليزي)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            {/* Address Section */}
            <div className="space-y-4 p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
              <Label className="text-lg font-semibold">العنوان (Address)</Label>
              <p className="text-sm text-gray-600">يرجى كتابة العنوان بالأحرف اللاتينية (Latin characters)</p>
              
              <div className="space-y-2">
                <Label>اسم الشارع والرقم (Street and Number)</Label>
                <Input
                  value={formData.street_number}
                  onChange={(e) => setFormData({ ...formData, street_number: e.target.value })}
                  placeholder="e.g., Friedrichstraße 123"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>الرمز البريدي (Postal Code)</Label>
                  <Input
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="e.g., 10115"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>المدينة (City)</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Berlin"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>البلد (Country)</Label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                >
                  <option value="Deutschland">Deutschland (Germany)</option>
                  <option value="Austria">Austria</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Bulgaria">Bulgaria</option>
                  <option value="Croatia">Croatia</option>
                  <option value="Cyprus">Cyprus</option>
                  <option value="Czech Republic">Czech Republic</option>
                  <option value="Denmark">Denmark</option>
                  <option value="Estonia">Estonia</option>
                  <option value="Finland">Finland</option>
                  <option value="France">France</option>
                  <option value="Greece">Greece</option>
                  <option value="Hungary">Hungary</option>
                  <option value="Ireland">Ireland</option>
                  <option value="Italy">Italy</option>
                  <option value="Latvia">Latvia</option>
                  <option value="Lithuania">Lithuania</option>
                  <option value="Luxembourg">Luxembourg</option>
                  <option value="Malta">Malta</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Poland">Poland</option>
                  <option value="Portugal">Portugal</option>
                  <option value="Romania">Romania</option>
                  <option value="Slovakia">Slovakia</option>
                  <option value="Slovenia">Slovenia</option>
                  <option value="Spain">Spain</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Norway">Norway</option>
                  <option value="Other">أخرى (Other)</option>
                </select>
                {formData.country === 'Other' && (
                  <Input
                    value={formData.custom_country || ''}
                    onChange={(e) => setFormData({ ...formData, custom_country: e.target.value, country: e.target.value || 'Other' })}
                    placeholder="اكتب اسم البلد (Enter country name)"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                    className="mt-2"
                  />
                )}
              </div>
            </div>

            {/* Google Maps Link */}
            <div className="space-y-2">
              <Label>رابط المطعم من Google Maps (اختياري)</Label>
              <Input
                value={formData.google_maps_link}
                onChange={(e) => setFormData({ ...formData, google_maps_link: e.target.value })}
                placeholder="https://maps.app.goo.gl/..."
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
              <p className="text-sm text-gray-500">
                سيتم استخدام هذا الرابط عند الضغط على "فتح في خرائط جوجل". إذا تُرك فارغاً، سيتم استخدام العنوان.
              </p>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label>الهاتف (Phone)</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+49 30 1234567"
                dir="ltr"
                style={{ textAlign: 'left' }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>الموقع الإلكتروني</Label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>البريد الإلكتروني</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="info@restaurant.com"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
            </div>

            {/* حقول السوشيال ميديا */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">روابط التواصل الاجتماعي (اختياري)</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    فيسبوك
                  </Label>
                  <Input
                    value={formData.facebook}
                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    placeholder="https://facebook.com/..."
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Instagram className="h-4 w-4 text-pink-600" />
                    إنستغرام
                  </Label>
                  <Input
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    placeholder="https://instagram.com/..."
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.04-.1z"/>
                    </svg>
                    تيك توك
                  </Label>
                  <Input
                    value={formData.tiktok}
                    onChange={(e) => setFormData({ ...formData, tiktok: e.target.value })}
                    placeholder="https://tiktok.com/@..."
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>نوع المطبخ (عربي)</Label>
                <Input
                  value={formData.cuisine_type_ar}
                  onChange={(e) => setFormData({ ...formData, cuisine_type_ar: e.target.value })}
                  placeholder="مثل: شامي، لبناني، عراقي"
                />
              </div>
              <div className="space-y-2">
                <Label>نوع المطبخ (إنجليزي)</Label>
                <Input
                  value={formData.cuisine_type}
                  onChange={(e) => setFormData({ ...formData, cuisine_type: e.target.value })}
                  placeholder="مثل: Syrian, Lebanese, Iraqi"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>قائمة الطعام (مفصولة بفواصل)</Label>
              <Textarea
                value={formData.menu_items}
                onChange={(e) => setFormData({ ...formData, menu_items: e.target.value })}
                placeholder="مثل: حمص، فلافل، كبة، شاورما"
              />
            </div>

            <div className="space-y-2">
              <Label>صور المطعم</Label>
              <ImageUpload images={images} setImages={setImages} />
            </div>

            <div className="space-y-4">
              <Label>ساعات العمل</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.keys(formData.opening_hours).map(day => (
                  <div key={day} className="space-y-2">
                    <Label>{
                      day === 'monday' ? 'الاثنين' :
                      day === 'tuesday' ? 'الثلاثاء' :
                      day === 'wednesday' ? 'الأربعاء' :
                      day === 'thursday' ? 'الخميس' :
                      day === 'friday' ? 'الجمعة' :
                      day === 'saturday' ? 'السبت' : 'الأحد'
                    }</Label>
                    <Input
                      value={formData.opening_hours[day]}
                      onChange={(e) => setFormData({
                        ...formData,
                        opening_hours: {
                          ...formData.opening_hours,
                          [day]: e.target.value
                        }
                      })}
                      placeholder="مثل: 09:00-22:00 أو closed"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Services/Amenities Section */}
            <Card className="border-2 border-dashed border-blue-300 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Star className="h-5 w-5" />
                  الخدمات المتوفرة
                </CardTitle>
                <CardDescription className="text-blue-600">
                  اختر الخدمات والمرافق المتوفرة في المطعم
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {restaurantServices.map((service) => (
                    <div 
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                        selectedServices.includes(service.id)
                          ? 'border-blue-500 bg-blue-100 shadow-md scale-105'
                          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="text-2xl mb-2">{service.icon}</div>
                      <div className="text-sm font-medium text-gray-800">{service.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{service.description}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-sm text-gray-600">
                  تم اختيار {selectedServices.length} من {restaurantServices.length} خدمات
                </div>
                
                {/* حقل نص إضافي للخدمات */}
                <div className="mt-4 space-y-2">
                  <Label htmlFor="services_note" className="flex items-center gap-2">
                    <span>📝</span>
                    <span>ملاحظة إضافية للخدمات (اختياري)</span>
                  </Label>
                  <Textarea
                    id="services_note"
                    value={formData.services_note}
                    onChange={(e) => setFormData({ ...formData, services_note: e.target.value })}
                    placeholder="أضف أي معلومات إضافية عن الخدمات هنا... مثلاً: يوجد واي فاي مجاني، مواقف سيارات واسعة، إلخ"
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500">
                    💡 هذا النص سيظهر أسفل الخدمات في صفحة تفاصيل المطعم
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Coupon Section */}
            <Card className="border-2 border-dashed border-orange-300 bg-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-800">
                  <Ticket className="h-5 w-5" />
                  كوبون الخصم
                </CardTitle>
                <CardDescription className="text-orange-600">
                  تعديل كوبون الخصم للمطعم
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="has_coupon"
                    checked={formData.has_coupon}
                    onChange={(e) => setFormData({ ...formData, has_coupon: e.target.checked })}
                    className="ml-2"
                  />
                  <Label htmlFor="has_coupon" className="text-sm font-medium">
                    يحتوي هذا المطعم على كوبون خصم
                  </Label>
                </div>

                {formData.has_coupon && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>عنوان الكوبون</Label>
                        <Input
                          value={formData.coupon_title}
                          onChange={(e) => setFormData({ ...formData, coupon_title: e.target.value })}
                          placeholder="مثل: خصم خاص للعملاء الجدد"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>قيمة الخصم</Label>
                        <Input
                          value={formData.coupon_discount}
                          onChange={(e) => setFormData({ ...formData, coupon_discount: e.target.value })}
                          placeholder="مثل: 20% أو 10 يورو"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>وصف الكوبون</Label>
                      <Textarea
                        value={formData.coupon_description}
                        onChange={(e) => setFormData({ ...formData, coupon_description: e.target.value })}
                        placeholder="وصف العرض المقدم..."
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>كود الكوبون (اختياري)</Label>
                        <Input
                          value={formData.coupon_code}
                          onChange={(e) => setFormData({ ...formData, coupon_code: e.target.value })}
                          placeholder="مثل: SAVE20"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>تاريخ الانتهاء</Label>
                        <Input
                          type="date"
                          value={formData.coupon_expiry}
                          onChange={(e) => setFormData({ ...formData, coupon_expiry: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>شروط الكوبون</Label>
                      <Textarea
                        value={formData.coupon_terms}
                        onChange={(e) => setFormData({ ...formData, coupon_terms: e.target.value })}
                        placeholder="الشروط والأحكام للكوبون..."
                        rows={2}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? 'جاري التحديث...' : 'حفظ التعديلات'}
              </Button>
              
              <Button 
                type="button" 
                variant="outline" 
                onClick={onCancel}
                className="flex-1"
              >
                إلغاء
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
// Forgot Password Component - Simple Modal Implementation
const ForgotPasswordFlow = ({ onClose, onBackToLogin }) => {
  const [step, setStep] = useState('email'); // 'email', 'code', 'password'
  const [email, setEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoCode, setDemoCode] = useState('');
  const [isOpen, setIsOpen] = useState(true);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/forgot-password`, { email });
      // Check if demo code was returned (fallback mode)
      if (response.data.reset_code) {
        setDemoCode(response.data.reset_code);
      }
      setStep('code');
      toast.success('تم إرسال رمز إعادة التعيين إلى بريدك الإلكتروني');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إرسال رمز إعادة التعيين');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (resetCode.length !== 6) {
      toast.error('يجب أن يكون الرمز مكون من 6 أرقام');
      return;
    }
    setStep('password');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('كلمتا المرور غير متطابقتين');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/reset-password`, {
        email,
        reset_code: resetCode,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      toast.success('تم تغيير كلمة المرور بنجاح');
      onBackToLogin();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        style={{ direction: 'rtl' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-red-600" />
              <h2 className="text-xl font-bold text-gray-900">إعادة تعيين كلمة المرور</h2>
            </div>
            <button 
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600">
            {step === 'email' && 'أدخل بريدك الإلكتروني لإرسال رمز إعادة التعيين'}
            {step === 'code' && 'أدخل الرمز المرسل إلى بريدك الإلكتروني'}
            {step === 'password' && 'أدخل كلمة المرور الجديدة'}
          </p>
        </div>

      {step === 'email' && (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email">البريد الإلكتروني</Label>
            <Input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="text-base"
              placeholder="أدخل بريدك الإلكتروني"
              dir="ltr"
              style={{ textAlign: 'left' }}
            />
          </div>
          
          <Button type="submit" className="w-full py-3" disabled={loading}>
            {loading ? 'جاري الإرسال...' : 'إرسال رمز إعادة التعيين'}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-blue-600 hover:text-blue-700 text-sm hover:underline"
            >
              العودة لتسجيل الدخول
            </button>
          </div>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={handleCodeSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-code">رمز إعادة التعيين (6 أرقام)</Label>
            <Input
              id="reset-code"
              type="text"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              className="text-base text-center font-mono text-2xl tracking-widest"
              placeholder="123456"
              maxLength={6}
            />
            <p className="text-xs text-gray-500 text-center">
              تم إرسال الرمز إلى بريدك الإلكتروني
            </p>
          </div>

          {demoCode && null}
          
          <Button type="submit" className="w-full py-3" disabled={resetCode.length !== 6}>
            التالي
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setStep('email')}
              className="text-blue-600 hover:text-blue-700 text-sm hover:underline"
            >
              العودة للخطوة السابقة
            </button>
          </div>
        </form>
      )}

      {step === 'password' && (
        <form onSubmit={handlePasswordReset} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">كلمة المرور الجديدة</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="text-base"
              placeholder="كلمة المرور الجديدة"
              minLength={6}
              dir="ltr"
              style={{ textAlign: 'left' }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">تأكيد كلمة المرور</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="text-base"
              placeholder="أعد كتابة كلمة المرور"
              minLength={6}
              dir="ltr"
              style={{ textAlign: 'left' }}
            />
          </div>

          {newPassword && confirmPassword && newPassword !== confirmPassword && (
            <p className="text-red-600 text-sm">كلمتا المرور غير متطابقتين</p>
          )}
          
          <Button 
            type="submit" 
            className="w-full py-3" 
            disabled={loading || newPassword !== confirmPassword || newPassword.length < 6}
          >
            {loading ? 'جاري تغيير كلمة المرور...' : 'تغيير كلمة المرور'}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setStep('code')}
              className="text-blue-600 hover:text-blue-700 text-sm hover:underline"
            >
              العودة للخطوة السابقة
            </button>
          </div>
        </form>
      )}
      </div>
    </div>
  );
};

// Login Form Component with Register Link
const LoginFormWithRegister = ({ onClose, onSwitchToRegister }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/login`, { email, password });
      login(response.data.token, response.data.user);
      toast.success('تم تسجيل الدخول بنجاح');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في تسجيل الدخول');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md" style={{ direction: 'rtl' }}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <User className="h-5 w-5 text-blue-600" />
          تسجيل الدخول
        </DialogTitle>
        <DialogDescription>
          أدخل بيانات الدخول الخاصة بك
        </DialogDescription>
      </DialogHeader>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">البريد الإلكتروني</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="text-base"
            placeholder="أدخل بريدك الإلكتروني"
            dir="ltr"
            style={{ textAlign: 'left' }}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password">كلمة المرور</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="text-base"
            placeholder="أدخل كلمة المرور"
            dir="ltr"
            style={{ textAlign: 'left' }}
          />
        </div>
        
        <Button type="submit" className="w-full py-3" disabled={loading}>
          {loading ? 'جاري تسجيل الدخول...' : 'تسجيل الدخول'}
        </Button>
      </form>

      {/* Forgot Password Link */}
      <div className="text-center mt-4">
        <button
          type="button"
          onClick={() => setShowForgotPassword(true)}
          className="text-red-600 hover:text-red-700 text-sm hover:underline"
        >
          نسيت كلمة المرور؟
        </button>
      </div>

      {/* Register Link */}
      <div className="mt-4 text-center border-t pt-4">
        <p className="text-sm text-gray-600">
          ليس لديك حساب؟{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
          >
            قم بالتسجيل
          </button>
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordFlow 
          onClose={() => setShowForgotPassword(false)}
          onBackToLogin={() => setShowForgotPassword(false)}
        />
      )}
    </DialogContent>
  );
};

// Keep original LoginForm for backward compatibility
const LoginForm = ({ onClose }) => {
  return <LoginFormWithRegister onClose={onClose} onSwitchToRegister={() => {}} />;
};

// Countries list in Arabic
const COUNTRIES = [
  'ألمانيا', 'النمسا', 'سويسرا', 'فرنسا', 'هولندا', 'بلجيكا', 'إيطاليا', 'إسبانيا', 
  'المملكة المتحدة', 'الدنمارك', 'السويد', 'النرويج', 'فنلندا', 'بولندا', 'التشيك',
  'المجر', 'رومانيا', 'بلغاريا', 'اليونان', 'البرتغال', 'لوكسمبورغ', 'أيرلندا',
  'سوريا', 'لبنان', 'الأردن', 'فلسطين', 'العراق', 'السعودية', 'الإمارات', 'قطر',
  'الكويت', 'البحرين', 'عُمان', 'اليمن', 'مصر', 'ليبيا', 'تونس', 'الجزائر', 'المغرب',
  'السودان', 'الصومال', 'جيبوتي', 'موريتانيا', 'جزر القمر', 'أخرى'
];

// Cities by country (main European countries)
const CITIES_BY_COUNTRY = {
  'ألمانيا': ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Cologne', 'Stuttgart', 'Dusseldorf', 'Dortmund', 'Essen', 'Leipzig', 'Bremen', 'Dresden', 'Hanover', 'Nuremberg', 'Duisburg', 'Bochum', 'Wuppertal', 'Bielefeld', 'Bonn', 'Munster', 'Karlsruhe', 'Mannheim', 'Augsburg', 'Wiesbaden', 'Gelsenkirchen', 'Monchengladbach', 'Braunschweig', 'Chemnitz', 'Kiel', 'Aachen', 'أخرى'],
  'النمسا': ['Vienna', 'Graz', 'Linz', 'Salzburg', 'Innsbruck', 'Klagenfurt', 'Villach', 'Wels', 'Sankt Polten', 'Dornbirn', 'Wiener Neustadt', 'Steyr', 'Feldkirch', 'Bregenz', 'Leonding', 'أخرى'],
  'سويسرا': ['Zurich', 'Geneva', 'Basel', 'Bern', 'Lausanne', 'Winterthur', 'Lucerne', 'St. Gallen', 'Lugano', 'Biel', 'Thun', 'Koniz', 'La Chaux-de-Fonds', 'Fribourg', 'Schaffhausen', 'أخرى'],
  'فرنسا': ['Paris', 'Marseille', 'Lyon', 'Toulouse', 'Nice', 'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille', 'Rennes', 'Reims', 'Le Havre', 'Saint-Etienne', 'Toulon', 'Grenoble', 'Dijon', 'Angers', 'Nimes', 'Villeurbanne', 'أخرى'],
  'هولندا': ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Eindhoven', 'Tilburg', 'Groningen', 'Almere', 'Breda', 'Nijmegen', 'Enschede', 'Haarlem', 'Arnhem', 'Zaanstad', 'Amersfoort', 'أخرى'],
  'بلجيكا': ['Brussels', 'Antwerp', 'Ghent', 'Charleroi', 'Liege', 'Bruges', 'Namur', 'Leuven', 'Mons', 'Mechelen', 'Aalst', 'La Louviere', 'Kortrijk', 'Hasselt', 'Ostend', 'أخرى'],
  'إيطاليا': ['Rome', 'Milan', 'Naples', 'Turin', 'Palermo', 'Genoa', 'Bologna', 'Florence', 'Bari', 'Catania', 'Venice', 'Verona', 'Messina', 'Padua', 'Trieste', 'Brescia', 'Taranto', 'Prato', 'Parma', 'Modena', 'أخرى'],
  'إسبانيا': ['Madrid', 'Barcelona', 'Valencia', 'Seville', 'Zaragoza', 'Malaga', 'Murcia', 'Palma', 'Las Palmas', 'Bilbao', 'Alicante', 'Cordoba', 'Valladolid', 'Vigo', 'Gijon', 'Hospitalet', 'Granada', 'Vitoria', 'Elche', 'Oviedo', 'أخرى'],
  'المملكة المتحدة': ['London', 'Birmingham', 'Leeds', 'Glasgow', 'Sheffield', 'Manchester', 'Edinburgh', 'Liverpool', 'Bristol', 'Cardiff', 'Leicester', 'Coventry', 'Bradford', 'Nottingham', 'Belfast', 'Newcastle', 'Brighton', 'Hull', 'Plymouth', 'Stoke', 'أخرى'],
  'الدنمارك': ['Copenhagen', 'Aarhus', 'Odense', 'Aalborg', 'Esbjerg', 'Randers', 'Kolding', 'Horsens', 'Vejle', 'Roskilde', 'Herning', 'Silkeborg', 'Naestved', 'Frederiksberg', 'Viborg', 'أخرى'],
  'السويد': ['Stockholm', 'Gothenburg', 'Malmo', 'Uppsala', 'Vasteras', 'Orebro', 'Linkoping', 'Helsingborg', 'Jonkoping', 'Norrkoping', 'Lund', 'Umea', 'Gavle', 'Boras', 'Sodertalje', 'أخرى'],
  'النرويج': ['Oslo', 'Bergen', 'Stavanger', 'Trondheim', 'Drammen', 'Fredrikstad', 'Kristiansand', 'Sandnes', 'Tromso', 'Sarpsborg', 'Skien', 'Alesund', 'Sandefjord', 'Haugesund', 'Tonsberg', 'أخرى'],
  'فنلندا': ['Helsinki', 'Espoo', 'Tampere', 'Vantaa', 'Oulu', 'Turku', 'Jyvaskyla', 'Lahti', 'Kuopio', 'Pori', 'Joensuu', 'Lappeenranta', 'Hameenlinna', 'Vaasa', 'Seinajoki', 'أخرى'],
  'بولندا': ['Warsaw', 'Krakow', 'Lodz', 'Wroclaw', 'Poznan', 'Gdansk', 'Szczecin', 'Bydgoszcz', 'Lublin', 'Katowice', 'Bialystok', 'Gdynia', 'Czestochowa', 'Radom', 'Sosnowiec', 'أخرى'],
  'البرتغال': ['Lisbon', 'Porto', 'Amadora', 'Braga', 'Setubal', 'Coimbra', 'Queluz', 'Funchal', 'Cacém', 'Vila Nova de Gaia', 'Loures', 'Évora', 'Rio de Mouro', 'Odivelas', 'Aveiro', 'أخرى'],
  'اليونان': ['Athens', 'Thessaloniki', 'Patras', 'Heraklion', 'Larissa', 'Volos', 'Rhodes', 'Ioannina', 'Chania', 'Chalcis', 'Agrinio', 'Kalamata', 'Kavala', 'Katerini', 'Trikala', 'أخرى'],
  'أيرلندا': ['Dublin', 'Cork', 'Limerick', 'Galway', 'Waterford', 'Drogheda', 'Dundalk', 'Swords', 'Bray', 'Navan', 'Ennis', 'Kilkenny', 'Carlow', 'Tralee', 'Newbridge', 'أخرى'],
};

// Splash Screen Component
const SplashScreen = ({ onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3000); // 3 seconds splash

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-orange-400 via-amber-500 to-red-500 flex items-center justify-center">
      {/* Background Food Icons */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute top-10 left-10 text-6xl">🍽️</div>
        <div className="absolute top-20 right-20 text-4xl">🥙</div>
        <div className="absolute top-40 left-32 text-5xl">🍖</div>
        <div className="absolute top-60 right-16 text-3xl">🍚</div>
        <div className="absolute bottom-40 left-20 text-5xl">🥗</div>
        <div className="absolute bottom-60 right-32 text-4xl">🍲</div>
        <div className="absolute bottom-20 left-40 text-6xl">🧆</div>
        <div className="absolute top-32 right-40 text-3xl">🍛</div>
        <div className="absolute bottom-80 left-60 text-4xl">🥘</div>
        <div className="absolute top-80 right-60 text-5xl">🍜</div>
      </div>

      {/* Logo and App Name */}
      <div className="text-center text-white z-10">
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-white bg-opacity-20 rounded-full flex items-center justify-center mb-4 backdrop-blur-sm border-2 border-white border-opacity-30">
            <Utensils className="h-12 w-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Arial, sans-serif' }}>
            دليل المطاعم العربية
          </h1>
          <p className="text-xl opacity-90">
            طعم الوطن بعيداً عن الوطن
          </p>
        </div>
        
        {/* Loading Animation */}
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-opacity-30 border-t-white"></div>
        </div>
      </div>
    </div>
  );
};

// Authentication Screen Component
// Simple Authentication Screen using new components
const AuthScreen = () => {
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);

  const handleAuthComplete = () => {
    // التحقق من إذن الموقع
    const locationPermission = localStorage.getItem('locationPermission');
    
    // إذا لم يتم السؤال عن الموقع من قبل، نسأل الآن
    if (!locationPermission) {
      setTimeout(() => {
        setShowLocationPrompt(true);
      }, 500);
    } else {
      // إعادة تحميل الصفحة للدخول للتطبيق
      window.location.reload();
    }
  };

  const handleLocationAccept = () => {
    setShowLocationPrompt(false);
    // طلب إذن الموقع من النظام
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          localStorage.setItem('userLocation', JSON.stringify(location));
          localStorage.setItem('locationPermission', 'always');
          toast.success('تم تحديد موقعك بنجاح');
          window.location.reload();
        },
        (error) => {
          console.log('Location error:', error);
          localStorage.setItem('locationPermission', 'denied');
          window.location.reload();
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    } else {
      localStorage.setItem('locationPermission', 'denied');
      window.location.reload();
    }
  };

  const handleLocationDeny = () => {
    setShowLocationPrompt(false);
    localStorage.setItem('locationPermission', 'denied');
    window.location.reload();
  };

  // رسالة طلب الموقع
  if (showLocationPrompt) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" dir="rtl">
        <div className="bg-white rounded-2xl shadow-2xl p-8 mx-4 max-w-sm w-full text-center animate-in fade-in zoom-in duration-300">
          {/* أيقونة الموقع */}
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center">
            <MapPin className="h-10 w-10 text-amber-600" />
          </div>
          
          {/* العنوان */}
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            تحديد موقعك
          </h2>
          
          {/* الوصف */}
          <p className="text-gray-500 mb-6 leading-relaxed">
            هل تريد السماح للتطبيق بتحديد موقعك لإظهار المطاعم القريبة منك؟
          </p>
          
          {/* الأزرار */}
          <div className="space-y-3">
            <button
              onClick={handleLocationAccept}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Navigation className="h-5 w-5" />
              <span>نعم، حدد موقعي</span>
            </button>
            
            <button
              onClick={handleLocationDeny}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-xl transition-all duration-200"
            >
              لا، شكراً
            </button>
          </div>
          
          {/* نص مساعد */}
          <p className="text-xs text-gray-400 mt-4">
            يمكنك تغيير هذا الإعداد لاحقاً من الإعدادات
          </p>
        </div>
      </div>
    );
  }

  return <AuthenticationGate onAuthenticated={handleAuthComplete} />;
};

// My Ratings Page Component
const MyRatingsPage = () => {
  const { token } = useAuth();
  const [myRatings, setMyRatings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyRatings();
  }, []);

  const fetchMyRatings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/my-ratings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRatings(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل التقييمات');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" style={{ direction: 'rtl' }}>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">تقييماتي</h1>
        <p className="text-gray-600">
          جميع تقييماتك للمطاعم في مكان واحد
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">جاري التحميل...</p>
        </div>
      ) : myRatings.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <Star className="h-24 w-24 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">لا توجد تقييمات بعد</h3>
            <p className="text-gray-500 mb-6">قم بزيارة المطاعم وإضافة تقييماتك</p>
            <Button onClick={() => window.location.reload()} className="bg-orange-600 hover:bg-orange-700">
              استكشف المطاعم
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-6 flex items-center justify-between">
            <p className="text-lg text-gray-700">
              عدد التقييمات: <span className="font-bold text-orange-600">{myRatings.length}</span>
            </p>
            <Button onClick={fetchMyRatings} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 ml-2" />
              تحديث
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {myRatings.map((rating) => (
              <Card key={rating.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <h3 className="font-bold text-lg text-orange-700 mb-2 line-clamp-2">
                      {rating.restaurant_name || 'مطعم'}
                    </h3>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-500" dir="ltr" style={{ textAlign: 'left' }}>
                        {new Date(rating.created_at).toLocaleDateString('en-GB', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                      <div className="flex items-center gap-1 bg-amber-100 px-2 py-1 rounded-full">
                        <Star className="h-4 w-4 text-amber-600 fill-current" />
                        <span className="font-bold text-sm text-amber-800">
                          {((rating.service_rating + rating.price_rating + rating.taste_rating + (rating.cleanliness_rating || 0)) / 4).toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="bg-blue-50 rounded p-2 text-center">
                      <p className="text-xs text-gray-600 mb-1">الخدمة</p>
                      <div className="flex items-center justify-center gap-1">
                        <Star className="h-3 w-3 text-blue-600 fill-current" />
                        <span className="font-bold text-blue-700">{rating.service_rating}</span>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded p-2 text-center">
                      <p className="text-xs text-gray-600 mb-1">السعر</p>
                      <div className="flex items-center justify-center gap-1">
                        <Star className="h-3 w-3 text-green-600 fill-current" />
                        <span className="font-bold text-green-700">{rating.price_rating}</span>
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded p-2 text-center">
                      <p className="text-xs text-gray-600 mb-1">الطعم</p>
                      <div className="flex items-center justify-center gap-1">
                        <Star className="h-3 w-3 text-orange-600 fill-current" />
                        <span className="font-bold text-orange-700">{rating.taste_rating}</span>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded p-2 text-center">
                      <p className="text-xs text-gray-600 mb-1">النظافة</p>
                      <div className="flex items-center justify-center gap-1">
                        <Star className="h-3 w-3 text-purple-600 fill-current" />
                        <span className="font-bold text-purple-700">{rating.cleanliness_rating || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {rating.comment && (
                    <div className="bg-gray-50 rounded p-3 border-r-4 border-orange-500">
                      <p className="text-xs text-gray-600 mb-1 font-medium">تعليقك:</p>
                      <p className="text-sm text-gray-700 line-clamp-3">{rating.comment}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Restaurant Suggestion Form Component
const RestaurantSuggestionForm = () => {
  const { user, token } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    restaurant_name: '',
    restaurant_type: '',
    restaurant_address: '',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/suggest-restaurant`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('تم إرسال اقتراحك بنجاح!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إرسال الاقتراح');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      restaurant_name: '',
      restaurant_type: '',
      restaurant_address: '',
      additional_notes: ''
    });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="container mx-auto px-4 py-8" style={{ direction: 'rtl' }}>
        <Card className="max-w-2xl mx-auto">
          <CardContent className="text-center py-12">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Utensils className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-800 mb-2">تم الإرسال بنجاح!</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                شكراً لك على اقتراح المطعم! 🎉
                <br />
                <strong>سيتم التواصل معك في أقرب فرصة</strong> لإضافة المطعم للدليل.
              </p>
            </div>
            
            <div className="space-y-3 text-sm text-gray-600">
              <p>📧 سيتم مراجعة اقتراحك من قبل فريق الإدارة</p>
              <p>📞 سيتم التواصل معك عبر الهاتف أو البريد الإلكتروني</p>
              <p>⏰ عادة ما يستغرق الأمر 24-48 ساعة</p>
            </div>

            <div className="flex gap-4 mt-8 justify-center">
              <Button onClick={resetForm} variant="outline">
                اقتراح مطعم آخر
              </Button>
              <Button onClick={() => window.location.reload()}>
                العودة للصفحة الرئيسية
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" style={{ direction: 'rtl' }}>
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">اقتراح مطعم جديد</h1>
          <p className="text-gray-600">
            ساعدنا في إضافة مطاعم عربية جديدة للدليل
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Utensils className="h-5 w-5 text-orange-600" />
              معلومات المطعم المقترح
            </CardTitle>
            <CardDescription>
              املأ المعلومات التالية وسيتم التواصل معك لإضافة المطعم
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">معلوماتك الشخصية</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="name">الاسم *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">البريد الإلكتروني *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="example@email.com"
                      dir="ltr"
                      style={{ textAlign: 'left' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">رقم الهاتف *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      placeholder="+49 30 1234567"
                    />
                  </div>
                </div>
              </div>

              {/* Restaurant Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">معلومات المطعم</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="restaurant_name">اسم المطعم *</Label>
                  <Input
                    id="restaurant_name"
                    value={formData.restaurant_name}
                    onChange={(e) => setFormData({ ...formData, restaurant_name: e.target.value })}
                    required
                    placeholder="مثل: مطعم الشام الأصيل"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="restaurant_type">نوع المطعم *</Label>
                  <Input
                    id="restaurant_type"
                    value={formData.restaurant_type}
                    onChange={(e) => setFormData({ ...formData, restaurant_type: e.target.value })}
                    required
                    placeholder="مثل: شامي، لبناني، عراقي، مغربي"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="restaurant_address">عنوان المطعم *</Label>
                  <Input
                    id="restaurant_address"
                    value={formData.restaurant_address}
                    onChange={(e) => setFormData({ ...formData, restaurant_address: e.target.value })}
                    required
                    placeholder="العنوان الكامل للمطعم"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additional_notes">ملاحظات إضافية</Label>
                  <Textarea
                    id="additional_notes"
                    value={formData.additional_notes}
                    onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                    placeholder="أي معلومات إضافية عن المطعم (اختياري)"
                    rows={3}
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MessageCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">ماذا يحدث بعد الإرسال؟</p>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-blue-700">
                      <li>سيتم إرسال اقتراحك لفريق الإدارة</li>
                      <li>سيتم التواصل معك خلال 24-48 ساعة</li>
                      <li>بعد التأكد من صحة المعلومات، سيتم إضافة المطعم للدليل</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full py-3 text-lg" disabled={loading}>
                {loading ? 'جاري الإرسال...' : 'إرسال الاقتراح'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Coupon Dialog Component
const CouponDialog = ({ restaurantId, restaurantName }) => {
  const [open, setOpen] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCoupon = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/restaurants/${restaurantId}/coupon`);
      setCoupon(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في جلب الكوبون');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCoupon = () => {
    setOpen(true);
    fetchCoupon();
  };

  const formatExpiry = (expiryDate) => {
    if (!expiryDate) return '';
    try {
      return new Date(expiryDate).toLocaleDateString('ar');
    } catch {
      return expiryDate;
    }
  };

  return (
    <>
      <Button 
        onClick={handleOpenCoupon}
        className="bg-orange-500 hover:bg-orange-600 text-white"
      >
        <Ticket className="h-4 w-4 mr-2" />
        كوبون خصم
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" style={{ direction: 'rtl' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Ticket className="h-5 w-5" />
              كوبون خصم خاص
            </DialogTitle>
            <DialogDescription>
              {restaurantName}
            </DialogDescription>
          </DialogHeader>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            </div>
          ) : coupon ? (
            <div className="space-y-4">
              {/* Coupon Design */}
              <div className="relative bg-gradient-to-r from-orange-400 to-red-500 text-white p-6 rounded-lg border-2 border-dashed border-orange-600">
                <div className="absolute -top-3 -left-3 w-6 h-6 bg-white rounded-full"></div>
                <div className="absolute -top-3 -right-3 w-6 h-6 bg-white rounded-full"></div>
                <div className="absolute -bottom-3 -left-3 w-6 h-6 bg-white rounded-full"></div>
                <div className="absolute -bottom-3 -right-3 w-6 h-6 bg-white rounded-full"></div>
                
                <div className="text-center space-y-2">
                  <h3 className="text-xl font-bold">{coupon.coupon_title || 'خصم خاص'}</h3>
                  <div className="text-3xl font-bold">
                    {coupon.coupon_discount}
                  </div>
                  <p className="text-sm opacity-90">
                    {coupon.coupon_description}
                  </p>
                  
                  {coupon.coupon_code && (
                    <div className="mt-4 p-3 bg-white bg-opacity-20 rounded-lg">
                      <p className="text-xs">كود الكوبون:</p>
                      <p className="text-lg font-bold tracking-wider">
                        {coupon.coupon_code}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Coupon Details */}
              <div className="space-y-3 text-sm">
                {coupon.coupon_expiry && (
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <span className="text-red-600 font-medium">تاريخ الانتهاء:</span>
                    <span className="text-red-700 font-bold">
                      {formatExpiry(coupon.coupon_expiry)}
                    </span>
                  </div>
                )}

                {coupon.coupon_terms && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-700 mb-1">الشروط والأحكام:</h4>
                    <p className="text-gray-600 text-xs leading-relaxed">
                      {coupon.coupon_terms}
                    </p>
                  </div>
                )}

                <div className="text-center pt-3 border-t">
                  <p className="text-xs text-gray-500">
                    أظهر هذا الكوبون للحصول على الخصم
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    تم إنشاؤه في: {new Date(coupon.generated_at).toLocaleString('ar')}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

// Register Form Component
const RegisterForm = ({ onClose }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    name: '', // خانة واحدة للاسم
    city: '',
    country: 'ألمانيا', // Default to Germany since this is a German restaurant directory
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [customCity, setCustomCity] = useState('');
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [customCountry, setCustomCountry] = useState('');
  const [showCustomCountry, setShowCustomCountry] = useState(false);

  const handleCountryChange = (value) => {
    setFormData({ ...formData, country: value, city: '' });
    setShowCustomCountry(value === 'أخرى');
    setShowCustomCity(false);
    setCustomCity('');
  };

  const handleCityChange = (value) => {
    if (value === 'أخرى') {
      setShowCustomCity(true);
      setFormData({ ...formData, city: '' });
    } else {
      setShowCustomCity(false);
      setFormData({ ...formData, city: value });
      setCustomCity('');
    }
  };

  const handleCustomCityChange = (e) => {
    const value = e.target.value;
    setCustomCity(value);
    setFormData({ ...formData, city: value });
  };

  const handleCustomCountryChange = (e) => {
    const value = e.target.value;
    setCustomCountry(value);
    setFormData({ ...formData, country: value });
  };

  const availableCities = CITIES_BY_COUNTRY[formData.country] || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/register`, formData);
      setVerificationCode(response.data.verification_code); // For demo purposes
      setShowVerification(true);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إنشاء الحساب');
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = (token, user) => {
    login(token, user);
    onClose();
  };

  return (
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" style={{ direction: 'rtl' }}>
      {!showVerification ? (
        <>
          <DialogHeader>
            <DialogTitle className="text-xl">إنشاء حساب جديد</DialogTitle>
            <DialogDescription className="text-sm">
              املأ البيانات التالية لإنشاء حساب مستخدم جديد
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Personal Information */}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">الاسم</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="text-base"
                  placeholder="أدخل اسمك الكامل"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">البلد</Label>
                  {!showCustomCountry ? (
                    <Select value={formData.country} onValueChange={handleCountryChange}>
                      <SelectTrigger className="text-base">
                        <SelectValue placeholder="اختر البلد" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="custom_country"
                      value={customCountry}
                      onChange={handleCustomCountryChange}
                      required
                      className="text-base"
                      placeholder="اكتب اسم البلد (عربي أو إنجليزي)"
                    />
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="city">المدينة</Label>
                  {availableCities.length > 0 && !showCustomCity ? (
                    <>
                      <Select value={formData.city} onValueChange={handleCityChange}>
                        <SelectTrigger className="text-base">
                          <SelectValue placeholder="اختر المدينة" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCities.map((city) => (
                            <SelectItem key={city} value={city}>
                              {city}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 يرجى كتابة المدينة باللغة الإنجليزية
                      </p>
                    </>
                  ) : (
                    <>
                      <Input
                        id="city"
                        value={showCustomCity ? customCity : formData.city}
                        onChange={showCustomCity ? handleCustomCityChange : (e) => setFormData({ ...formData, city: e.target.value })}
                        required
                        className="text-base"
                        placeholder="مثال: Berlin, Munich, Vienna..."
                        dir="ltr"
                      />
                      <p className="text-xs text-orange-600 mt-1 font-medium">
                        ⚠️ يجب كتابة المدينة باللغة الإنجليزية
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">البريد الإلكتروني</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="text-base"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">كلمة المرور</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="text-base"
                  minLength={6}
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full py-3 text-base" 
              disabled={loading || !formData.country}
            >
              {loading ? 'جاري إنشاء الحساب...' : 'إنشاء حساب مستخدم'}
            </Button>
          </form>
        </>
      ) : (
        <VerificationDialog 
          email={formData.email}
          onVerified={handleVerified}
          demoCode={verificationCode}
        />
      )}
    </DialogContent>
  );
};

// Rating Component
const RatingComponent = ({ restaurantId, onRatingAdded }) => {
  const { user, token, updateUser } = useAuth();
  const [ratings, setRatings] = useState({
    service_rating: 5,
    price_rating: 5,
    taste_rating: 5,
    cleanliness_rating: 5,
    comment: ''
  });
  const [loading, setLoading] = useState(false);
  const [showCongrats, setShowCongrats] = useState(false);
  const [congratsData, setCongratsData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/restaurants/${restaurantId}/ratings`, ratings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم إضافة التقييم بنجاح');
      
      // Check if gamification data is returned
      if (response.data.gamification) {
        const gamification = response.data.gamification;
        
        // Update user with new points and level
        const updatedUser = {
          ...user,
          points: gamification.total_points,
          level: gamification.level,
          badges: [...(user.badges || []), ...gamification.new_badges]
        };
        updateUser(updatedUser);
        
        // Show congratulations modal if it's first rating or level up
        if (gamification.first_rating || gamification.level_up || gamification.new_badges.length > 0) {
          setCongratsData(gamification);
          setShowCongrats(true);
        }
      }
      
      onRatingAdded();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'خطأ في إضافة التقييم');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600">يجب تسجيل الدخول لإضافة تقييم</p>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4" style={{ direction: 'rtl' }}>
        <div className="space-y-4">
          <div>
            <Label>تقييم الخدمة ({ratings.service_rating}/5)</Label>
            <Input
              type="range"
              min="1"
              max="5"
              value={ratings.service_rating}
              onChange={(e) => setRatings({ ...ratings, service_rating: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>ضعيف</span>
              <span>ممتاز</span>
            </div>
          </div>
          
          <div>
            <Label>تقييم الأسعار ({ratings.price_rating}/5)</Label>
            <Input
              type="range"
              min="1"
              max="5"
              value={ratings.price_rating}
              onChange={(e) => setRatings({ ...ratings, price_rating: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>مرتفع</span>
              <span>مناسب</span>
            </div>
          </div>
          
          <div>
            <Label>تقييم الطعم ({ratings.taste_rating}/5)</Label>
            <Input
              type="range"
              min="1"
              max="5"
              value={ratings.taste_rating}
              onChange={(e) => setRatings({ ...ratings, taste_rating: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>غير لذيذ</span>
              <span>لذيذ جداً</span>
            </div>
          </div>
          
          <div>
            <Label>تقييم النظافة ({ratings.cleanliness_rating}/5)</Label>
            <Input
              type="range"
              min="1"
              max="5"
              value={ratings.cleanliness_rating}
              onChange={(e) => setRatings({ ...ratings, cleanliness_rating: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>غير نظيف</span>
              <span>نظيف جداً</span>
            </div>
          </div>
          
          <div>
            <Label htmlFor="comment">تعليق (اختياري)</Label>
            <Textarea
              id="comment"
              value={ratings.comment}
              onChange={(e) => setRatings({ ...ratings, comment: e.target.value })}
              placeholder="شاركنا تجربتك مع هذا المطعم..."
            />
          </div>
        </div>
        
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? 'جاري إضافة التقييم...' : 'إضافة التقييم'}
        </Button>
      </form>

      {/* Congratulations Modal */}
      {showCongrats && congratsData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[200]" style={{ direction: 'rtl' }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="text-center">
              {/* Icon */}
              <div className="inline-block bg-gradient-to-br from-orange-400 to-amber-500 rounded-full p-4 mb-4">
                <Trophy className="h-12 w-12 text-white" />
              </div>
              
              {/* Title */}
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                {congratsData.first_rating && 'تهانينا! أول تقييم لك!'}
                {!congratsData.first_rating && congratsData.level_up && 'مبروك! ترقية في المستوى!'}
                {!congratsData.first_rating && !congratsData.level_up && 'رائع! حصلت على نقاط جديدة!'}
              </h2>
              
              {/* Points earned */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 mb-4 border border-orange-200">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-3xl font-bold text-orange-600">+{congratsData.earned_points}</span>
                  <span className="text-gray-600">نقطة</span>
                </div>
                <div className="text-sm text-gray-600">
                  إجمالي النقاط: <span className="font-bold text-orange-600">{congratsData.total_points}</span>
                </div>
              </div>
              
              {/* Level info */}
              {congratsData.level_up && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-4 mb-4 border border-blue-200">
                  <div className="flex items-center justify-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    <span className="text-lg font-bold text-blue-800">
                      المستوى الجديد: {congratsData.level}
                    </span>
                  </div>
                </div>
              )}
              
              {/* New badges */}
              {congratsData.new_badges && congratsData.new_badges.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">حصلت على وسام جديد:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {congratsData.new_badges.map((badge, index) => (
                      <div key={index} className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg px-4 py-2 border-2 border-amber-300">
                        <div className="flex items-center gap-2">
                          <Award className="h-5 w-5 text-amber-700" />
                          <span className="font-bold text-amber-900">{badge}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Encouragement message */}
              {congratsData.first_rating && (
                <p className="text-gray-600 text-sm mb-4">
                  شكراً لمشاركة تجربتك! استمر في كتابة التقييمات لكسب المزيد من النقاط والأوسمة.
                </p>
              )}
              
              {/* Close button */}
              <Button 
                onClick={() => setShowCongrats(false)}
                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
              >
                رائع!
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// دالة تحويل رابط الصورة إلى thumbnail (للتحميل السريع)
const getOptimizedImageUrl = (url, size = 'thumbnail') => {
  if (!url) return '';
  
  // Unsplash images - add size parameters
  if (url.includes('unsplash.com')) {
    const baseUrl = url.split('?')[0];
    if (size === 'thumbnail') {
      return `${baseUrl}?w=400&h=300&fit=crop&q=60&auto=format`;
    } else if (size === 'medium') {
      return `${baseUrl}?w=800&h=600&fit=crop&q=75&auto=format`;
    } else {
      return `${baseUrl}?w=1200&q=80&auto=format`;
    }
  }
  
  // Cloudinary images
  if (url.includes('cloudinary.com')) {
    if (size === 'thumbnail') {
      return url.replace('/upload/', '/upload/w_400,h_300,c_fill,q_60,f_auto/');
    } else if (size === 'medium') {
      return url.replace('/upload/', '/upload/w_800,h_600,c_fill,q_75,f_auto/');
    }
  }
  
  // imgix images
  if (url.includes('imgix.net')) {
    const separator = url.includes('?') ? '&' : '?';
    if (size === 'thumbnail') {
      return `${url}${separator}w=400&h=300&fit=crop&q=60&auto=format`;
    }
  }
  
  // Other images - return as is
  return url;
};

// Image Gallery Component for Multiple Images
const ImageGallery = ({ images, altText, size = 'thumbnail', priority = false }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // صورة افتراضية
  const defaultImage = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop&q=60&auto=format";
  
  // إذا لم توجد صور، استخدم الصورة الافتراضية
  const actualImages = (images && images.length > 0 && images[0]) ? images : [defaultImage];
  
  // تحويل الصور إلى thumbnails
  const optimizedImages = actualImages.map(img => getOptimizedImageUrl(img, size));
  
  const nextImage = () => {
    setImageLoaded(false);
    setCurrentImageIndex((prev) => (prev + 1) % actualImages.length);
  };
  
  const prevImage = () => {
    setImageLoaded(false);
    setCurrentImageIndex((prev) => (prev - 1 + actualImages.length) % actualImages.length);
  };

  // صورة احتياطية عند فشل التحميل
  const fallbackImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Ctext fill='%239ca3af' font-family='Arial' font-size='40' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3E🍽️%3C/text%3E%3C/svg%3E";
  
  return (
    <div className="relative w-full h-full bg-gray-100">
      {/* Skeleton أثناء التحميل */}
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer" />
      )}
      
      <img 
        src={imageError ? fallbackImage : optimizedImages[currentImageIndex]} 
        alt={altText}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={priority ? "high" : "auto"}
        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setImageLoaded(true)}
        onError={() => {
          setImageError(true);
          setImageLoaded(true);
        }}
      />
      
      {actualImages.length > 1 && (
        <>
          {/* Navigation Arrows */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          
          {/* Image Counter */}
          <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
            {currentImageIndex + 1} / {actualImages.length}
          </div>
          
          {/* Dots Indicator */}
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
            {actualImages.map((_, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setImageLoaded(false);
                  setCurrentImageIndex(index);
                }}
                className={`w-2 h-2 rounded-full ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Helper function to check if restaurant is "new"
const isRestaurantNew = (restaurant) => {
  // Check if restaurant has no ratings
  const hasNoRatings = !restaurant.average_rating || restaurant.average_rating === 0 || !restaurant.total_ratings || restaurant.total_ratings === 0;
  
  if (!hasNoRatings) {
    return false; // Has ratings, so not new
  }
  
  // Check if created within last 30 days
  if (!restaurant.created_at) {
    return true; // No creation date, assume new for backward compatibility
  }
  
  const createdDate = new Date(restaurant.created_at);
  const now = new Date();
  const daysDifference = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
  
  return daysDifference <= 30;
};

// دالة تحديد حالة المطعم (مفتوح/مغلق)
const getRestaurantStatus = (openingHours) => {
  if (!openingHours || Object.keys(openingHours).length === 0) {
    return { isOpen: null, statusText: '', nextChange: '' };
  }

  const now = new Date();
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  
  const currentDay = days[now.getDay()];
  const yesterdayIndex = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const yesterday = days[yesterdayIndex];
  
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTime = currentHour * 60 + currentMinute; // الوقت بالدقائق من منتصف الليل

  // دالة لتحليل وقت العمل
  const parseHours = (dayHours) => {
    if (!dayHours || dayHours === 'closed' || dayHours === 'مغلق') {
      return null;
    }
    
    let openTime, closeTime;
    
    if (typeof dayHours === 'string') {
      const parts = dayHours.replace(/\s/g, '').split('-');
      if (parts.length !== 2) return null;
      const [openStr, closeStr] = parts;
      const [openHour, openMin] = openStr.split(':').map(Number);
      const [closeHour, closeMin] = closeStr.split(':').map(Number);
      openTime = openHour * 60 + (openMin || 0);
      closeTime = closeHour * 60 + (closeMin || 0);
    } else if (typeof dayHours === 'object') {
      const [openHour, openMin] = (dayHours.open || '09:00').split(':').map(Number);
      const [closeHour, closeMin] = (dayHours.close || '22:00').split(':').map(Number);
      openTime = openHour * 60 + (openMin || 0);
      closeTime = closeHour * 60 + (closeMin || 0);
    } else {
      return null;
    }
    
    return { openTime, closeTime };
  };

  // التحقق من اليوم الحالي
  const todayHours = parseHours(openingHours[currentDay]);
  const yesterdayHours = parseHours(openingHours[yesterday]);

  // التحقق مما إذا كان المطعم مفتوحاً من الأمس (يغلق بعد منتصف الليل)
  if (yesterdayHours) {
    const { openTime: yOpen, closeTime: yClose } = yesterdayHours;
    // إذا كان وقت الإغلاق أقل من وقت الفتح، يعني يغلق بعد منتصف الليل
    if (yClose < yOpen && yClose > 0) {
      // المطعم مفتوح من الأمس ويغلق اليوم بعد منتصف الليل
      if (currentTime < yClose) {
        const minsLeft = yClose - currentTime;
        const hoursLeft = Math.floor(minsLeft / 60);
        
        let nextChange = '';
        if (hoursLeft === 0) {
          nextChange = `يغلق بعد ${minsLeft} دقيقة`;
        } else if (hoursLeft === 1) {
          nextChange = 'يغلق بعد ساعة';
        } else if (hoursLeft === 2) {
          nextChange = 'يغلق بعد ساعتين';
        } else {
          nextChange = `يغلق بعد ${hoursLeft} ساعات`;
        }
        
        return { isOpen: true, statusText: 'مفتوح', nextChange };
      }
    }
  }

  // التحقق من ساعات اليوم الحالي
  if (!todayHours) {
    return { isOpen: false, statusText: 'مغلق', nextChange: 'مغلق اليوم' };
  }

  const { openTime, closeTime } = todayHours;

  // التحقق مما إذا كان المطعم يغلق بعد منتصف الليل
  const closesAfterMidnight = closeTime < openTime || closeTime <= 6 * 60; // يغلق قبل 6 صباحاً

  if (closesAfterMidnight) {
    // المطعم يفتح اليوم ويغلق غداً بعد منتصف الليل
    if (currentTime >= openTime) {
      // مفتوح - يحسب الوقت حتى منتصف الليل + وقت الإغلاق
      const minsUntilMidnight = 24 * 60 - currentTime;
      const totalMinsLeft = minsUntilMidnight + closeTime;
      const hoursLeft = Math.floor(totalMinsLeft / 60);
      
      let nextChange = '';
      if (hoursLeft === 0) {
        nextChange = `يغلق بعد ${totalMinsLeft} دقيقة`;
      } else if (hoursLeft === 1) {
        nextChange = 'يغلق بعد ساعة';
      } else if (hoursLeft === 2) {
        nextChange = 'يغلق بعد ساعتين';
      } else {
        nextChange = `يغلق بعد ${hoursLeft} ساعات`;
      }
      
      return { isOpen: true, statusText: 'مفتوح', nextChange };
    } else {
      // لم يفتح بعد
      const openHourStr = Math.floor(openTime / 60);
      const openMinStr = openTime % 60;
      const openTimeStr = `${openHourStr.toString().padStart(2, '0')}:${openMinStr.toString().padStart(2, '0')}`;
      return { isOpen: false, statusText: 'مغلق', nextChange: `يفتح ${openTimeStr}` };
    }
  } else {
    // المطعم يفتح ويغلق في نفس اليوم
    if (currentTime >= openTime && currentTime < closeTime) {
      const minsLeft = closeTime - currentTime;
      const hoursLeft = Math.floor(minsLeft / 60);
      
      let nextChange = '';
      if (hoursLeft === 0) {
        nextChange = `يغلق بعد ${minsLeft % 60} دقيقة`;
      } else if (hoursLeft === 1) {
        nextChange = 'يغلق بعد ساعة';
      } else if (hoursLeft === 2) {
        nextChange = 'يغلق بعد ساعتين';
      } else {
        nextChange = `يغلق بعد ${hoursLeft} ساعات`;
      }
      
      return { isOpen: true, statusText: 'مفتوح', nextChange };
    } else if (currentTime < openTime) {
      const openHourStr = Math.floor(openTime / 60);
      const openMinStr = openTime % 60;
      const openTimeStr = `${openHourStr.toString().padStart(2, '0')}:${openMinStr.toString().padStart(2, '0')}`;
      return { isOpen: false, statusText: 'مغلق', nextChange: `يفتح ${openTimeStr}` };
    } else {
      return { isOpen: false, statusText: 'مغلق', nextChange: 'يفتح غداً' };
    }
  }
};

// Skeleton Card Component - بطاقة تحميل متحركة
const SkeletonCard = () => (
  <Card className="overflow-hidden border-2 border-amber-200 rounded-xl shadow-md animate-pulse">
    {/* صورة placeholder */}
    <div className="relative h-32 sm:h-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer"></div>
    
    <CardContent className="p-0">
      {/* Header - اسم المطعم + حالة المطعم */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          {/* اسم المطعم */}
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
          {/* حالة المطعم */}
          <div className="flex flex-col items-center px-2 py-1 rounded-lg bg-gray-200 w-20">
            <div className="h-3 bg-gray-300 rounded w-12 mb-1"></div>
            <div className="h-2 bg-gray-300 rounded w-16"></div>
          </div>
        </div>
      </div>
      
      {/* Info Section */}
      <div className="px-4 py-3 space-y-2.5" style={{ direction: 'rtl' }}>
        {/* نوع المطبخ + أيقونات الخدمات */}
        <div className="flex items-center justify-between gap-2">
          <div className="h-6 bg-amber-100 rounded-full w-20 border border-amber-200"></div>
          <div className="flex items-center gap-1">
            <div className="w-6 h-6 bg-gray-200 rounded"></div>
            <div className="w-6 h-6 bg-gray-200 rounded"></div>
            <div className="w-6 h-6 bg-gray-200 rounded"></div>
          </div>
        </div>
        
        {/* المدينة */}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-16"></div>
        </div>
        
        {/* التقييم - 5 نجوم */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <div key={star} className="w-4 h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-4 bg-amber-100 rounded w-8"></div>
            <div className="h-3 bg-gray-200 rounded w-10"></div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Restaurant Card Component
const RestaurantCard = ({ restaurant, rank, showRank = false, onClick, priority = false }) => {
  const { user, token, favorites, setFavorites } = useAuth();
  const isFavorite = favorites.includes(restaurant.id);

  // دالة لتحديث الـ Offline Cache
  const updateOfflineCache = (action, restaurantData) => {
    try {
      const cached = localStorage.getItem('offlineFavorites');
      let offlineFavorites = cached ? JSON.parse(cached) : [];
      
      if (action === 'add') {
        // إضافة المطعم إذا غير موجود
        if (!offlineFavorites.find(r => r.id === restaurantData.id)) {
          offlineFavorites.push(restaurantData);
        }
      } else if (action === 'remove') {
        // حذف المطعم
        offlineFavorites = offlineFavorites.filter(r => r.id !== restaurantData.id);
      }
      
      localStorage.setItem('offlineFavorites', JSON.stringify(offlineFavorites));
    } catch (e) { /* تجاهل خطأ التخزين */ }
  };

  const toggleFavorite = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${restaurant.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => prev.filter(id => id !== restaurant.id));
        updateOfflineCache('remove', restaurant);
        toast.success('تم حذف المطعم من المفضلة');
      } else {
        await axios.post(`${API}/favorites/${restaurant.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavorites(prev => [...prev, restaurant.id]);
        updateOfflineCache('add', restaurant);
        toast.success('تم إضافة المطعم للمفضلة');
      }
    } catch (error) {
      toast.error('حدث خطأ في العملية');
    }
  };

  const openInMaps = (e) => {
    e.stopPropagation();
    // Use google_maps_link if available, otherwise use address
    if (restaurant.google_maps_link && restaurant.google_maps_link.trim()) {
      window.open(restaurant.google_maps_link, '_blank');
    } else if (restaurant.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`, '_blank');
    }
  };

  // Prepare images array - if restaurant has multiple images use them, otherwise use single image
  const restaurantImages = restaurant.images && restaurant.images.length > 0 
    ? restaurant.images 
    : [restaurant.image];

  return (
    <Card 
      className="cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden border-2 border-amber-200 hover:border-amber-400 rounded-xl shadow-md shadow-amber-100/50"
      onClick={onClick}
    >
      {/* تصميم أفقي - الصورة على اليسار والمعلومات على اليمين */}
      <div className="flex flex-row-reverse" style={{ direction: 'rtl' }}>
        {/* الصورة على اليسار - تتمدد لملء ارتفاع البطاقة */}
        <div className="relative w-[7.5rem] sm:w-36 flex-shrink-0 self-stretch min-h-[8rem]">
          <ImageGallery 
            images={restaurantImages} 
            altText={restaurant.name_ar}
            priority={priority}
          />
          {/* أيقونة القلب على الجانب الأيسر من الصورة */}
          <button
            onClick={toggleFavorite}
            className={`absolute top-2 left-2 p-1.5 rounded-full shadow-md z-10 transition-colors ${
              isFavorite 
                ? 'bg-red-500 text-white' 
                : 'bg-white/90 text-gray-400 hover:text-red-500'
            }`}
          >
            <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
          {/* شعار جديد على الجانب الأيمن من الصورة */}
          {isRestaurantNew(restaurant) && (
            <div className="absolute top-2 right-2 z-10">
              <Badge className="bg-amber-400/90 text-white shadow-md text-[10px] px-1.5 py-0.5">
                جديد
              </Badge>
            </div>
          )}
          {/* شعار خصم */}
          {restaurant.has_coupon && (
            <div className="absolute bottom-2 right-2 z-10">
              <Badge className="bg-orange-500/90 text-white animate-pulse shadow-md text-[10px] px-1.5 py-0.5">
                <Ticket className="h-2.5 w-2.5 mr-0.5" />
                خصم
              </Badge>
            </div>
          )}
          {/* Ranking Badge */}
          {showRank && rank && (
            <div className="absolute bottom-2 left-2 bg-gradient-to-br from-amber-500 to-orange-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-lg border-2 border-white z-10">
              {rank}
            </div>
          )}
        </div>
        
        {/* المعلومات على اليمين */}
        <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
          {/* السطر الأول: اسم المطعم بالعربي */}
          <h3 className="font-bold text-[15px] text-amber-900 leading-tight truncate">{restaurant.name_ar}</h3>
          
          {/* السطر الثاني: اسم المطعم بالأجنبية */}
          {restaurant.name && (
            <p className="text-xs text-gray-400 truncate mt-0.5">{restaurant.name}</p>
          )}
          
          {/* السطر الثالث: نوع المطبخ + المدينة + حالة مفتوح/مغلق */}
          <div className="flex items-center gap-1.5 mt-1.5">
            {restaurant.cuisine_type_ar && (
              <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-300">
                {restaurant.cuisine_type_ar}
              </span>
            )}
            <div className="flex items-center gap-0.5 text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-300">
              <MapPin className="h-3 w-3 text-blue-500" />
              <span className="text-[11px] font-semibold">{restaurant.city}</span>
            </div>
            {/* حالة المطعم */}
            {(() => {
              const status = getRestaurantStatus(restaurant.opening_hours);
              if (status.isOpen === null) return null;
              return (
                <div className={`flex flex-col items-center px-2 py-0.5 rounded-md text-[11px] ${
                  status.isOpen 
                    ? 'bg-green-100 text-green-700 border border-green-300' 
                    : 'bg-red-100 text-red-700 border border-red-300'
                }`}>
                  <div className="flex items-center gap-0.5">
                    <div className={`w-1.5 h-1.5 rounded-full ${status.isOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <span className="font-bold">{status.statusText}</span>
                  </div>
                  {status.nextChange && (
                    <span className="text-[9px] opacity-75">{status.nextChange}</span>
                  )}
                </div>
              );
            })()}
          </div>
          
          {/* السطر الرابع: التقييم + أيقونة الخريطة + أيقونات الخدمات */}
          <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100">
            {/* التقييم */}
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star}
                  className={`h-3.5 w-3.5 ${
                    star <= Math.round(restaurant.average_rating || 0)
                      ? 'text-amber-400 fill-current'
                      : 'text-gray-300'
                  }`}
                />
              ))}
              {restaurant.total_ratings > 0 && (
                <span className="text-[11px] text-gray-500 mr-1">({restaurant.total_ratings})</span>
              )}
            </div>
            
            {/* أيقونة الخريطة */}
            <button
              onClick={openInMaps}
              className="p-1 rounded-full text-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Navigation className="h-4 w-4" />
            </button>
            
            {/* أيقونات الخدمات */}
            {restaurant.service_icons && restaurant.service_icons.length > 0 && (() => {
              const filteredServices = restaurant.service_icons.filter(service => {
                const serviceId = (service.id || service.type || '').toLowerCase();
                const serviceName = (service.name || service.name_ar || '').toLowerCase();
                return (
                  serviceId.includes('delivery') || serviceName.includes('توصيل') ||
                  serviceId.includes('takeaway') || serviceName.includes('خارجي') || serviceName.includes('أخذ') ||
                  serviceId.includes('dine') || serviceName.includes('جلوس') || serviceName.includes('تناول')
                );
              }).slice(0, 3);
              
              if (filteredServices.length === 0) return null;
              
              return (
                <div className="flex items-center gap-0.5">
                  {filteredServices.map((service, idx) => {
                    const serviceId = (service.id || service.type || '').toLowerCase();
                    const serviceName = (service.name || service.name_ar || '').toLowerCase();
                    const isActive = service.active !== false;
                    
                    let Icon = null;
                    if (serviceId.includes('delivery') || serviceName.includes('توصيل')) {
                      Icon = Car;
                    } else if (serviceId.includes('takeaway') || serviceName.includes('خارجي') || serviceName.includes('أخذ')) {
                      Icon = ShoppingBag;
                    } else if (serviceId.includes('dine') || serviceName.includes('جلوس') || serviceName.includes('تناول')) {
                      Icon = UtensilsCrossed;
                    }
                    
                    if (!Icon) return null;
                    
                    return (
                      <div 
                        key={idx}
                        className={`p-0.5 rounded ${isActive ? 'bg-green-100' : 'bg-gray-200'}`}
                      >
                        <Icon className={`h-3 w-3 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            
            {/* المسافة */}
            {restaurant.distance && restaurant.distance < 999 && (
              <span className="text-[10px] text-blue-600 font-bold">
                {restaurant.distance.toFixed(1)} كم
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

// Restaurant Detail Component
const RestaurantDetail = ({ restaurant, onBack, onEdit, onDelete }) => {
  const [restaurantRatings, setRestaurantRatings] = useState([]);
  const [loadingRatings, setLoadingRatings] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const { user, token } = useAuth();
  
  // قوائم المستخدم (جربته / أريد زيارته)
  const [listStatus, setListStatus] = useState({ tried: false, want_to_visit: false });
  const [loadingList, setLoadingList] = useState(false);
  
  // الإعجابات والتعليقات على التقييمات
  const [likedRatings, setLikedRatings] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [ratingComments, setRatingComments] = useState({});
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    // Scroll to top instantly when restaurant detail page opens (no animation)
    window.scrollTo(0, 0);
    
    fetchRatings();
    // Record restaurant view
    recordView();
    // جلب حالة القوائم إذا كان المستخدم مسجل الدخول
    if (user && token) {
      fetchListStatus();
    }
  }, [restaurant.id]);

  const fetchRatings = async () => {
    try {
      const response = await axios.get(`${API}/restaurants/${restaurant.id}/ratings`);
      setRestaurantRatings(response.data);
      
      // جلب حالة الإعجابات للتقييمات إذا كان المستخدم مسجل الدخول
      if (user && token) {
        const likesStatus = {};
        for (const rating of response.data) {
          try {
            const likeRes = await axios.get(`${API}/ratings/${rating.id}/liked`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            likesStatus[rating.id] = likeRes.data;
          } catch (e) {
            likesStatus[rating.id] = { liked: false, likes_count: rating.likes_count || 0 };
          }
        }
        setLikedRatings(likesStatus);
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
    } finally {
      setLoadingRatings(false);
    }
  };

  // جلب حالة القوائم
  const fetchListStatus = async () => {
    try {
      const response = await axios.get(`${API}/lists/restaurant/${restaurant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListStatus(response.data);
    } catch (error) {
      console.error('Error fetching list status:', error);
    }
  };
  
  // إضافة/إزالة من القوائم
  const toggleList = async (listType) => {
    if (!user || !token) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }
    
    setLoadingList(true);
    try {
      if (listStatus[listType]) {
        // إزالة من القائمة
        await axios.delete(`${API}/lists/${restaurant.id}/${listType}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setListStatus(prev => ({ ...prev, [listType]: false }));
        toast.success(listType === 'tried' ? 'تم الحذف من "جربته"' : 'تم الحذف من "أريد زيارته"');
      } else {
        // إضافة للقائمة
        await axios.post(`${API}/lists`, {
          restaurant_id: restaurant.id,
          list_type: listType
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // إذا أضاف لـ "جربته"، يتم حذفه من "أريد زيارته" تلقائياً
        if (listType === 'tried') {
          setListStatus({ tried: true, want_to_visit: false });
        } else {
          setListStatus(prev => ({ ...prev, [listType]: true }));
        }
        toast.success(listType === 'tried' ? 'تمت الإضافة إلى "جربته"' : 'تمت الإضافة إلى "أريد زيارته"');
      }
    } catch (error) {
      toast.error('حدث خطأ');
      console.error('Error toggling list:', error);
    } finally {
      setLoadingList(false);
    }
  };
  
  // إعجاب/إلغاء إعجاب بتقييم
  const toggleLike = async (ratingId) => {
    if (!user || !token) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/ratings/${ratingId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLikedRatings(prev => ({
        ...prev,
        [ratingId]: { liked: response.data.liked, likes_count: response.data.likes_count }
      }));
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };
  
  // جلب تعليقات تقييم
  const fetchComments = async (ratingId) => {
    try {
      const response = await axios.get(`${API}/ratings/${ratingId}/comments`);
      setRatingComments(prev => ({ ...prev, [ratingId]: response.data }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };
  
  // تبديل عرض التعليقات
  const toggleComments = async (ratingId) => {
    if (expandedComments[ratingId]) {
      setExpandedComments(prev => ({ ...prev, [ratingId]: false }));
    } else {
      await fetchComments(ratingId);
      setExpandedComments(prev => ({ ...prev, [ratingId]: true }));
    }
  };
  
  // إضافة تعليق
  const submitComment = async (ratingId) => {
    if (!user || !token) {
      toast.error('يجب تسجيل الدخول أولاً');
      return;
    }
    
    if (!newComment.trim()) {
      toast.error('يرجى كتابة تعليق');
      return;
    }
    
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/ratings/${ratingId}/comments`, {
        content: newComment.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewComment('');
      await fetchComments(ratingId);
      toast.success('تم إضافة التعليق');
    } catch (error) {
      toast.error('حدث خطأ');
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const recordView = async () => {
    try {
      await axios.post(`${API}/restaurants/${restaurant.id}/view`);
    } catch (error) {
      console.error('Error recording view:', error);
    }
  };

  const openInMaps = () => {
    // Use google_maps_link if available, otherwise use address
    if (restaurant.google_maps_link && restaurant.google_maps_link.trim()) {
      window.open(restaurant.google_maps_link, '_blank');
    } else if (restaurant.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`, '_blank');
    }
  };

  // دوال التنقل بين الصور
  const nextImage = () => {
    if (restaurant.images && restaurant.images.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % restaurant.images.length);
    }
  };

  const previousImage = () => {
    if (restaurant.images && restaurant.images.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + restaurant.images.length) % restaurant.images.length);
    }
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
    resetZoom();
  };

  // دوال التكبير والتصغير
  const resetZoom = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleImageDoubleClick = () => {
    if (imageScale === 1) {
      setImageScale(2);
    } else {
      resetZoom();
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setImageScale(prev => Math.max(1, Math.min(4, prev + delta)));
  };

  const handleMouseDown = (e) => {
    if (imageScale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && imageScale > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch Events for Mobile - Dragging
  const [touchStartPos, setTouchStartPos] = useState({ x: 0, y: 0 });
  const [lastTouchPos, setLastTouchPos] = useState({ x: 0, y: 0 });
  
  const handleTouchStart = (e) => {
    if (imageScale > 1 && e.touches.length === 1) {
      const touch = e.touches[0];
      setTouchStartPos({ x: touch.clientX, y: touch.clientY });
      setLastTouchPos({ x: touch.clientX, y: touch.clientY });
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && imageScale > 1 && e.touches.length === 1) {
      e.preventDefault(); // Prevent scrolling
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastTouchPos.x;
      const deltaY = touch.clientY - lastTouchPos.y;
      
      setImagePosition(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastTouchPos({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Pinch to Zoom for Mobile
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialScale, setInitialScale] = useState(1);

  const getPinchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStartPinch = (e) => {
    if (e.touches.length === 2) {
      const distance = getPinchDistance(e.touches[0], e.touches[1]);
      setInitialPinchDistance(distance);
      setInitialScale(imageScale);
    } else if (e.touches.length === 1 && imageScale > 1) {
      handleTouchStart(e);
    }
  };

  const handleTouchMovePinch = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getPinchDistance(e.touches[0], e.touches[1]);
      const scale = (distance / initialPinchDistance) * initialScale;
      setImageScale(Math.max(1, Math.min(4, scale)));
    } else if (e.touches.length === 1) {
      handleTouchMove(e);
    }
  };

  // إضافة دعم السحب على الشاشات اللمسية للـ carousel (swipe between images)
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleCarouselTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleCarouselTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleCarouselTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      // سحب لليسار - الصورة التالية
      nextImage();
    }
    if (touchStart - touchEnd < -75) {
      // سحب لليمين - الصورة السابقة
      previousImage();
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" style={{ direction: 'rtl' }}>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        {/* زر أريد زيارته فقط */}
        {user && (
          <div className="flex gap-2">
            <Button
              variant={listStatus.want_to_visit || listStatus.tried ? "default" : "outline"}
              size="sm"
              onClick={() => toggleList('want_to_visit')}
              disabled={loadingList || listStatus.tried}
              className={listStatus.tried 
                ? "bg-green-600 text-white cursor-default"
                : listStatus.want_to_visit 
                  ? "bg-blue-600 hover:bg-blue-700 text-white" 
                  : "border-blue-500 text-blue-600 hover:bg-blue-50"}
              data-testid="want-to-visit-button"
            >
              {listStatus.tried ? (
                <>
                  <BookmarkCheck className="h-4 w-4 ml-1" />
                  قمت بزيارته ✓
                </>
              ) : listStatus.want_to_visit ? (
                <>
                  <BookmarkCheck className="h-4 w-4 ml-1" />
                  في قائمتي
                </>
              ) : (
                <>
                  <List className="h-4 w-4 ml-1" />
                  أريد زيارته
                </>
              )}
            </Button>
          </div>
        )}
        
        {/* Admin Edit Button */}
        {(user?.role === 'admin' || user?.is_admin) && onEdit && (
          <Button 
            variant="outline" 
            className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300"
            onClick={() => onEdit(restaurant)}
          >
            <span className="ml-2">✏️</span>
            تعديل المطعم
          </Button>
        )}
      </div>
      
      {/* Restaurant Images Carousel */}
      {restaurant.images && restaurant.images.length > 0 && (
        <div className="relative w-full bg-gray-100 rounded-xl overflow-hidden shadow-lg" style={{ direction: 'ltr' }}>
          {/* الصورة الحالية */}
          <div 
            className="relative h-64 sm:h-80 md:h-96 cursor-pointer"
            onTouchStart={handleCarouselTouchStart}
            onTouchMove={handleCarouselTouchMove}
            onTouchEnd={handleCarouselTouchEnd}
            onClick={() => setShowImageModal(true)}
          >
            <img
              src={getOptimizedImageUrl(restaurant.images[currentImageIndex], 'medium')}
              alt={`${restaurant.name_ar} ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            
            {/* تأثير تدرج في الأسفل للنقاط */}
            <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/50 to-transparent"></div>
            
            {/* عداد الصور */}
            {restaurant.images.length > 1 && (
              <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
                {currentImageIndex + 1} / {restaurant.images.length}
              </div>
            )}
          </div>
          
          {/* أسهم التنقل - تظهر فقط إذا كان هناك أكثر من صورة */}
          {restaurant.images.length > 1 && (
            <>
              {/* سهم اليسار (التالي في RTL) */}
              <button
                onClick={nextImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 sm:p-3 shadow-lg transition-all hover:scale-110 z-10"
                aria-label="الصورة التالية"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              
              {/* سهم اليمين (السابق في RTL) */}
              <button
                onClick={previousImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 sm:p-3 shadow-lg transition-all hover:scale-110 z-10"
                aria-label="الصورة السابقة"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
              
              {/* نقاط المؤشرات */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {restaurant.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`transition-all ${
                      index === currentImageIndex
                        ? 'w-8 h-2 bg-white'
                        : 'w-2 h-2 bg-white/50 hover:bg-white/75'
                    } rounded-full`}
                    aria-label={`الذهاب إلى الصورة ${index + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Image Modal - عرض الصورة بالحجم الكامل مع التكبير */}
      {showImageModal && restaurant.images && restaurant.images.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={() => { setShowImageModal(false); resetZoom(); }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onTouchMove={handleTouchMovePinch}
          onTouchEnd={handleTouchEnd}
        >
          <button
            onClick={() => { setShowImageModal(false); resetZoom(); }}
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-2 z-[60]"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* معلومات التكبير */}
          {imageScale > 1 && (
            <div className="absolute top-4 left-4 bg-black/60 text-white px-3 py-2 rounded-lg z-[60] text-sm">
              تكبير: {Math.round(imageScale * 100)}%
            </div>
          )}
          
          {/* نصيحة للمستخدم */}
          {imageScale > 1 && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-2 rounded-lg z-[60] text-xs">
              اسحب الصورة لمشاهدة التفاصيل 👆
            </div>
          )}
          
          <div 
            className="relative w-full h-full flex items-center justify-center p-4 overflow-hidden touch-none"
            onClick={(e) => e.stopPropagation()}
            onWheel={handleWheel}
          >
            <img
              src={getOptimizedImageUrl(restaurant.images[currentImageIndex], 'full')}
              alt={`${restaurant.name_ar} ${currentImageIndex + 1}`}
              className="max-w-full max-h-[calc(100vh-8rem)] object-contain"
              style={{
                transform: `scale(${imageScale}) translate(${imagePosition.x / imageScale}px, ${imagePosition.y / imageScale}px)`,
                cursor: imageScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
                userSelect: 'none',
                touchAction: 'none',
                transition: isDragging ? 'none' : 'transform 0.2s'
              }}
              onDoubleClick={handleImageDoubleClick}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStartPinch}
              draggable={false}
            />
            
            {/* أسهم التنقل في الـ modal */}
            {restaurant.images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); previousImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-[60]"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-[60]"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
              </>
            )}
            
            {/* عداد الصور في الـ modal */}
            {restaurant.images.length > 1 && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full z-[60]">
                {currentImageIndex + 1} / {restaurant.images.length}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Restaurant Info */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
          <div className="space-y-3">
            {/* اسم المطعم - عربي وإنجليزي */}
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1">
                <CardTitle className="text-3xl font-bold text-amber-900 mb-1">
                  {restaurant.name_ar}
                </CardTitle>
                <CardDescription className="text-lg text-gray-600 font-medium">
                  {restaurant.name}
                </CardDescription>
              </div>
              {/* زر الكوبون */}
              {restaurant.has_coupon && (
                <CouponDialog restaurantId={restaurant.id} restaurantName={restaurant.name_ar} />
              )}
            </div>
            
            {/* نوع المطبخ، زر المشاركة، والتقييم - في سطر واحد */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {/* نوع المطبخ - مميز */}
              {restaurant.cuisine_type_ar && (
                <div className="inline-flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full border border-amber-200 shadow-sm">
                  <Utensils className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">
                    {restaurant.cuisine_type_ar}
                  </span>
                  {restaurant.cuisine_type && (
                    <span className="text-sm text-gray-500">
                      • {restaurant.cuisine_type}
                    </span>
                  )}
                </div>
              )}
              
              <div className="flex items-center gap-3">
                {/* زر المشاركة */}
                <ShareRestaurant restaurant={restaurant} />
                
                {/* التقييم */}
                {restaurant.average_rating > 0 && (
                  <Badge className="bg-amber-400/80 hover:bg-amber-500/80 text-white text-sm font-normal px-3 py-1.5 shadow-sm">
                    <Star className="h-4 w-4 ml-1.5 fill-current" />
                    {restaurant.average_rating}/5
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6 pt-6">
          {/* الوصف */}
          {restaurant.description_ar && (
            <div className="bg-gray-50 rounded-lg p-4 border-r-4 border-amber-500">
              <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <span>📝</span>
                <span>عن المطعم</span>
              </h4>
              <p className="text-gray-700 leading-relaxed">{restaurant.description_ar}</p>
              {restaurant.description && (
                <p className="text-gray-500 mt-2 text-sm italic" dir="ltr" style={{ textAlign: 'left' }}>
                  {restaurant.description}
                </p>
              )}
            </div>
          )}
          
          {/* معلومات الاتصال والعنوان - من اليسار */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ساعات العمل - من اليمين */}
            {restaurant.opening_hours && (
              <div className="order-2 md:order-1">
                <h4 className="font-semibold mb-3 flex items-center gap-2 text-gray-900">
                  <span>🕐</span>
                  <span>ساعات العمل</span>
                </h4>
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
                  <div className="space-y-2">
                    {Object.entries(restaurant.opening_hours).map(([day, hours]) => {
                      const dayName = day === 'monday' ? 'الاثنين' : 
                                     day === 'tuesday' ? 'الثلاثاء' :
                                     day === 'wednesday' ? 'الأربعاء' :
                                     day === 'thursday' ? 'الخميس' :
                                     day === 'friday' ? 'الجمعة' :
                                     day === 'saturday' ? 'السبت' : 'الأحد';
                      
                      // التعامل مع أنواع مختلفة من البيانات
                      let displayHours = hours;
                      if (typeof hours === 'object' && hours !== null) {
                        displayHours = hours.open && hours.close 
                          ? `${hours.open}-${hours.close}` 
                          : hours.hours || 'غير محدد';
                      }
                      const isClosed = displayHours === 'closed' || !displayHours;
                      
                      return (
                        <div 
                          key={day} 
                          className={`flex items-center justify-between px-3 py-2 rounded-md ${
                            isClosed ? 'bg-gray-100 text-gray-500' : 'bg-white text-gray-800'
                          } shadow-sm`}
                        >
                          <span className="font-medium text-sm w-20">{dayName}</span>
                          <span className={`text-sm font-semibold ${isClosed ? 'text-red-600' : 'text-green-700'}`} dir="ltr">
                            {isClosed ? '🔴 مغلق' : `🟢 ${displayHours}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* معلومات الاتصال - العنوان والهاتف */}
            <div className="order-1 md:order-2" dir="ltr">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2" dir="rtl">
                <span>📞</span>
                <span>معلومات الاتصال</span>
              </h4>
              
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 border border-blue-200 shadow-sm">
                {/* Grid محاذاة مثالية للأيقونات والمحتوى */}
                <div className="grid grid-cols-[40px_auto_1fr] gap-x-2 gap-y-3" dir="rtl">
                  {/* الهاتف - أولاً */}
                  {restaurant.phone && (
                    <>
                      <a 
                        href={`tel:${restaurant.phone}`}
                        className="flex justify-end self-center cursor-pointer"
                        title="اضغط للاتصال"
                      >
                        <div className="bg-green-500 w-7 h-7 rounded-md shadow-sm flex items-center justify-center hover:bg-green-600 transition-colors">
                          <Phone className="h-4 w-4 text-white flex-shrink-0" />
                        </div>
                      </a>
                      <a 
                        href={`tel:${restaurant.phone}`}
                        className="text-base font-bold text-gray-900 self-center hover:text-green-700 transition-colors cursor-pointer"
                      >
                        اتصل
                      </a>
                      <a 
                        href={`tel:${restaurant.phone}`}
                        className="text-base font-bold text-gray-800 p-2 hover:bg-white/50 rounded-lg transition-all self-center"
                        dir="ltr"
                        style={{ textAlign: 'left' }}
                      >
                        {restaurant.phone}
                      </a>
                    </>
                  )}
                  
                  {/* خط فاصل */}
                  {restaurant.phone && (
                    <div className="col-span-3 border-t border-blue-200"></div>
                  )}
                  
                  {/* العنوان - ثانياً */}
                  <div 
                    className="flex justify-end self-center cursor-pointer"
                    onClick={() => {
                      const addressText = `${restaurant.street_number || ''}\n${restaurant.postal_code || ''} ${restaurant.city || ''}, ${restaurant.country || ''}`.trim();
                      navigator.clipboard.writeText(addressText);
                      toast.success('تم نسخ العنوان', { duration: 2000 });
                    }}
                    title="اضغط لنسخ العنوان"
                  >
                    <div className="bg-blue-500 w-7 h-7 rounded-md shadow-sm flex items-center justify-center hover:bg-blue-600 transition-colors">
                      <MapPin className="h-4 w-4 text-white flex-shrink-0" />
                    </div>
                  </div>
                  <span 
                    className="text-base font-bold text-blue-900 self-center cursor-pointer hover:text-blue-700 transition-colors"
                    onClick={() => {
                      const addressText = `${restaurant.street_number || ''}\n${restaurant.postal_code || ''} ${restaurant.city || ''}, ${restaurant.country || ''}`.trim();
                      navigator.clipboard.writeText(addressText);
                      toast.success('تم نسخ العنوان', { duration: 2000 });
                    }}
                  >
                    العنوان
                  </span>
                  <div 
                    className="cursor-pointer hover:bg-white/50 p-2 rounded-lg transition-all group"
                    onClick={() => {
                      const addressText = `${restaurant.street_number || ''}\n${restaurant.postal_code || ''} ${restaurant.city || ''}, ${restaurant.country || ''}`.trim();
                      navigator.clipboard.writeText(addressText);
                      toast.success('تم نسخ العنوان', { duration: 2000 });
                    }}
                    title="اضغط لنسخ العنوان"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                  >
                    {restaurant.street_number && (
                      <div className="text-lg font-bold text-gray-900 mb-0.5">
                        {restaurant.street_number}
                      </div>
                    )}
                    {(restaurant.postal_code || restaurant.city || restaurant.country) && (
                      <div className="text-base font-medium text-gray-700">
                        {restaurant.postal_code} {restaurant.city}, {restaurant.country}
                      </div>
                    )}
                    <div className="text-xs text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity mt-1" dir="rtl">
                      📋 اضغط للنسخ
                    </div>
                  </div>
                </div>
                
                {/* زر خرائط جوجل */}
                <Button 
                  onClick={openInMaps}
                  className="w-full mt-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md text-base"
                >
                  <Navigation className="h-4 w-4 mr-2" />
                  فتح في خرائط جوجل
                </Button>
              </div>
            </div>
          </div>
          
          {/* Menu Items */}
          {restaurant.menu_items && restaurant.menu_items.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <h4 className="font-semibold mb-3 text-gray-900">أشهر الأطباق:</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {restaurant.menu_items.map((item, index) => (
                  <Badge key={index} variant="outline" className="justify-center p-2">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Service Icons Section */}
          {restaurant.service_icons && restaurant.service_icons.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <h4 className="font-semibold mb-3 text-gray-900">الخدمات المتوفرة:</h4>
              <div className="flex flex-wrap gap-3">
                {restaurant.service_icons.map((service, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg border border-blue-100"
                    title={service.name}
                  >
                    <span className="text-2xl">{service.icon}</span>
                    <span className="text-sm font-medium text-gray-800">{service.name}</span>
                  </div>
                ))}
              </div>
              {/* نص إضافي للخدمات */}
              {restaurant.services_note && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border-r-4 border-blue-500">
                  <p className="text-sm text-gray-700 italic">{restaurant.services_note}</p>
                </div>
              )}
            </div>
          )}
          
          {/* قسم تواصل معنا - الأيقونات الاجتماعية */}
          {(restaurant.website || restaurant.email || restaurant.facebook || restaurant.instagram || restaurant.tiktok) && (
            <div className="pt-6 border-t border-gray-200">
              <h4 className="font-semibold mb-4 text-gray-900 text-lg">تواصل معنا</h4>
              <div className="flex flex-wrap gap-2 sm:gap-4">
                {/* أيقونة الموقع الإلكتروني */}
                {restaurant.website && (
                  <a 
                    href={restaurant.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white rounded-2xl transition-all duration-300 hover:scale-110 hover:rotate-3 shadow-lg hover:shadow-xl"
                    title="زيارة الموقع الإلكتروني"
                  >
                    <Globe className="h-6 w-6 sm:h-7 sm:w-7 relative z-10" />
                    <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </a>
                )}
                
                {/* أيقونة البريد الإلكتروني */}
                {restaurant.email && (
                  <a 
                    href={`mailto:${restaurant.email}`}
                    className="group relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white rounded-2xl transition-all duration-300 hover:scale-110 hover:rotate-3 shadow-lg hover:shadow-xl"
                    title={`راسلنا: ${restaurant.email}`}
                  >
                    <Mail className="h-6 w-6 sm:h-7 sm:w-7 relative z-10" />
                    <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </a>
                )}
                
                {/* أيقونة فيسبوك */}
                {restaurant.facebook && (
                  <a 
                    href={restaurant.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#1877F2] to-[#0d5dca] hover:from-[#0d66d9] hover:to-[#0a4db3] text-white rounded-2xl transition-all duration-300 hover:scale-110 hover:rotate-3 shadow-lg hover:shadow-xl"
                    title="فيسبوك"
                  >
                    <Facebook className="h-6 w-6 sm:h-7 sm:w-7 relative z-10" />
                    <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </a>
                )}
                
                {/* أيقونة انستغرام */}
                {restaurant.instagram && (
                  <a 
                    href={restaurant.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white rounded-2xl transition-all duration-300 hover:scale-110 hover:rotate-3 shadow-lg hover:shadow-xl"
                    title="انستغرام"
                  >
                    <Instagram className="h-6 w-6 sm:h-7 sm:w-7 relative z-10" />
                    <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </a>
                )}
                
                {/* أيقونة تيك توك */}
                {restaurant.tiktok && (
                  <a 
                    href={restaurant.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-gray-900 to-gray-700 hover:from-black hover:to-gray-800 text-white rounded-2xl transition-all duration-300 hover:scale-110 hover:rotate-3 shadow-lg hover:shadow-xl"
                    title="تيك توك"
                  >
                    <svg className="h-6 w-6 sm:h-7 sm:w-7 relative z-10" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.04-.1z"/>
                    </svg>
                    <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </a>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Ratings Section */}
      <Tabs defaultValue="ratings" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ratings">التقييمات</TabsTrigger>
          <TabsTrigger value="add-rating">إضافة تقييم</TabsTrigger>
        </TabsList>
        
        <TabsContent value="ratings" className="space-y-4">
          {loadingRatings ? (
            <div className="text-center p-4">جاري تحميل التقييمات...</div>
          ) : restaurantRatings.length === 0 ? (
            <div className="text-center p-4 text-gray-500">لا توجد تقييمات بعد</div>
          ) : (
            <div className="space-y-4">
              {restaurantRatings.map((rating) => (
                <Card key={rating.id} data-testid={`rating-card-${rating.id}`}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {/* معلومات المستخدم والتقييمات */}
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <span className="font-semibold text-gray-900">{rating.user_name || rating.username || 'مستخدم'}</span>
                        <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
                          <Badge variant="outline" className="bg-blue-50">خدمة: {rating.service_rating}/5</Badge>
                          <Badge variant="outline" className="bg-green-50">سعر: {rating.price_rating}/5</Badge>
                          <Badge variant="outline" className="bg-orange-50">طعم: {rating.taste_rating}/5</Badge>
                          <Badge variant="outline" className="bg-purple-50">نظافة: {rating.cleanliness_rating || 'N/A'}/5</Badge>
                        </div>
                      </div>
                      
                      {/* تعليق المستخدم */}
                      {rating.comment && (
                        <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-lg">{rating.comment}</p>
                      )}
                      
                      {/* أزرار الإعجاب والتعليقات */}
                      <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
                        {/* زر الإعجاب */}
                        <button
                          onClick={() => toggleLike(rating.id)}
                          className={`flex items-center gap-1 text-sm transition-colors ${
                            likedRatings[rating.id]?.liked 
                              ? 'text-red-500' 
                              : 'text-gray-500 hover:text-red-500'
                          }`}
                          data-testid={`like-button-${rating.id}`}
                        >
                          <ThumbsUp className={`h-4 w-4 ${likedRatings[rating.id]?.liked ? 'fill-current' : ''}`} />
                          <span>{likedRatings[rating.id]?.likes_count || rating.likes_count || 0}</span>
                        </button>
                        
                        {/* زر التعليقات */}
                        <button
                          onClick={() => toggleComments(rating.id)}
                          className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-500 transition-colors"
                          data-testid={`comments-button-${rating.id}`}
                        >
                          <MessageCircle className="h-4 w-4" />
                          <span>{rating.comments_count || 0} تعليق</span>
                        </button>
                      </div>
                      
                      {/* قسم التعليقات (يظهر عند الضغط) */}
                      {expandedComments[rating.id] && (
                        <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                          {/* قائمة التعليقات */}
                          {ratingComments[rating.id]?.length > 0 ? (
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                              {ratingComments[rating.id].map((comment) => (
                                <div key={comment.id} className="bg-gray-50 p-3 rounded-lg">
                                  <div className="flex justify-between items-start">
                                    <span className="font-medium text-sm text-gray-900">{comment.user_name}</span>
                                    <span className="text-xs text-gray-400">
                                      {new Date(comment.created_at).toLocaleDateString('ar-SA')}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1">{comment.content}</p>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400 text-center">لا توجد تعليقات بعد</p>
                          )}
                          
                          {/* إضافة تعليق جديد */}
                          {user && (
                            <div className="flex gap-2">
                              <Input
                                placeholder="أضف تعليقاً..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                className="flex-1 text-sm"
                                data-testid={`comment-input-${rating.id}`}
                              />
                              <Button
                                size="sm"
                                onClick={() => submitComment(rating.id)}
                                disabled={submittingComment || !newComment.trim()}
                                className="bg-blue-600 hover:bg-blue-700"
                                data-testid={`submit-comment-${rating.id}`}
                              >
                                {submittingComment ? '...' : 'إرسال'}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="add-rating">
          <Card>
            <CardHeader>
              <CardTitle>إضافة تقييم جديد</CardTitle>
            </CardHeader>
            <CardContent>
              <RatingComponent 
                restaurantId={restaurant.id} 
                onRatingAdded={fetchRatings} 
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Interactive Map Component
const InteractiveMap = ({ restaurants }) => {
  const openInGoogleMaps = (restaurant) => {
    // Use google_maps_link if available, otherwise use address
    if (restaurant.google_maps_link && restaurant.google_maps_link.trim()) {
      window.open(restaurant.google_maps_link, '_blank');
    } else if (restaurant.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}`, '_blank');
    }
  };

  return (
    <div className="space-y-6" style={{ direction: 'rtl' }}>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">خريطة المطاعم</h2>
        <p className="text-gray-600">اضغط على مطعم لفتحه في خرائط جوجل</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {restaurants.map(restaurant => (
          <Card key={restaurant.id} className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">{restaurant.name_ar}</h3>
                    <p className="text-sm text-gray-500">{restaurant.city}</p>
                  </div>
                  <Badge variant="outline">{restaurant.cuisine_type_ar}</Badge>
                </div>
                
                <div className="text-sm text-gray-600">
                  <div className="flex items-center mb-1">
                    <MapPin className="h-4 w-4 ml-1" />
                    <span>{restaurant.address}</span>
                  </div>
                  <a 
                    href={`tel:${restaurant.phone}`}
                    className="flex items-center text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer"
                    title="اضغط للاتصال"
                  >
                    <Phone className="h-4 w-4 ml-1" />
                    <span dir="ltr">{restaurant.phone}</span>
                  </a>
                </div>
                
                <Button 
                  onClick={() => openInGoogleMaps(restaurant)}
                  className="w-full"
                  size="sm"
                >
                  <Navigation className="h-4 w-4 mr-2" />
                  فتح في خرائط جوجل
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

    </div>
  );
};

// Favorites Component
const FavoritesPage = () => {
  const { token } = useAuth();
  const [favoriteRestaurants, setFavoriteRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    // أولاً: حاول جلب من الإنترنت
    if (navigator.onLine) {
      try {
        const response = await axios.get(`${API}/favorites`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFavoriteRestaurants(response.data);
        // حفظ في localStorage للوصول Offline
        try {
          localStorage.setItem('offlineFavorites', JSON.stringify(response.data));
          localStorage.setItem('offlineFavoritesTime', Date.now().toString());
        } catch (e) { /* تجاهل خطأ التخزين */ }
      } catch (error) {
        // إذا فشل الإنترنت، جرب الـ Cache
        loadOfflineFavorites();
      } finally {
        setLoading(false);
      }
    } else {
      // لا يوجد إنترنت - استخدم البيانات المحفوظة
      loadOfflineFavorites();
      setLoading(false);
    }
  };

  const loadOfflineFavorites = () => {
    try {
      const cached = localStorage.getItem('offlineFavorites');
      if (cached) {
        setFavoriteRestaurants(JSON.parse(cached));
      }
    } catch (e) {
      setFavoriteRestaurants([]);
    }
  };

  if (selectedRestaurant) {
    return (
      <RestaurantDetail 
        restaurant={selectedRestaurant} 
        onBack={() => setSelectedRestaurant(null)}
        onDelete={(restaurantId) => {
          // Remove from favorites list
          const updated = favoriteRestaurants.filter(r => r.id !== restaurantId);
          setFavoriteRestaurants(updated);
          // تحديث الـ Cache
          try {
            localStorage.setItem('offlineFavorites', JSON.stringify(updated));
          } catch (e) { /* تجاهل */ }
        }}
      />
    );
  }

  return (
    <div className="space-y-6" style={{ direction: 'rtl' }}>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">مطاعمي المفضلة</h2>
        <p className="text-gray-600">المطاعم التي أضفتها لقائمة المفضلة</p>
        {isOffline && favoriteRestaurants.length > 0 && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">
            <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
            وضع عدم الاتصال - البيانات محفوظة محلياً
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Coffee className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600">جاري تحميل المطاعم المفضلة...</p>
        </div>
      ) : favoriteRestaurants.length === 0 ? (
        <div className="text-center py-12">
          <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg text-gray-600">لم تضف أي مطاعم للمفضلة بعد</p>
          <p className="text-sm text-gray-500 mt-2">ابحث عن مطاعم وأضفها للمفضلة لتظهر هنا</p>
          {isOffline && (
            <p className="text-sm text-amber-600 mt-2">أنت غير متصل بالإنترنت</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {favoriteRestaurants.map(restaurant => (
            <RestaurantCard
              key={restaurant.id}
              restaurant={restaurant}
              onClick={() => setSelectedRestaurant(restaurant)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// صفحة أريد زيارتها (Want to Visit Page)
const WantToVisitPage = () => {
  const { token, user } = useAuth();
  const [wantToVisit, setWantToVisit] = useState([]);
  const [visited, setVisited] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [movingId, setMovingId] = useState(null);

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const response = await axios.get(`${API}/lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWantToVisit(response.data.want_to_visit || []);
      setVisited(response.data.tried || []);
    } catch (error) {
      console.error('Error fetching lists:', error);
      toast.error('حدث خطأ في تحميل القوائم');
    } finally {
      setLoading(false);
    }
  };

  // نقل المطعم من "أريد زيارتها" إلى "قمت بزيارتها"
  const markAsVisited = async (restaurantId) => {
    setMovingId(restaurantId);
    try {
      // حذف من want_to_visit
      await axios.delete(`${API}/lists/${restaurantId}/want_to_visit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // إضافة إلى tried
      await axios.post(`${API}/lists`, {
        restaurant_id: restaurantId,
        list_type: 'tried'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // تحديث الحالة محلياً
      const movedItem = wantToVisit.find(item => item.restaurant.id === restaurantId);
      if (movedItem) {
        setWantToVisit(prev => prev.filter(item => item.restaurant.id !== restaurantId));
        setVisited(prev => [...prev, { ...movedItem, list_id: Date.now().toString() }]);
      }
      
      toast.success('تم نقل المطعم إلى "قمت بزيارتها"');
    } catch (error) {
      console.error('Error moving restaurant:', error);
      toast.error('حدث خطأ');
    } finally {
      setMovingId(null);
    }
  };

  // حذف من القائمة
  const removeFromList = async (restaurantId, listType) => {
    try {
      await axios.delete(`${API}/lists/${restaurantId}/${listType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (listType === 'want_to_visit') {
        setWantToVisit(prev => prev.filter(item => item.restaurant.id !== restaurantId));
      } else {
        setVisited(prev => prev.filter(item => item.restaurant.id !== restaurantId));
      }
      
      toast.success('تم الحذف من القائمة');
    } catch (error) {
      console.error('Error removing from list:', error);
      toast.error('حدث خطأ');
    }
  };

  if (selectedRestaurant) {
    return (
      <RestaurantDetail 
        restaurant={selectedRestaurant} 
        onBack={() => {
          setSelectedRestaurant(null);
          fetchLists(); // تحديث القوائم عند العودة
        }}
      />
    );
  }

  return (
    <div className="space-y-6" style={{ direction: 'rtl' }}>
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">📋 أريد زيارتها</h2>
        <p className="text-gray-600">المطاعم التي تخطط لزيارتها</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Coffee className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600">جاري تحميل القوائم...</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* قسم: أريد زيارتها */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-blue-200">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <h3 className="text-lg font-bold text-blue-700">أريد زيارتها ({wantToVisit.length})</h3>
            </div>
            
            {wantToVisit.length === 0 ? (
              <div className="text-center py-8 bg-blue-50 rounded-lg">
                <List className="h-10 w-10 text-blue-300 mx-auto mb-2" />
                <p className="text-gray-500">لا توجد مطاعم في هذه القائمة</p>
                <p className="text-sm text-gray-400 mt-1">أضف مطاعم من صفحة تفاصيل المطعم</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {wantToVisit.map(item => (
                  <Card key={item.list_id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="flex">
                      {/* صورة المطعم */}
                      <div 
                        className="w-24 h-24 flex-shrink-0 cursor-pointer"
                        onClick={() => setSelectedRestaurant(item.restaurant)}
                      >
                        {item.restaurant.images?.[0] ? (
                          <img 
                            src={item.restaurant.images[0]} 
                            alt={item.restaurant.name_ar}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <Coffee className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      
                      {/* معلومات المطعم */}
                      <div className="flex-1 p-3">
                        <h4 
                          className="font-bold text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={() => setSelectedRestaurant(item.restaurant)}
                        >
                          {item.restaurant.name_ar || item.restaurant.name}
                        </h4>
                        <p className="text-sm text-gray-500">{item.restaurant.city}</p>
                        
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            onClick={() => markAsVisited(item.restaurant.id)}
                            disabled={movingId === item.restaurant.id}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                          >
                            {movingId === item.restaurant.id ? (
                              <span className="animate-spin">⏳</span>
                            ) : (
                              <>
                                <Check className="h-3 w-3 ml-1" />
                                قمت بزيارته
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeFromList(item.restaurant.id, 'want_to_visit')}
                            className="text-red-500 border-red-300 hover:bg-red-50 text-xs"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* قسم: قمت بزيارتها */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-green-200">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <h3 className="text-lg font-bold text-green-700">قمت بزيارتها ({visited.length})</h3>
            </div>
            
            {visited.length === 0 ? (
              <div className="text-center py-8 bg-green-50 rounded-lg">
                <BookmarkCheck className="h-10 w-10 text-green-300 mx-auto mb-2" />
                <p className="text-gray-500">لم تزر أي مطعم من قائمتك بعد</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {visited.map(item => (
                  <Card key={item.list_id} className="overflow-hidden hover:shadow-lg transition-shadow bg-green-50/30">
                    <div className="flex">
                      {/* صورة المطعم */}
                      <div 
                        className="w-24 h-24 flex-shrink-0 cursor-pointer relative"
                        onClick={() => setSelectedRestaurant(item.restaurant)}
                      >
                        {item.restaurant.images?.[0] ? (
                          <img 
                            src={item.restaurant.images[0]} 
                            alt={item.restaurant.name_ar}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <Coffee className="h-8 w-8 text-gray-400" />
                          </div>
                        )}
                        {/* علامة الزيارة */}
                        <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full p-1">
                          <Check className="h-3 w-3" />
                        </div>
                      </div>
                      
                      {/* معلومات المطعم */}
                      <div className="flex-1 p-3">
                        <h4 
                          className="font-bold text-gray-900 cursor-pointer hover:text-green-600"
                          onClick={() => setSelectedRestaurant(item.restaurant)}
                        >
                          {item.restaurant.name_ar || item.restaurant.name}
                        </h4>
                        <p className="text-sm text-gray-500">{item.restaurant.city}</p>
                        <p className="text-xs text-green-600 mt-1">✓ تمت الزيارة</p>
                        
                        <div className="flex gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeFromList(item.restaurant.id, 'tried')}
                            className="text-red-500 border-red-300 hover:bg-red-50 text-xs"
                          >
                            <X className="h-3 w-3 ml-1" />
                            حذف
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Geolocation Component
const LocationPermission = ({ onLocationUpdate, userLocation, locationError }) => {
  const [requesting, setRequesting] = useState(false);

  // Function to detect if user is on mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const requestLocation = async () => {
    setRequesting(true);
    
    const isMobile = isMobileDevice();
    
    // تحقق من دعم المتصفح
    if (!navigator.geolocation) {
      const msg = isMobile 
        ? 'التطبيق لا يدعم تحديد الموقع على هذا الجهاز' 
        : 'المتصفح لا يدعم تحديد الموقع';
      toast.error(msg);
      onLocationUpdate(null, msg);
      setRequesting(false);
      return;
    }

    // تحقق من بروتوكول HTTPS
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      const msg = 'تحديد الموقع يتطلب اتصال آمن (HTTPS)';
      toast.error(msg);
      onLocationUpdate(null, msg);
      setRequesting(false);
      return;
    }

    // Show info message based on device type
    const infoMessage = isMobile
      ? 'سيطلب منك التطبيق السماح بالوصول للموقع. يرجى اختيار "السماح" 📍'
      : 'سيظهر لك المتصفح طلب السماح بالوصول للموقع. يرجى اختيار "السماح" 📍';
    
    toast.info(infoMessage, { 
      duration: 3000,
      style: {
        direction: 'rtl',
        textAlign: 'right'
      }
    });

    // Different options for mobile vs desktop
    const options = isMobile ? {
      enableHighAccuracy: true, // على الموبايل استخدم دقة عالية
      timeout: 30000, // 30 ثانية للموبايل
      maximumAge: 300000 // يمكن استخدام موقع محفوظ حتى 5 دقائق
    } : {
      enableHighAccuracy: false, // على الكمبيوتر دقة عادية
      timeout: 15000, // 15 ثانية للكمبيوتر
      maximumAge: 0 // لا تستخدم موقع محفوظ
    };

    // Add a retry mechanism for mobile
    let retryCount = 0;
    const maxRetries = isMobile ? 2 : 0; // محاولتين إضافيتين للموبايل فقط

    const attemptGetLocation = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          onLocationUpdate(location);
          toast.success('تم تحديد موقعك بنجاح');
          setRequesting(false);
        },
        (error) => {
          console.error(`خطأ في تحديد الموقع (محاولة ${retryCount + 1}):`, error);
          
          // Retry logic for mobile on timeout or unavailable errors
          if (isMobile && retryCount < maxRetries && (error.code === 2 || error.code === 3)) {
            retryCount++;
            toast.info(`جاري إعادة المحاولة... (${retryCount}/${maxRetries})`, { duration: 2000 });
            setTimeout(() => attemptGetLocation(), 1000);
            return;
          }
          
          const isMobile = isMobileDevice();
          let errorMessage = 'فشل في تحديد الموقع';
          let helpMessage = '';
          
          switch(error.code) {
            case 1: // PERMISSION_DENIED
              if (isMobile) {
                errorMessage = 'تم رفض إذن الوصول للموقع';
                helpMessage = '📱 للسماح بالوصول للموقع من التطبيق:\n\n' +
                            '🔹 Android:\n' +
                            '1. افتح إعدادات الجهاز\n' +
                            '2. اختر "التطبيقات" أو "Apps"\n' +
                            '3. ابحث عن "دليل المطاعم" أو اسم التطبيق\n' +
                            '4. اضغط على "الأذونات" أو "Permissions"\n' +
                            '5. فعّل "الموقع" أو "Location"\n' +
                            '6. أعد فتح التطبيق\n\n' +
                            '🔹 iOS:\n' +
                            '1. افتح "الإعدادات" (Settings)\n' +
                            '2. مرر للأسفل واختر التطبيق\n' +
                            '3. اضغط على "الموقع" (Location)\n' +
                            '4. اختر "أثناء استخدام التطبيق"';
              } else {
                errorMessage = 'تم رفض إذن الوصول للموقع';
                helpMessage = '💻 للسماح بالوصول للموقع من المتصفح:\n\n' +
                            '• Chrome/Edge: اضغط على أيقونة القفل 🔒 بجانب عنوان الموقع، ثم اختر "السماح"\n\n' +
                            '• Firefox: اضغط على أيقونة القفل، ثم "السماح بالوصول للموقع"\n\n' +
                            '• Safari: إعدادات Safari → الموقع → السماح';
              }
              break;
            case 2: // POSITION_UNAVAILABLE
              errorMessage = isMobile ? 'لا يمكن تحديد الموقع. تأكد من تفعيل GPS' : 'لا يمكن تحديد الموقع حالياً';
              if (isMobile) {
                helpMessage = '📱 تأكد من:\n' +
                            '• تفعيل خدمات الموقع (GPS) من إعدادات الجهاز\n' +
                            '• وجود اتصال قوي بالإنترنت أو بيانات الجوال\n' +
                            '• أنك في مكان مفتوح (ليس داخل مبنى)\n' +
                            '• إيقاف وضع الطيران\n' +
                            '• عدم استخدام VPN\n' +
                            '• إعادة تشغيل التطبيق';
              } else {
                helpMessage = '💻 تأكد من:\n' +
                            '• تفعيل خدمات الموقع على جهازك\n' +
                            '• وجود اتصال بالإنترنت\n' +
                            '• عدم استخدام VPN قد يحجب الموقع';
              }
              break;
            case 3: // TIMEOUT
              errorMessage = 'انتهت مهلة تحديد الموقع';
              helpMessage = isMobile
                ? '📱 حاول مرة أخرى. تأكد من:\n• وجود اتصال قوي بالإنترنت أو بيانات الجوال\n• تفعيل GPS\n• أنك في مكان مفتوح\n• الانتظار قليلاً ثم المحاولة مرة أخرى'
                : '💻 حاول مرة أخرى. تأكد من اتصالك بالإنترنت وتفعيل خدمات الموقع';
              break;
            default:
              errorMessage = `خطأ غير معروف: ${error.message || 'حدث خطأ'}`;
              helpMessage = isMobile
                ? '📱 حاول إعادة فتح التطبيق'
                : '💻 حاول إعادة تحميل الصفحة';
          }
          
          onLocationUpdate(null, errorMessage);
          
          // Show main error
          toast.error(errorMessage, { duration: 4000 });
          
          // Show help message after a short delay
          if (helpMessage) {
            setTimeout(() => {
              toast.info(helpMessage, { 
                duration: 10000,
                style: {
                  direction: 'rtl',
                  textAlign: 'right',
                  whiteSpace: 'pre-line',
                  fontSize: '14px'
                }
              });
            }, 500);
          }
          
          setRequesting(false);
        },
        options
      );
    };

    try {
      attemptGetLocation();
    } catch (err) {
      console.error('استثناء في تحديد الموقع:', err);
      const msg = isMobile 
        ? '📱 حدث خطأ غير متوقع. حاول إعادة فتح التطبيق'
        : '💻 حدث خطأ غير متوقع في تحديد الموقع';
      toast.error(msg);
      onLocationUpdate(null, msg);
      setRequesting(false);
    }
  };

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200" style={{ direction: 'rtl' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MapPin className="h-6 w-6 text-blue-600 ml-2" />
          <div>
            {userLocation ? (
              <div>
                <p className="font-semibold text-green-700">تم تفعيل تحديد الموقع ✓</p>
                <p className="text-sm text-gray-600">المطاعم مرتبة حسب القرب منك</p>
              </div>
            ) : (
              <div>
                <p className="font-semibold text-blue-700">فعّل تحديد الموقع</p>
                <p className="text-sm text-gray-600">لإظهار المطاعم الأقرب إليك أولاً</p>
              </div>
            )}
            {locationError && (
              <p className="text-sm text-red-600 mt-1">{locationError}</p>
            )}
          </div>
        </div>
        
        {!userLocation && (
          <Button 
            onClick={requestLocation}
            disabled={requesting}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {requesting ? 'جاري التحديد...' : 'تفعيل الموقع'}
          </Button>
        )}
        
        {userLocation && (
          <Button 
            onClick={requestLocation}
            size="sm"
            variant="outline"
            className="text-blue-600 border-blue-600"
          >
            تحديث الموقع
          </Button>
        )}
      </div>
    </div>
  );
};

// Distance calculation helper
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const d = R * c; // Distance in kilometers
  return d;
};

// Add Post Form Component (Admin Only)
const AddPostForm = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('الرجاء اختيار صورة صالحة');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('حجم الصورة يجب أن يكون أقل من 5MB');
        return;
      }

      setImageFile(file);
      
      // Convert to base64 for preview and upload
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setImage(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      toast.error('الرجاء ملء جميع الحقول المطلوبة');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/posts`, {
        title,
        content,
        image: image || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم نشر الخبر بنجاح');
      setTitle('');
      setContent('');
      setImage('');
      setImageFile(null);
      setImagePreview('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل نشر الخبر');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8" style={{ direction: 'rtl' }}>
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>نشر خبر جديد</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">العنوان *</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="عنوان الخبر"
                  required
                  style={{ direction: 'rtl' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">المحتوى *</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="اكتب محتوى الخبر هنا..."
                  required
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  style={{ direction: 'rtl' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">الصورة (اختياري)</label>
                
                <div className="space-y-2">
                  {/* Upload Button */}
                  <div className="flex gap-2">
                    <label className="flex-1 cursor-pointer">
                      <div className="bg-blue-500 hover:bg-blue-600 text-white text-center py-3 px-4 rounded-md font-medium transition-colors">
                        📷 {imagePreview ? 'تغيير الصورة' : 'تحميل صورة من الجهاز'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full rounded-lg"
                        style={{ maxHeight: '300px', objectFit: 'cover' }}
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 left-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm font-medium"
                      >
                        ✕ إزالة
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full">
                {loading ? 'جاري النشر...' : 'نشر الخبر'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Edit Post Form Component
const EditPostForm = ({ post, onSuccess, onCancel }) => {
  const [title, setTitle] = useState(post.title || '');
  const [content, setContent] = useState(post.content || '');
  const [image, setImage] = useState(post.image || '');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(post.image || '');
  const [loading, setLoading] = useState(false);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('الرجاء اختيار صورة صالحة');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('حجم الصورة يجب أن يكون أقل من 5MB');
        return;
      }

      setImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setImage(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      toast.error('الرجاء ملء جميع الحقول المطلوبة');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/posts/${post.id}`, {
        title,
        content,
        image: image || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('تم تحديث المنشور بنجاح');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل تحديث المنشور');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">العنوان *</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="عنوان الخبر"
          required
          style={{ direction: 'rtl' }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">المحتوى *</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="اكتب محتوى الخبر هنا..."
          required
          rows={8}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          style={{ direction: 'rtl' }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">الصورة (اختياري)</label>
        
        <div className="space-y-2">
          <div className="flex gap-2">
            <label className="flex-1 cursor-pointer">
              <div className="bg-blue-500 hover:bg-blue-600 text-white text-center py-3 px-4 rounded-md font-medium transition-colors">
                📷 {imagePreview ? 'تغيير الصورة' : 'تحميل صورة من الجهاز'}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>

          {imagePreview && (
            <div className="relative">
              <img 
                src={imagePreview} 
                alt="Preview" 
                className="w-full rounded-lg"
                style={{ maxHeight: '300px', objectFit: 'cover' }}
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 left-2 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-md text-sm font-medium"
              >
                ✕ إزالة
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={loading} className="flex-1">
          {loading ? 'جاري التحديث...' : 'حفظ التعديلات'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          إلغاء
        </Button>
      </div>
    </form>
  );
};

// News Page Component
const NewsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
      
      const token = localStorage.getItem('token');
      if (token) {
        const liked = new Set();
        for (const post of response.data) {
          try {
            const likeRes = await axios.get(`${API}/posts/${post.id}/liked`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (likeRes.data.liked) {
              liked.add(post.id);
            }
          } catch (e) {}
        }
        setLikedPosts(liked);
      }
    } catch (error) {
      toast.error('خطأ في تحميل الأخبار');
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (postId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('يجب تسجيل الدخول للإعجاب');
      return;
    }

    try {
      const response = await axios.post(`${API}/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newLiked = new Set(likedPosts);
      if (response.data.liked) {
        newLiked.add(postId);
      } else {
        newLiked.delete(postId);
      }
      setLikedPosts(newLiked);
      
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1 }
          : post
      ));
    } catch (error) {
      toast.error('خطأ في الإعجاب');
    }
  };

  const openComments = async (post) => {
    setSelectedPost(post);
    try {
      const response = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل التعليقات');
    }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('يجب تسجيل الدخول للتعليق');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/posts/${selectedPost.id}/comments`,
        { content: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setComments([response.data, ...comments]);
      setCommentText('');
      
      setPosts(posts.map(post => 
        post.id === selectedPost.id 
          ? { ...post, comments_count: post.comments_count + 1 }
          : post
      ));
      
      toast.success('تم إضافة التعليق');
    } catch (error) {
      toast.error('خطأ في إضافة التعليق');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنشور؟')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPosts(posts.filter(post => post.id !== postId));
      toast.success('تم حذف المنشور بنجاح');
    } catch (error) {
      toast.error('خطأ في حذف المنشور');
    }
  };

  const [editingPost, setEditingPost] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    
    return date.toLocaleDateString('ar');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-opacity-30 border-t-orange-500 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" style={{ direction: 'rtl' }}>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">آخر الأخبار</h2>
          <p className="text-gray-600">آخر الأخبار والعروض من المطاعم العربية</p>
        </div>

        {posts.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent className="pt-6">
              <span className="text-6xl mb-4 block">📰</span>
              <p className="text-lg text-gray-600">لا توجد أخبار حالياً</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {posts.map(post => {
              const isLiked = likedPosts.has(post.id);
              
              return (
                <Card key={post.id}>
                  <CardContent className="pt-6">
                    {/* Post Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold ml-3">
                          {post.author_name[0]}
                        </div>
                        <div>
                          <p className="font-semibold">{post.author_name}</p>
                          <p className="text-sm text-gray-500">{formatDate(post.created_at)}</p>
                        </div>
                      </div>
                      
                      {/* Admin Actions */}
                      {(user?.role === 'admin' || user?.is_admin) && (
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingPost(post)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <span className="ml-1">✏️</span>
                            تعديل
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deletePost(post.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <span className="ml-1">🗑️</span>
                            حذف
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Post Content */}
                    <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                    <p className="text-gray-700 mb-4 whitespace-pre-wrap">{post.content}</p>

                    {/* Post Image */}
                    {post.image && (
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full rounded-lg mb-4"
                        style={{ maxHeight: '400px', objectFit: 'cover' }}
                      />
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center gap-4 pt-4 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleLike(post.id)}
                        className="flex items-center gap-2"
                      >
                        <span className="text-xl">{isLiked ? '❤️' : '🤍'}</span>
                        <span>{post.likes_count}</span>
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openComments(post)}
                        className="flex items-center gap-2"
                      >
                        <span className="text-xl">💬</span>
                        <span>{post.comments_count}</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Comments Dialog */}
      {selectedPost && (
        <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" style={{ direction: 'rtl' }}>
            <DialogHeader>
              <DialogTitle>التعليقات</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {comments.map(comment => (
                <div key={comment.id} className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {comment.user_name[0]}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{comment.user_name}</p>
                    <p className="text-gray-700">{comment.content}</p>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(comment.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              <Input
                placeholder="اكتب تعليقك..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addComment()}
                className="flex-1"
                style={{ direction: 'rtl' }}
              />
              <Button onClick={addComment}>إرسال</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Post Dialog */}
      {editingPost && (
        <Dialog open={!!editingPost} onOpenChange={() => setEditingPost(null)}>
          <DialogContent className="max-w-2xl" style={{ direction: 'rtl' }}>
            <DialogHeader>
              <DialogTitle>تعديل المنشور</DialogTitle>
            </DialogHeader>
            
            <EditPostForm 
              post={editingPost}
              onSuccess={() => {
                setEditingPost(null);
                fetchPosts();
              }}
              onCancel={() => setEditingPost(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Main Home Component
const Home = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [minRating, setMinRating] = useState('all');
  const [hasCoupons, setHasCoupons] = useState('all');
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false); // Prevent concurrent fetches
  const [fetchError, setFetchError] = useState(null); // تتبع خطأ التحميل
  const [currentView, setCurrentView] = useState('home');
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationRequested, setLocationRequested] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [appReady, setAppReady] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [tempSearchTerm, setTempSearchTerm] = useState('');
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const { user } = useAuth();
  
  // حفظ موضع التمرير
  const scrollPositionRef = useRef(0);

  // News/Posts state
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');

  // Search state - البحث الفوري من أول حرف
  const [actualSearchTerm, setActualSearchTerm] = useState('');

  // تحديث البحث فوراً عند الكتابة
  useEffect(() => {
    setActualSearchTerm(searchTerm.trim());
  }, [searchTerm]);

  // Handle Search Function - للتوافق مع زر البحث
  const handleSearch = () => {
    // البحث يعمل تلقائياً الآن، لكن نبقي الدالة للتوافق
  };

  // Clear search and filters - show all restaurants
  const clearSearchAndFilters = () => {
    setSearchTerm('');
    setActualSearchTerm('');
    setSelectedCity('all');
    setSelectedCuisine('all');
    setMinRating('all');
    setHasCoupons('all');
    setShowFilters(false);
    setSelectedRestaurant(null); // إعادة تعيين المطعم المحدد للرجوع للقائمة
    setSelectedPost(null); // إعادة تعيين الخبر المحدد أيضاً
  };

  // معالج زر الرجوع في Android
  const stateRef = useRef({ selectedRestaurant, selectedPost, showFilters, currentView });
  
  // تحديث الـ ref عند تغير الحالة
  useEffect(() => {
    stateRef.current = { selectedRestaurant, selectedPost, showFilters, currentView };
  }, [selectedRestaurant, selectedPost, showFilters, currentView]);
  
  // حفظ currentView في localStorage للاستخدام من Android
  useEffect(() => {
    localStorage.setItem('currentView', currentView);
  }, [currentView]);
  
  // حفظ حالة المطعم المفتوح
  useEffect(() => {
    localStorage.setItem('hasOpenRestaurant', selectedRestaurant ? 'true' : 'false');
  }, [selectedRestaurant]);
  
  // حفظ حالة الخبر المفتوح
  useEffect(() => {
    localStorage.setItem('hasOpenPost', selectedPost ? 'true' : 'false');
  }, [selectedPost]);
  
  // حفظ حالة الفلاتر
  useEffect(() => {
    localStorage.setItem('showFilters', showFilters ? 'true' : 'false');
  }, [showFilters]);
  
  useEffect(() => {
    let capacitorListener = null;
    
    const handleBack = () => {
      const { selectedRestaurant: sr, selectedPost: sp, showFilters: sf, currentView: cv } = stateRef.current;
      
      console.log('Back pressed - state:', { sr: !!sr, sp: !!sp, sf, cv });
      
      // أولاً: تحقق من وجود modal صور مفتوح
      const imageModal = document.querySelector('[class*="bg-black"][class*="fixed"][class*="inset-0"]');
      if (imageModal) {
        // إغلاق modal الصور
        const closeBtn = imageModal.querySelector('button');
        if (closeBtn) closeBtn.click();
        return;
      }
      
      // ثانياً: تحقق من وجود قائمة المستخدم مفتوحة
      const userMenuContainer = document.querySelector('.user-menu-container');
      if (userMenuContainer) {
        const userMenu = userMenuContainer.querySelector('[class*="z-[9999]"]');
        if (userMenu) {
          // إغلاق القائمة بالنقر على الزر
          const menuButton = userMenuContainer.querySelector('button');
          if (menuButton) menuButton.click();
          return;
        }
      }
      
      // إذا كان هناك مطعم مفتوح، نرجع للقائمة
      if (sr) {
        setSelectedRestaurant(null);
        return;
      }
      
      // إذا كان هناك خبر مفتوح، نرجع للقائمة
      if (sp) {
        setSelectedPost(null);
        return;
      }
      
      // إذا كانت الفلاتر مفتوحة، نغلقها
      if (sf) {
        setShowFilters(false);
        return;
      }
      
      // إذا لم نكن في صفحة home، نرجع لها
      if (cv !== 'home') {
        setCurrentView('home');
        return;
      }
      
      // في صفحة المطاعم الرئيسية: Android يتعامل مع الخروج
      // لا نفعل شيء هنا - MainActivity يتعامل معه
    };
    
    // معالج الحدث من Android Native (hardwareBackPress)
    const handleHardwareBackPress = () => {
      handleBack();
    };
    
    const initBackHandler = async () => {
      // إضافة مستمع للحدث الجديد من Native Android
      window.addEventListener('hardwareBackPress', handleHardwareBackPress);
      
      // Capacitor App plugin كـ backup
      if (Capacitor.isNativePlatform()) {
        try {
          capacitorListener = await CapacitorApp.addListener('backButton', handleBack);
          console.log('Back button listener ready');
        } catch (e) {
          console.error('Back button error:', e);
        }
      }
    };
    
    initBackHandler();
    
    return () => {
      window.removeEventListener('hardwareBackPress', handleHardwareBackPress);
      if (capacitorListener) capacitorListener.remove();
    };
  }, []);

  // التحديث الذكي عند فتح التطبيق
  useEffect(() => {
    const initApp = async () => {
      try {
        // تحميل البيانات المحفوظة فوراً (للسرعة القصوى)
        const cachedRestaurants = localStorage.getItem('cachedRestaurants');
        const cachedCities = localStorage.getItem('cachedCities');
        
        let hasCache = false;
        
        // عرض الـ Cache فوراً
        if (cachedRestaurants) {
          try {
            const cached = JSON.parse(cachedRestaurants);
            if (cached && cached.length > 0) {
              setRestaurants(cached);
              hasCache = true;
              setLoading(false); // إيقاف التحميل فوراً
            }
          } catch (e) {
          }
        }
        
        if (cachedCities) {
          try {
            setCities(JSON.parse(cachedCities));
          } catch (e) { /* Invalid cached cities */ }
        }
        
        // تحديث البيانات في الخلفية (فقط إذا كان هناك اتصال)
        const lastUpdate = localStorage.getItem('lastAppUpdate');
        const now = Date.now();
        const CACHE_DURATION = 30 * 1000; // 30 ثانية فقط - تحديث سريع
        
        const needsUpdate = !lastUpdate || (now - parseInt(lastUpdate)) > CACHE_DURATION;
        
        // تحقق من الاتصال بالإنترنت
        if (navigator.onLine) {
          // دائماً نحدّث البيانات من الخادم عند فتح التطبيق
          setTimeout(() => {
            fetchRestaurants(false);
            fetchCities();
            localStorage.setItem('lastAppUpdate', now.toString());
          }, hasCache ? 100 : 0);
          
          // إذا لم يكن هناك cache، نحمّل فوراً
          if (!hasCache) {
            await fetchRestaurants(false);
            await fetchCities();
          }
        } else {
          // بدون إنترنت: نستخدم الكاش فقط
          if (!hasCache) {
            setFetchError('لا يوجد اتصال بالإنترنت ولا توجد بيانات محفوظة');
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Error in initApp:', error);
        setLoading(false);
      }
    };
    
    initApp();
  }, []); // يعمل مرة واحدة فقط عند التحميل

  // طلب الموقع فقط إذا كان المستخدم قد وافق سابقاً (ليس عند أول فتح)
  useEffect(() => {
    const loadSavedLocation = () => {
      const permission = localStorage.getItem('locationPermission');
      
      // فقط إذا كان المستخدم قد وافق سابقاً
      if (permission === 'always') {
        // استخدام الموقع المحفوظ أولاً
        const savedLocation = localStorage.getItem('userLocation');
        if (savedLocation) {
          try {
            const location = JSON.parse(savedLocation);
            setUserLocation(location);
          } catch (e) {
            console.error('Error parsing saved location');
          }
        }
        
        // تحديث الموقع في الخلفية (بدون طلب إذن جديد)
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const location = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              setUserLocation(location);
              localStorage.setItem('userLocation', JSON.stringify(location));
            },
            (error) => {
              console.log('Location update failed, using cached location');
            },
            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 300000
            }
          );
        }
      }
    };
    
    // تأخير قصير للتأكد من تهيئة التطبيق
    setTimeout(loadSavedLocation, 1000);
  }, []);

  // التحديث عند استعادة التطبيق من الخلفية
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // تحديث الموقع إذا كان مسموح
        const currentPermission = localStorage.getItem('locationPermission');
        if (currentPermission === 'always' && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const location = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              setUserLocation(location);
              localStorage.setItem('userLocation', JSON.stringify(location));
            },
            () => {},
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
          );
        }
        
        // تحديث قائمة المطاعم عند العودة للتطبيق
        const lastUpdate = localStorage.getItem('lastAppUpdate');
        const now = Date.now();
        const UPDATE_THRESHOLD = 30 * 1000; // 30 ثانية - تحديث سريع
        
        if (!lastUpdate || (now - parseInt(lastUpdate)) > UPDATE_THRESHOLD) {
          await fetchRestaurants(false);
          localStorage.setItem('lastAppUpdate', now.toString());
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (currentView === 'news') {
      fetchPosts();
    }
  }, [currentView]);

  useEffect(() => {
    filterRestaurants();
  }, [restaurants, actualSearchTerm, selectedCity, selectedCuisine, minRating, hasCoupons, userLocation]);

  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
      
      // Check liked status for each post
      const liked = new Set();
      for (const post of response.data) {
        try {
          const likeResponse = await axios.get(`${API}/posts/${post.id}/liked`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          if (likeResponse.data.liked) {
            liked.add(post.id);
          }
        } catch (error) { /* Ignore like check errors */ }
      }
      setLikedPosts(liked);
    } catch (error) {
      toast.error('خطأ في تحميل الأخبار');
    } finally {
      setLoadingPosts(false);
    }
  };

  const toggleLike = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const newLiked = new Set(likedPosts);
      if (response.data.liked) {
        newLiked.add(postId);
      } else {
        newLiked.delete(postId);
      }
      setLikedPosts(newLiked);
      
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1
          };
        }
        return post;
      }));
    } catch (error) {
      toast.error('خطأ في الإعجاب');
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل التعليقات');
    }
  };

  const addComment = async (postId) => {
    if (!commentText.trim()) return;
    
    try {
      const response = await axios.post(`${API}/posts/${postId}/comments`, 
        { content: commentText },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      setComments([response.data, ...comments]);
      setCommentText('');
      
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return { ...post, comments_count: post.comments_count + 1 };
        }
        return post;
      }));
      
      toast.success('تم إضافة التعليق');
    } catch (error) {
      toast.error('خطأ في إضافة التعليق');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    
    return date.toLocaleDateString('ar');
  };

  const fetchRestaurants = async (showError = true, retries = 3) => {
    // Prevent concurrent fetches
    if (isFetching) {
      return;
    }
    
    setIsFetching(true);
    setFetchError(null);
    
    try {
      const response = await axios.get(`${API}/restaurants`, {
        timeout: 15000, // تقليل timeout لتجربة أسرع
        headers: {
          'Accept-Encoding': 'gzip, deflate',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.data && Array.isArray(response.data)) {
        setRestaurants(response.data);
        setFetchError(null);
        // حفظ في cache
        try {
          localStorage.setItem('cachedRestaurants', JSON.stringify(response.data));
          localStorage.setItem('lastSuccessfulFetch', Date.now().toString());
        } catch (storageError) {
          localStorage.removeItem('cachedRestaurants');
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      const isNetworkError = !error.response || 
        error.code === 'ECONNABORTED' || 
        error.code === 'ETIMEDOUT' ||
        error.message.includes('Network Error') ||
        error.message.includes('timeout');
      
      // Retry مرة واحدة فقط لتسريع التجربة
      if (isNetworkError && retries > 0) {
        setIsFetching(false);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchRestaurants(showError, retries - 1);
      }
      
      // استخدام Cache
      const cachedData = localStorage.getItem('cachedRestaurants');
      
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          setRestaurants(parsed);
          // إذا استخدمنا الـ cache بسبب خطأ شبكة، نحفظ حالة الخطأ
          if (isNetworkError) {
            setFetchError('network');
          }
        } catch (e) {
          console.error('Error parsing cached data', e);
          setFetchError('network');
        }
      } else {
        // لا يوجد cache ولا اتصال
        setFetchError('network');
      }
      
      console.error('❌ Error fetching restaurants:', error);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API}/cities`);
      // Filter out empty strings to prevent Select component errors
      const validCities = response.data.filter(city => city && city.trim() !== '');
      setCities(validCities);
      // حفظ في cache للتحميل السريع لاحقاً
      localStorage.setItem('cachedCities', JSON.stringify(validCities));
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const filterRestaurants = () => {
    let filtered = restaurants;

    if (actualSearchTerm) {
      const searchWords = actualSearchTerm.toLowerCase().split(' ').filter(word => word.length > 0);
      
      filtered = filtered.filter(restaurant => {
        // Search in Arabic name, English name, description, cuisine type, and menu items
        const searchFields = [
          restaurant.name_ar || '',
          restaurant.name || '',
          restaurant.description_ar || '',
          restaurant.description || '',
          restaurant.cuisine_type || '',
          restaurant.cuisine_type_ar || '',
          restaurant.address || '',
          restaurant.city || '',
          // Add menu items to search
          ...(restaurant.menu_items || [])
        ].join(' ').toLowerCase();

        // Check if any search word matches any field (similar words support)
        return searchWords.some(word => 
          searchFields.includes(word) || 
          searchFields.includes(word.substring(0, Math.max(3, word.length - 1))) // Partial match for similar words
        );
      });
    }

    if (selectedCity && selectedCity !== 'all') {
      filtered = filtered.filter(restaurant => restaurant.city === selectedCity);
    }

    if (selectedCuisine && selectedCuisine !== 'all') {
      filtered = filtered.filter(restaurant => 
        restaurant.cuisine_type.toLowerCase().includes(selectedCuisine.toLowerCase()) ||
        restaurant.cuisine_type_ar.toLowerCase().includes(selectedCuisine.toLowerCase())
      );
    }

    if (minRating && minRating !== 'all') {
      filtered = filtered.filter(restaurant => restaurant.average_rating >= parseFloat(minRating));
    }

    if (hasCoupons && hasCoupons !== 'all') {
      filtered = filtered.filter(restaurant => restaurant.has_coupon === (hasCoupons === 'true'));
    }

    // Sort by distance if user location is available, otherwise by newest first
    if (userLocation) {
      filtered = filtered.map(restaurant => {
        if (restaurant.latitude && restaurant.longitude) {
          const distance = calculateDistance(
            userLocation.lat, userLocation.lng,
            restaurant.latitude, restaurant.longitude
          );
          return { ...restaurant, distance };
        }
        return { ...restaurant, distance: 999 }; // Put restaurants without coordinates at the end
      }).sort((a, b) => a.distance - b.distance);
    } else {
      // Sort by newest first (assuming restaurants have created_at field, or use reverse order)
      // If created_at exists, use it; otherwise reverse the array to show recently added first
      if (filtered.length > 0 && filtered[0].created_at) {
        filtered = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      } else {
        // If no created_at field, reverse the array to show newest additions first
        filtered = [...filtered].reverse();
      }
    }

    setFilteredRestaurants(filtered);
  };

  const handleLocationUpdate = (location, error) => {
    if (location) {
      setUserLocation(location);
      setLocationError(null);
      localStorage.setItem('userLocation', JSON.stringify(location));
    } else {
      setLocationError(error);
      setUserLocation(null);
      localStorage.removeItem('userLocation');
    }
  };

  // Auto-request location on component mount
  useEffect(() => {
    // دالة للحصول على الموقع الحالي تلقائياً
    const autoRequestLocation = async () => {
      // استخدام navigator.geolocation مباشرة (يعمل في المتصفح والتطبيق الأصلي)
      if (!navigator.geolocation) {
        const savedLocation = localStorage.getItem('userLocation');
        if (savedLocation) {
          try {
            const location = JSON.parse(savedLocation);
            setUserLocation(location);
          } catch (error) {
            console.error('Error parsing saved location:', error);
            localStorage.removeItem('userLocation');
          }
        }
        return;
      }

      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const options = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 300000 // 5 دقائق
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          setLocationError(null);
          localStorage.setItem('userLocation', JSON.stringify(location));
          console.log('✅ تم الحصول على الموقع:', location);
        },
        (error) => {
          console.error('خطأ في الحصول على الموقع:', error);
          setLocationError('تعذر الحصول على الموقع');
          // استخدام الموقع المحفوظ كـ fallback
          const savedLocation = localStorage.getItem('userLocation');
          if (savedLocation) {
            try {
              const location = JSON.parse(savedLocation);
              setUserLocation(location);
            } catch (err) {
              console.error('Error parsing saved location:', err);
              localStorage.removeItem('userLocation');
            }
          }
        },
        options
      );
    };

    // طلب الموقع تلقائياً عند تحميل التطبيق
    autoRequestLocation();
  }, []);

  // تحديث الموقع تلقائياً عند العودة للصفحة الرئيسية
  useEffect(() => {
    if (currentView === 'home' && navigator.geolocation) {
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const options = {
        enableHighAccuracy: isMobile,
        timeout: 8000,
        maximumAge: isMobile ? 120000 : 0 // 2 دقائق للموبايل
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          
          // احفظ الموقع الحالي في متغير للمقارنة
          const currentLoc = userLocation;
          
          // تحقق من تغيير الموقع بشكل كبير (أكثر من 1 كم تقريباً)
          if (currentLoc) {
            const distance = calculateDistance(
              currentLoc.lat, currentLoc.lng,
              newLocation.lat, newLocation.lng
            );
            
            // إذا تغير الموقع بأكثر من 0.5 كم، حدّث
            if (distance > 0.5) {
              setUserLocation(newLocation);
              localStorage.setItem('userLocation', JSON.stringify(newLocation));
            }
          } else {
            // إذا لم يكن هناك موقع محفوظ، احفظ الموقع الجديد
            setUserLocation(newLocation);
            localStorage.setItem('userLocation', JSON.stringify(newLocation));
          }
        },
        (error) => {
        },
        options
      );
    }
  }, [currentView]); // يتم التنفيذ عند تغيير currentView فقط

  // تحديث التطبيق عند العودة له (فتحه بعد الإغلاق)
  useEffect(() => {
    let isAppVisible = !document.hidden;
    
    const handleVisibilityChange = () => {
      const wasHidden = !isAppVisible;
      isAppVisible = !document.hidden;
      
      // إذا كان التطبيق مخفياً وأصبح مرئياً الآن
      if (wasHidden && isAppVisible) {
        
        // 1. تحديث الموقع الجغرافي أولاً
        if (navigator.geolocation) {
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          const options = {
            enableHighAccuracy: isMobile,
            timeout: 10000,
            maximumAge: 0 // دائماً احصل على موقع جديد
          };
          
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const newLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              setUserLocation(newLocation);
              localStorage.setItem('userLocation', JSON.stringify(newLocation));
            },
            (error) => {
            },
            options
          );
        }
        
        // 2. إعادة جلب المطاعم من السيرفر (لإظهار المطاعم الجديدة)
        fetchRestaurants(false); // Silent refresh after Median update
        
        // 3. إعادة جلب المدن (في حالة إضافة مدن جديدة)
        fetchCities();
        
      }
    };
    
    // الاستماع لتغيير visibility
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // الاستماع لحدث focus (عندما يعود المستخدم للتطبيق)
    window.addEventListener('focus', () => {
      if (!document.hidden) {
        
        // تحديث الموقع
        if (navigator.geolocation) {
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const newLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              setUserLocation(newLocation);
              localStorage.setItem('userLocation', JSON.stringify(newLocation));
            },
            () => {},
            { enableHighAccuracy: isMobile, timeout: 8000, maximumAge: 0 }
          );
        }
        
        // تحديث المطاعم
        fetchRestaurants(false); // Silent refresh after location update
      }
    });
    
    // تنظيف
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // يتم التنفيذ مرة واحدة عند تحميل المكون

  // دالة للتنقل مع تصفير المطعم والخبر
  const handleNavigate = (view) => {
    setSelectedRestaurant(null);
    setSelectedPost(null);
    setShowFilters(false);
    setCurrentView(view);
  };

  if (selectedRestaurant) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
        <Header currentView={currentView} setCurrentView={setCurrentView} onClearSearch={clearSearchAndFilters} onNavigate={handleNavigate} />
        <main className="container mx-auto px-4 py-8">
          <RestaurantDetail 
            restaurant={selectedRestaurant} 
            onBack={() => {
              setSelectedRestaurant(null);
              // استعادة موضع التمرير بعد العودة
              setTimeout(() => window.scrollTo(0, scrollPositionRef.current), 50);
            }}
            onEdit={(restaurant) => {
              setEditingRestaurant(restaurant);
              setSelectedRestaurant(null);
              setCurrentView('edit-restaurant');
            }}
            onDelete={(restaurantId) => {
              // Remove from restaurants list
              setRestaurants(restaurants.filter(r => r.id !== restaurantId));
            }}
          />
        </main>
      </div>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'favorites':
        return user ? <FavoritesPage /> : (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لعرض المفضلة</p>
          </div>
        );
      case 'want-to-visit':
        return user ? <WantToVisitPage /> : (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لعرض قائمة أريد زيارتها</p>
          </div>
        );
      case 'add-restaurant':
        return user ? (
          user.role === 'admin' ? <AddRestaurantForm /> : (
            <div className="text-center py-12" style={{ direction: 'rtl' }}>
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg text-gray-600">هذه الصفحة متاحة للمدراء فقط</p>
              <p className="text-sm text-gray-500 mt-2">يمكن للمدراء إضافة مطاعم جديدة للدليل</p>
            </div>
          )
        ) : (
          <div className="text-center py-12" style={{ direction: 'rtl' }}>
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لإضافة مطعم</p>
          </div>
        );
      
      case 'edit-restaurant':
        return user && (user.role === 'admin' || user.is_admin) && editingRestaurant ? (
          <EditRestaurantForm 
            restaurant={editingRestaurant}
            onSuccess={() => {
              setCurrentView('home');
              setEditingRestaurant(null);
              fetchRestaurants(false); // Silent refresh after edit
              toast.success('تم تحديث المطعم بنجاح');
            }}
            onCancel={() => {
              setCurrentView('home');
              setEditingRestaurant(null);
            }}
          />
        ) : (
          <div className="text-center py-12" style={{ direction: 'rtl' }}>
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">هذه الصفحة متاحة للمدراء فقط</p>
          </div>
        );
      
      case 'add-post':
        return user ? (
          user.role === 'admin' || user.is_admin ? <AddPostForm /> : (
            <div className="text-center py-12" style={{ direction: 'rtl' }}>
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg text-gray-600">هذه الصفحة متاحة للمدراء فقط</p>
              <p className="text-sm text-gray-500 mt-2">يمكن للمدراء نشر الأخبار والعروض</p>
            </div>
          )
        ) : (
          <div className="text-center py-12" style={{ direction: 'rtl' }}>
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لنشر الأخبار</p>
          </div>
        );
      
      case 'my-ratings':
        return user ? <MyRatingsPage /> : (
          <div className="text-center py-12" style={{ direction: 'rtl' }}>
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لعرض تقييماتك</p>
          </div>
        );
      
      case 'suggest-restaurant':
        return user ? <RestaurantSuggestionForm /> : (
          <div className="text-center py-12" style={{ direction: 'rtl' }}>
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لاقتراح مطعم</p>
          </div>
        );
      
      case 'contact-us':
        return user ? <ContactUs /> : (
          <div className="text-center py-12" style={{ direction: 'rtl' }}>
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول للاتصال بنا</p>
          </div>
        );
      
      case 'admin':
        return user && user.is_admin ? <AdminDashboard /> : (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يتطلب صلاحيات المدير</p>
          </div>
        );
      
      case 'profile':
        return user ? <ProfileEdit /> : (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لتعديل البيانات</p>
          </div>
        );
      
      case 'change-password':
        return user ? <ChangePassword /> : (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لتغيير كلمة المرور</p>
          </div>
        );
      
      case 'contact':
        return <ContactUs />;
      
      case 'about':
        return <AboutApp />;
      
      case 'delete-account':
        return user ? <DeleteAccount /> : (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg text-gray-600">يجب تسجيل الدخول لحذف الحساب</p>
          </div>
        );
      
      case 'location-settings':
        return <LocationSettings />;
      
      case 'news':
        return <NewsPage />;
      
      case 'top-rated':
        return <TopRatedRestaurants restaurants={restaurants} onRestaurantClick={setSelectedRestaurant} />;
      
      case 'privacy-policy':
        return <PrivacyPolicy />;
      
      default:
        return (
          <>
            {/* Search Section */}
            <section className="py-6 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
              <div className="container mx-auto px-4" style={{ direction: 'rtl' }}>
                {/* Search Bar with Search and Filter Buttons */}
                <div className="max-w-3xl mx-auto px-2">
                  <div className="flex gap-2 sm:gap-3" style={{ direction: 'rtl' }}>
                    <div className="relative flex-1">
                      <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        placeholder="ابحث عن مطعم أو طعام أو مدينة..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        className="pl-4 pr-12 py-3 text-sm sm:text-lg search-input"
                        style={{ direction: 'rtl' }}
                      />
                    </div>
                    <Button
                      onClick={handleSearch}
                      className="px-3 py-3 sm:px-4 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      <Search className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowFilters(!showFilters)}
                      className={`px-3 py-3 sm:px-4 ${showFilters ? 'border-2 border-white bg-white/20' : 'border-transparent bg-transparent'} hover:bg-white/30 text-white`}
                    >
                      <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            {/* Initial Location Permission Dialog */}
            <InitialLocationPermission />
            
            <main className="container mx-auto px-4 py-8">
              {/* Collapsible Filters */}
              {showFilters && (
                <Card className="mb-8">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-800" style={{ direction: 'rtl' }}>خيارات التصفية</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowFilters(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" style={{ direction: 'rtl' }}>
                      <Select value={selectedCity} onValueChange={setSelectedCity}>
                        <SelectTrigger>
                          <SelectValue placeholder="اختر المدينة" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">جميع المدن</SelectItem>
                          {cities.map(city => (
                            <SelectItem key={city} value={city}>{city}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <Select value={selectedCuisine} onValueChange={setSelectedCuisine}>
                        <SelectTrigger>
                          <SelectValue placeholder="نوع المطبخ" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">جميع الأنواع</SelectItem>
                          <SelectItem value="syrian">شامي</SelectItem>
                          <SelectItem value="lebanese">لبناني</SelectItem>
                          <SelectItem value="iraqi">عراقي</SelectItem>
                          <SelectItem value="egyptian">مصري</SelectItem>
                          <SelectItem value="moroccan">مغربي</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Select value={minRating} onValueChange={setMinRating}>
                        <SelectTrigger>
                          <SelectValue placeholder="التقييم الأدنى" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">جميع التقييمات</SelectItem>
                          <SelectItem value="4">4+ نجوم</SelectItem>
                          <SelectItem value="3">3+ نجوم</SelectItem>
                          <SelectItem value="2">2+ نجوم</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Select value={hasCoupons} onValueChange={setHasCoupons}>
                        <SelectTrigger>
                          <SelectValue placeholder="كوبونات الخصم" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">جميع المطاعم</SelectItem>
                          <SelectItem value="true">
                            <div className="flex items-center gap-2">
                              <Ticket className="h-4 w-4 text-orange-500" />
                              مع كوبونات خصم
                            </div>
                          </SelectItem>
                          <SelectItem value="false">بدون كوبونات</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedCity('all');
                          setSelectedCuisine('all');
                          setMinRating('all');
                        }}
                      >
                        <Filter className="h-4 w-4 mr-2" />
                        إعادة تعيين
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Results */}
              {loading ? (
                // عرض Skeleton Cards أثناء التحميل
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" dir="rtl">
                  {[...Array(6)].map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : restaurants.length === 0 && fetchError === 'network' ? (
                // خطأ في الاتصال - عرض skeleton
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" dir="rtl">
                  {[...Array(6)].map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : filteredRestaurants.length === 0 && restaurants.length > 0 ? (
                // توجد مطاعم لكن لا تطابق البحث
                <div className="text-center py-12" dir="rtl">
                  <Utensils className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg text-gray-600 mb-2">لم نجد مطاعم تطابق بحثك</p>
                  <p className="text-sm text-gray-400">جرّب تغيير معايير البحث أو الفلاتر</p>
                  <button
                    onClick={clearSearchAndFilters}
                    className="mt-4 text-amber-600 hover:text-amber-700 font-medium"
                  >
                    مسح جميع الفلاتر
                  </button>
                </div>
              ) : filteredRestaurants.length === 0 ? (
                // لا توجد مطاعم - عرض skeleton
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" dir="rtl">
                  {[...Array(6)].map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : (
                <>
                  {/* Show count only when search or filter is active */}
                  {(actualSearchTerm || selectedCity !== 'all' || selectedCuisine !== 'all' || minRating !== 'all' || hasCoupons !== 'all') && (
                    <div className="mb-6" style={{ direction: 'rtl' }}>
                      <p className="text-lg text-gray-600">
                        تم العثور على {filteredRestaurants.length} مطعم
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" dir="rtl">
                    {/* عرض جميع المطاعم */}
                    {filteredRestaurants.map((restaurant, index) => (
                      <RestaurantCard
                        key={restaurant.id}
                        restaurant={restaurant}
                        onClick={() => {
                          scrollPositionRef.current = window.scrollY;
                          setSelectedRestaurant(restaurant);
                        }}
                        priority={index < 6}
                      />
                    ))}
                  </div>
                </>
              )}
            </main>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header currentView={currentView} setCurrentView={setCurrentView} onClearSearch={clearSearchAndFilters} onNavigate={handleNavigate} />
      {renderCurrentView()}
    </div>
  );
};

// Main App Component with Authentication-First Architecture
function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-center" />
        <CookieConsent />
        <OfflineAlert />
        <AppContent />
      </div>
    </AuthProvider>
  );
}

// App Content with Authentication Guard
const AppContent = () => {
  const { isAuthenticated, loading, isFirstTime, markAsSeenApp } = useAuth();
  
  // التحقق من وجود cache و hasSeenApp للتحميل السريع
  const hasCache = !!localStorage.getItem('cachedRestaurants');
  const hasSeenApp = !!localStorage.getItem('hasSeenApp');
  const hasToken = !!localStorage.getItem('token');
  
  // السبلاش يظهر فقط عند أول فتح للتطبيق (Cold Start)
  // لا يظهر عند العودة من الخلفية
  const [showSplash, setShowSplash] = useState(() => {
    // تحقق إذا كان هذا فتح جديد للتطبيق أم عودة من الخلفية
    const sessionActive = sessionStorage.getItem('appSessionActive');
    if (sessionActive) {
      return false; // لا تظهر السبلاش إذا كانت الجلسة نشطة
    }
    sessionStorage.setItem('appSessionActive', 'true');
    return true; // أظهر السبلاش فقط في أول فتح
  });

  const handleSplashComplete = () => {
    setShowSplash(false);
    markAsSeenApp();
  };

  // إظهار شاشة تحميل صغيرة فقط إذا لم نتخطى السبلاش
  if (loading && showSplash) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-400 to-amber-500">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-opacity-30 border-t-white mx-auto mb-4"></div>
          <p className="text-lg">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (showSplash) {
    return <NewSplashScreen onFinish={handleSplashComplete} hasCache={hasCache} />;
  }

  if (!isAuthenticated && !loading) {
    return <AuthScreen />;
  }

  // إذا كان لا يزال يحمّل لكن لدينا cache، نعرض الصفحة الرئيسية
  if (loading && !showSplash) {
    return <Home />;
  }

  return <Home />;
};

export default App;